import React, { useState, useEffect, useRef, createRef, memo, useMemo, useCallback, useContext, startTransition } from "react";
import { io } from "socket.io-client";
import {
  Constants,
  useMeeting,
  useParticipant,
  usePubSub,
  useTranscription,
  useTranslation,
  useMediaDevice,
  createCameraVideoTrack,
  useWhiteboard,
} from "../config/sdkConfig";
import { vbExtension } from "../context/AgoraReactContext";
import { ChromaKeyProcessor } from "../utils/ChromaKeyProcessor";
// BrandingOverlayProcessor removed — logo is now rendered as HTML overlay in ParticipantView
import { SidebarConatiner } from "../components/sidebar/SidebarContainer";
import MemorizedParticipantView from "./components/ParticipantView";
import MemorizedWebinarParticipantView from "./components/WebinarParticipantView";
import FocusParticipantView from "./components/FocusParticipantView";
import { PresenterView, PresenterCameraPiP } from "../components/PresenterView";
import WaitingToJoinScreenNew from "../components/screens/WaitingToJoinScreenNew";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import useWindowSize from "../hooks/useWindowSize";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import TopBar from "./components/TopBar";
import CrossRoomWaitingBanner from "./components/CrossRoomWaitingBanner";
import { sideBarModes } from "../utils/common";
import { useSiteContext } from "../context/SiteContext";
import WaitingParticipantsPanel from "../components/waitingRoom/WaitingParticipantsPanel";
import ElementSection from "../components/meeting/ElementSection";
import CtaSection from "../components/meeting/CtaSection";
import TalkingPointSection from "../components/meeting/TalkingPointSection";
import DocumentViewer from "../components/meeting/DocumentViewer";
import PrompterSection from "../components/meeting/PrompterSection";
import PrompterViewer from "../components/studio/prompter/PrompterViewer";
import { getFolderElements } from "../services/elementServices";
import VideoSection from "../components/meeting/VideoSection";
import IOSMediaConsent from "../components/meeting/IOSMediaConsent";
import WebsiteSection from "../components/meeting/WebsiteSection";
import SpeakerParticipantView from "./components/SpeakerParticipantView";
import SpeakerSideParticipantView from "./components/SpeakerSideParticipantView";
import PollVotingModal from "../components/meeting/PollVotingModal";
import LeadCaptureViewer from "../components/meeting/LeadCaptureViewer";
import PollResults from "./components/PollResults";
import TimerStageRenderer from "../components/studio/timer/TimerStageRenderer";
import PresentationStageRenderer from "../components/studio/presentation/PresentationStageRenderer";
import { AudioPlayerBar } from "../components/studio/audio";
import { ContractRenderer } from "../components/studio/contract";
import { ParticipantPermissionModal } from "../components/modals/PermissionRequestModal";
import PrivacyNoticeModal from "./components/PrivacyNoticeModal";
import WebViewer from "../components/meeting/WebViewer";
import { StudioBottomControls, StudioLeftSidebar, StudioRightSidebar, ReactionsOverlay, RaisedHandsQueue } from "../components/studio";
import CaptionsBar from "../components/studio/translation/CaptionsBar";
import LiveCaptions from "../components/studio/translation/LiveCaptions";
import { createCaptionSession } from "../components/studio/translation/speechService";
import { createAICaptionSession } from "../components/studio/translation/aiCaptionsService";
import { startStt, stopStt, parseSttMessage, toAgoraSttLanguage, isTranslationSupported, buildAllTargetsTranslateConfig } from "../services/agoraSttService";
import { translateTextLocally, isAgoraSttTranscribable, getLanguageLabel } from "../components/studio/translation/languageConfig";
import requests from "../services/httpServices";
import axios from "axios";
import { getVirtualBackgroundPreference, saveVirtualBackgroundPreference } from "../services/virtualBackgroundServices";
import RealTimeTranslatorModal from "../components/studio/translation/RealTimeTranslatorModal";
import pollServices from "../services/pollServices";
import folderService from "../services/folderServices";
import { initializePubSubRTM, cleanupPubSubRTM } from "../hooks/useAgoraPubSub";
import AgoraContext, { stringToNumericUid } from "../context/AgoraContext";
import { useAgoraRTM } from "../context/AgoraRTMContext";
import { useMeetingStateSync } from "../hooks/useMeetingStateSync";
import { useMeetingState } from "../context/MeetingStateContext";
import { useBrandKit } from "../context/BrandKitContext";
import { BreakoutRoomProvider } from "../context/BreakoutRoomContext";
import * as meetingServices from "../services/meetingServices";
import { startWebinar, endWebinar } from "../services/roomServices";
import rewardService from "../services/rewardService";
import RewardCelebration from "../components/RewardCelebration";
// WhiteboardContainer - now uses CDN-loaded white-web-sdk instead of bundled fastboard
import WhiteboardContainer from "../components/studio/whiteboard/WhiteboardContainer";

// ────────────────────────────────────────────────────────────────────────
// Grid-recorder: module-level state for per-participant audio capture.
// Lives outside any React component lifecycle so the flush/pause/resume
// window fns AND the collected MediaRecorder blobs survive if the gallery
// MeetingContainerNew unmounts — which happens when the host clicks
// "End meeting for all" (Conference flips isMeetingLeft, MeetingProvider
// tree unwraps, all 4 cells unmount). The worker's stop signal comes
// milliseconds later; previously the cleanup had already deleted the
// window fn, so the worker got {error:'flush function not installed'}
// and per-participant audio was lost.
//
// Now: the gallery cell's useEffect only manages the polling loop + the
// tryStartRecorder closure that adds to this shared Map. The window fns
// and the Map itself are installed once at module-init time (below) and
// persist for the life of the Chrome process.
const __rlinkRecorderRecorders = (typeof window !== 'undefined')
  ? (window.__rlinkRecorderRecorders = window.__rlinkRecorderRecorders || new Map())
  : new Map();

// Per-participant VIDEO MediaRecorders (mirror of audio above).
// Captures each remote participant's video MediaStreamTrack DIRECTLY from
// Agora — bypasses Chrome's Xvfb compositor entirely. Each chunk is
// streamed live to the worker over HTTP (5s segments) so memory stays
// bounded regardless of meeting length.
//
// Gated on URL param `?perTrack=1` so it ONLY runs when the worker
// passes the flag (i.e. recorderConfig.perTrackCapture is true on the
// backend). For all other recordings this code is a no-op.
//
// Phase 1: just capture and store. Phase 2 (separate work) will compose
// the captured WebMs into the final layout MP4s via FFmpeg post-stop.
// Until Phase 2 ships, the existing Xvfb pipeline produces the actual
// recording output exactly as today — these per-track WebMs are
// additive data with zero impact on what users see.
const __rlinkRecorderVideoRecorders = (typeof window !== 'undefined')
  ? (window.__rlinkRecorderVideoRecorders = window.__rlinkRecorderVideoRecorders || new Map())
  : new Map();

// Per-participant segment counter for the per-track video pipeline.
// When Agora replaces a participant's MediaStreamTrack mid-recording (e.g.
// because we called setRemoteVideoStreamType to switch to HIGH, or the
// participant turned their camera off/on), the old MediaRecorder loses
// its source. We listen for track.ended, drop that recorder, and the
// polling loop spawns a NEW MediaRecorder for the new track. Each
// MediaRecorder writes to its own segment file (segmentIndex 0, 1, 2…)
// so the worker can concat them in order at compose time. Lives at
// module scope alongside the other per-recorder state — survives the
// gallery cell unmount on "End for all".
const __rlinkRecorderVideoSegments = (typeof window !== 'undefined')
  ? (window.__rlinkRecorderVideoSegments = window.__rlinkRecorderVideoSegments || new Map())
  : new Map();

if (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('recorder') === 'true' &&
    typeof window.__rlinkFlushParticipantAudio !== 'function') {
  window.__rlinkPauseParticipantAudio = () => {
    for (const [, rec] of __rlinkRecorderRecorders) {
      try { if (rec.recorder.state === 'recording') rec.recorder.pause(); } catch (_) {}
    }
    console.log('[Recorder] Paused all participant audio recorders (module-level)');
  };
  window.__rlinkResumeParticipantAudio = () => {
    for (const [, rec] of __rlinkRecorderRecorders) {
      try { if (rec.recorder.state === 'paused') rec.recorder.resume(); } catch (_) {}
    }
    console.log('[Recorder] Resumed all participant audio recorders (module-level)');
  };
  window.__rlinkFlushParticipantAudio = async (recordingId, workerBase) => {
    console.log('[Recorder] FLUSH called with recordingId=' + recordingId +
      ', workerBase=' + workerBase + ', recorders=' + __rlinkRecorderRecorders.size);
    const results = [];
    for (const [pid, rec] of __rlinkRecorderRecorders) {
      try {
        if (rec.recorder.state === 'recording') {
          rec.recorder.requestData();
          await new Promise(r => setTimeout(r, 300));
          rec.recorder.stop();
          await new Promise(r => setTimeout(r, 300));
        }
        if (rec.chunks.length === 0) {
          console.warn('[Recorder] ' + rec.name + ' has no audio chunks');
          results.push({ id: pid, name: rec.name, ok: false, reason: 'no-chunks' });
          continue;
        }
        const blob = new Blob(rec.chunks, { type: 'audio/webm' });
        const form = new FormData();
        form.append('audio', blob, rec.name + '.webm');
        form.append('participantId', pid);
        form.append('participantName', rec.name);
        const url = workerBase + '/participant-audio-upload/' + recordingId;
        console.log('[Recorder] POST ' + url + ' size=' + blob.size);
        const resp = await fetch(url, { method: 'POST', body: form });
        const txt = await resp.text().catch(() => '');
        console.log('[Recorder] POST result: ' + resp.status + ' ' + txt.substring(0, 100));
        results.push({ id: pid, name: rec.name, ok: resp.ok, status: resp.status, size: blob.size });
      } catch (e) {
        console.error('[Recorder] upload failed for ' + rec.name + ':', e.message);
        results.push({ id: pid, name: rec.name, ok: false, error: e.message });
      }
    }
    __rlinkRecorderRecorders.clear();
    return results;
  };
  console.log('[Recorder] module-level flush/pause/resume fns installed (survives cell unmount)');

  // Auto-flush on page teardown — required for Egress recorder (which
  // runs Chrome inside a Docker container we have no DevTools handle
  // on, so the worker can't call window.__rlinkFlushParticipantAudio
  // via puppeteer.evaluate the way the in-house pool browser does).
  // The page reads recordingId + workerBase from its own URL params
  // (the worker passes them in for egress mode) and flushes itself
  // before Chrome closes. This is also a defensive backstop for the
  // in-house pipeline: if the browser crashes mid-stop or puppeteer's
  // evaluate fails, we no longer silently drop participant_audio.
  //
  // Fetch uses keepalive:true so Chrome holds the request open for up
  // to a few seconds after pagehide while the body uploads. Modern
  // Chrome supports up to several MB on keepalive — sufficient for
  // typical few-minute recordings (~200 KB - 2 MB per participant).
  // Bigger blobs may not finish; if that becomes a problem we move
  // to streaming chunk uploads.
  const __rlinkAutoFlushOnHide = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const recordingId = params.get('recordingId');
      const workerBase = params.get('workerBase');
      if (!recordingId || !workerBase) {
        console.log('[Recorder] pagehide fired but no recordingId/workerBase in URL — skipping auto-flush (in-house pipeline path expected)');
        return;
      }
      if (__rlinkRecorderRecorders.size === 0) {
        console.log('[Recorder] pagehide: no participant recorders to flush');
        return;
      }
      console.log('[Recorder] pagehide → auto-flush ' + __rlinkRecorderRecorders.size + ' recorders → ' + workerBase);
      // Inline a copy of the flush logic so we control the fetch options.
      // Can't reuse __rlinkFlushParticipantAudio above because it does NOT
      // pass keepalive:true, which is required for the upload to survive
      // the page teardown that pagehide signals.
      for (const [pid, rec] of __rlinkRecorderRecorders) {
        try {
          if (rec.recorder.state === 'recording') {
            rec.recorder.requestData();
            // brief synchronous-ish pause so requestData's chunk lands
            await new Promise(r => setTimeout(r, 200));
            rec.recorder.stop();
            await new Promise(r => setTimeout(r, 100));
          }
          if (rec.chunks.length === 0) continue;
          const blob = new Blob(rec.chunks, { type: 'audio/webm' });
          const form = new FormData();
          form.append('audio', blob, rec.name + '.webm');
          form.append('participantId', pid);
          form.append('participantName', rec.name);
          await fetch(workerBase + '/participant-audio-upload/' + recordingId, {
            method: 'POST',
            body: form,
            keepalive: true,
          });
        } catch (e) {
          // pagehide handlers can't surface errors to the user — best effort.
          console.warn('[Recorder] pagehide flush failed for ' + rec.name + ': ' + (e?.message || e));
        }
      }
      __rlinkRecorderRecorders.clear();
    } catch (outerErr) {
      console.warn('[Recorder] pagehide handler error: ' + (outerErr?.message || outerErr));
    }
  };
  window.addEventListener('pagehide', __rlinkAutoFlushOnHide);
  // Some Chromium builds fire visibilitychange→hidden but skip pagehide
  // when the renderer is killed abruptly. Listen on both for resilience.
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') __rlinkAutoFlushOnHide();
  });

  // Per-participant VIDEO finalize. Chunks are streamed live during the
  // recording (see useEffect below). At stop time the worker invokes this
  // to flush any pending data and signal end-of-stream.
  window.__rlinkFlushParticipantVideo = async (recordingId, workerBase) => {
    console.log('[Recorder] VIDEO FLUSH called recorders=' + __rlinkRecorderVideoRecorders.size);
    const results = [];
    for (const [pid, rec] of __rlinkRecorderVideoRecorders) {
      try {
        if (rec.recorder.state === 'recording') {
          rec.recorder.requestData();
          await new Promise(r => setTimeout(r, 300));
          rec.recorder.stop();
          await new Promise(r => setTimeout(r, 300));
        }
        // POST a final marker so the worker can mark the stream complete.
        const form = new FormData();
        form.append('participantId', pid);
        form.append('participantName', rec.name || ('p_' + pid));
        form.append('isFinal', 'true');
        const url = workerBase + '/participant-video-finalize/' + recordingId;
        const resp = await fetch(url, { method: 'POST', body: form });
        results.push({ id: pid, name: rec.name, ok: resp.ok, chunks: rec.chunkCount });
      } catch (e) {
        console.error('[Recorder] video finalize failed for ' + (rec.name || pid) + ':', e.message);
        results.push({ id: pid, name: rec.name, ok: false, error: e.message });
      }
    }
    __rlinkRecorderVideoRecorders.clear();
    // Reset segment counters so the next recording starts fresh at
    // segment 0 for each participant.
    __rlinkRecorderVideoSegments.clear();
    return results;
  };
  console.log('[Recorder] module-level video flush fn installed');
}

export function MeetingContainerNew({
  onMeetingLeave,
  setIsMeetingLeft,
  setCustomVideoStream,
  micOn,
  setMicOn,
  webcamOn,
  setWebcamOn,
  customAudioStream,
  setCustomAudioStream,
  customVideoStream,
  // Grid recorder: when multiple sibling MeetingContainerNew instances render
  // inside ONE shared MeetingProvider (single Agora connection, 1x decode
  // instead of 4x), each instance needs its own layout. This prop wins over
  // the URL `?layout=` param and short-circuits setSelectedLayout writes so
  // siblings don't thrash the shared MeetingAppContext.selectedLayout value.
  recorderLayoutOverride,
}) {
  // NOTE: mic/cam localStorage persistence for breakout redirects is done
  // after useMeeting() hook, using actual Agora state (mMeeting.localMicOn/localWebcamOn)
  // instead of the micOn/webcamOn props which only reflect the initial state.

  const [showJoinScreen, setShowJoinScreen] = useState(() => {
    // Recorder bot: skip join screen entirely from the start
    if (new URLSearchParams(window.location.search).get('recorder') === 'true') return false;
    return null;
  });
  const [participantsData, setParticipantsData] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [isPollLauncher, setIsPollLauncher] = useState(false); // Track if current user launched the poll
  const [showPollModal, setShowPollModal] = useState(false);
  const [activeLeadCapture, setActiveLeadCapture] = useState(null);
  const [isLeadCaptureLauncher, setIsLeadCaptureLauncher] = useState(false); // Track if current user launched the lead capture
  const [showLeadCaptureModal, setShowLeadCaptureModal] = useState(false);
  const [activeTimer, setActiveTimer] = useState(null);
  const [isTimerLauncher, setIsTimerLauncher] = useState(false); // Track if current user launched the timer
  const [timerState, setTimerState] = useState({ status: 'idle' });
  const [activePresentation, setActivePresentation] = useState(null);
  const [isPresentationLauncher, setIsPresentationLauncher] = useState(false); // Track if current user launched the presentation
  const [presentationState, setPresentationState] = useState({ currentSlide: 1, status: 'idle' });

  // Permission modal states
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [showVideoPermissionModal, setShowVideoPermissionModal] =
    useState(false);

  // Studio layout states
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(true);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(true);
  const [activeContractView, setActiveContractView] = useState(null);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [isBRB, setIsBRB] = useState(false);
  const [activeReaction, setActiveReaction] = useState(null);
  const [mutedByHost, setMutedByHost] = useState(false); // Track if user was muted by host
  const [cameraOffByHost, setCameraOffByHost] = useState(false); // Track if user's camera was disabled by host
  const [meetingStartTime, setMeetingStartTime] = useState(null); // Track when meeting was joined for call duration timer

  // Translation & Captions states
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionDisplayMode, setCaptionDisplayMode] = useState('floating'); // 'bar' or 'floating'
  const [spokenLanguage, setSpokenLanguage] = useState('en');
  const [captionLanguage, setCaptionLanguage] = useState('en');
  const [liveCaptions, setLiveCaptions] = useState([]);
  const [captionSession, setCaptionSession] = useState(null);
  const [showTranslatorModal, setShowTranslatorModal] = useState(false);
  // Refs to track latest caption-enabled state for the stream-message handler
  // (avoids stale closure issues when the effect re-runs)
  const captionsEnabledRef = useRef(false);
  captionsEnabledRef.current = captionsEnabled;

  // Rewards Panel state
  const [showRewardsPanel, setShowRewardsPanel] = useState(false);

  // Waiting Room states (for hosts)
  const [waitingParticipants, setWaitingParticipants] = useState([]);
  const [showWaitingRoomPanel, setShowWaitingRoomPanel] = useState(false);
  const [waitingRoomChatMessages, setWaitingRoomChatMessages] = useState({}); // { [odId]: [messages] }
  // Cross-room waiting banners — one entry per "other room of mine that has
  // queued guests". Backend emits 'cross-room-waiting' on join-waiting-room
  // when the room owner is a connected host in a different meeting.
  const [crossRoomQueues, setCrossRoomQueues] = useState([]);
  const waitingRoomSocketRef = useRef(null);
  const meetingSettingsRef = useRef(null);
  const waitingChimeLastRef = useRef(0);
  const waitingJoinTimesRef = useRef({});
  const waitingParticipantsRef = useRef([]);
  useEffect(() => { waitingParticipantsRef.current = waitingParticipants; }, [waitingParticipants]);

  // AI Captions states (Agora STT powered)
  const [aiCaptionsEnabled, setAICaptionsEnabled] = useState(false);
  const aiCaptionsEnabledRef = useRef(false);
  aiCaptionsEnabledRef.current = aiCaptionsEnabled;
  const [aiCaptionSession, setAICaptionSession] = useState(null);
  const [aiCaptions, setAICaptions] = useState([]);
  const [sttPubBotUid, setSttPubBotUid] = useState(null); // UID of the STT bot sending captions
  const [sttAgentId, setSttAgentId] = useState(null);
  // ─── Translation provider preference ────────────────────────────────────
  // 2026-04-07: Google Translate is now the PRIMARY translator (was Agora).
  // The flow is:
  //   1. STT is still started WITH translateConfig so Agora translation
  //      messages are available as a fallback.
  //   2. For each transcribe-final message that needs translation, we call
  //      Google Translate immediately. If it succeeds, that translation is
  //      shown to the user.
  //   3. If Google Translate errors out (network error / blocked region /
  //      bad payload), we flip `useAgoraTranslateFallback` to true. From
  //      that point on the stream-message handler accepts Agora 'translate'
  //      messages instead of using Google.
  //
  // To revert to Agora-first / Google-fallback behavior, restore the old
  // code blocks marked `OLD AGORA-PRIMARY BEHAVIOR — KEPT FOR ROLLBACK`
  // further down in this file.
  // ────────────────────────────────────────────────────────────────────────

  // [LEGACY — kept so we can flip back to Agora-primary if needed]
  // Set to true only when Agora STT is unavailable — then translate captions via Google Translate
  const [useGoogleTranslateFallback, setUseGoogleTranslateFallback] = useState(false);
  const useGoogleTranslateFallbackRef = useRef(false);

  // [NEW] When true, Google Translate has failed and we fall back to Agora's
  // native 'translate' stream messages. Defaults to false (Google primary).
  const [useAgoraTranslateFallback, setUseAgoraTranslateFallback] = useState(false);
  const useAgoraTranslateFallbackRef = useRef(false);

  // [NEW] When true, the user picked a spoken language that Agora STT cannot
  // transcribe, so we auto-reverted to English and want the spoken-language
  // picker to restrict its options to the Agora-transcribable subset only.
  // (Caption language is unaffected — Google can still translate to anything.)
  const [restrictSpokenToAgora, setRestrictSpokenToAgora] = useState(false);
  const sttPubBotUidRef = useRef(null);
  // Flag to suppress auto-restart logic when we intentionally stop the bot (e.g. language change forceRestart)
  const sttIntentionalRestartRef = useRef(false);
  // Track which target language codes the current STT bot was started with (null = no translate)
  // Used to detect when a caption language change requires an STT restart
  const sttHasTranslateRef = useRef(null); // null or Set of BCP-47 codes e.g. Set(['es-ES','fr-FR'])
  // STT Bot active state (independent of AI Captions - used for notetaker)
  const [sttBotActive, setSttBotActive] = useState(false);
  // Notetaker settings loaded from user preferences
  const [notetakerSettings, setNotetakerSettings] = useState(null);
  const notetakerSettingsLoadedRef = useRef(false);
  const spokenLanguageRef = useRef(spokenLanguage);
  const captionLanguageRef = useRef(captionLanguage);

  // Keep language refs in sync
  useEffect(() => { spokenLanguageRef.current = spokenLanguage; }, [spokenLanguage]);
  useEffect(() => { captionLanguageRef.current = captionLanguage; }, [captionLanguage]);

  // Device selection states
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);

  // Meeting statistics for settings panel (use ref to avoid re-rendering the entire tree every 2s)
  const localParticipantStatsRef = useRef(null);

  // Remembers the layout the user was on before the FIRST spotlight in a series,
  // so we can revert when the last spotlight is removed (see onSpotlightChanged).
  const preSpotlightLayoutRef = useRef(null);

  // Camera resolution state - load from localStorage
  // Default is 480p; users can opt into higher resolutions via the settings modal.
  const [cameraResolution, setCameraResolution] = useState(() => {
    try {
      const saved = localStorage.getItem('rlink_settings');
      if (saved) {
        const settings = JSON.parse(saved);
        return settings.cameraResolution || '480p';
      }
    } catch { }
    return '480p';
  });

  // Load saved device preferences from localStorage
  const [selectedAudioInput, setSelectedAudioInput] = useState(() => {
    try {
      return localStorage.getItem('rlink_selectedMic') || null;
    } catch { return null; }
  });
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(() => {
    try {
      return localStorage.getItem('rlink_selectedSpeaker') || null;
    } catch { return null; }
  });
  const [selectedVideoInput, setSelectedVideoInput] = useState(() => {
    try {
      return localStorage.getItem('rlink_selectedCamera') || null;
    } catch { return null; }
  });

  // Branding is now managed via MeetingAppContext - get it from useMeetingAppContext below
  const {
    meetingSettings,
    showElementSection,
    setFolderElementList,
    setShowElementSection,
    activeElement,
    setActiveElement,
    meetingRole,
    getMeetingSession,
    meetingSession,
    selectedLayout: _ctxSelectedLayout,
    setSelectedLayout: _ctxSetSelectedLayout,
    translationStatus,
    setTranslationStatus,
    setIsMeetingReconnecting,
    isMeetinngReconnecting,
    showPollResults,
    setShowPollResults,
    setMeetingSettings,
    prompterElement,
    setPrompterElement,
    pollResults,
    setPollResults,
    // Co-host management
    coHosts,
    addCoHost,
    removeCoHost,
    isCoHost,
    setMeetingRole,
    user,
    setHideSelfView,
    customMeetingName,
  } = useSiteContext();

  // Grid-recorder shadowing: when `recorderLayoutOverride` is set, this
  // instance uses its own layout (read path) and ignores setSelectedLayout
  // calls (write path) so 4 sibling instances don't fight over the shared
  // MeetingAppContext.selectedLayout value. Normal (non-recorder) renders
  // are unaffected: override is undefined → shadowed values fall through
  // to the real context values unchanged.
  const selectedLayout = recorderLayoutOverride || _ctxSelectedLayout;
  const setSelectedLayout = recorderLayoutOverride
    ? (() => { /* no-op in grid recorder secondary cells */ })
    : _ctxSetSelectedLayout;

  const {
    startWhiteboard: startWhiteboardBase,
    stopWhiteboard: stopWhiteboardBase,
    fastboard,
    isLoading: isWhiteboardLoading,
    error: whiteboardError,
    isWhiteboardOpen,
    roomInfo: whiteboardRoomInfo,
    joinWhiteboard,
  } = useWhiteboard();
  const [isElementRefreshing, setIsElementRefreshing] = useState(false);
  const [guestHiddenWhiteboard, setGuestHiddenWhiteboard] = useState(false);

  // Keep meetingSettingsRef in sync (for use in socket callbacks to avoid stale closures)
  useEffect(() => { meetingSettingsRef.current = meetingSettings; }, [meetingSettings]);

  // Media device hooks for audio/video device selection
  const { getMicrophones, getCameras, getPlaybackDevices } = useMediaDevice({
    onDeviceChanged: () => {
      // Refresh device lists when devices change
      fetchDevices();
    }
  });

  const fetchDevices = async () => {
    try {
      const mics = await getMicrophones();
      const cameras = await getCameras();
      const speakers = await getPlaybackDevices();
      if (mics && mics.length) {
        setAudioInputs(mics);
        // Check if saved device exists, otherwise use first available
        const savedMic = localStorage.getItem('rlink_selectedMic');
        const micExists = savedMic && mics.some(m => m.deviceId === savedMic);
        if (!selectedAudioInput || !micExists) {
          setSelectedAudioInput(micExists ? savedMic : mics[0].deviceId);
        }
      }
      if (speakers && speakers.length) {
        setAudioOutputs(speakers);
        // Check if saved device exists, otherwise use first available
        const savedSpeaker = localStorage.getItem('rlink_selectedSpeaker');
        const speakerExists = savedSpeaker && speakers.some(s => s.deviceId === savedSpeaker);
        if (!selectedAudioOutput || !speakerExists) {
          setSelectedAudioOutput(speakerExists ? savedSpeaker : speakers[0].deviceId);
        }
      }
      if (cameras && cameras.length) {
        setVideoInputs(cameras);
        // Check if saved device exists, otherwise use first available
        const savedCamera = localStorage.getItem('rlink_selectedCamera');
        const cameraExists = savedCamera && cameras.some(c => c.deviceId === savedCamera);
        if (!selectedVideoInput || !cameraExists) {
          setSelectedVideoInput(cameraExists ? savedCamera : cameras[0].deviceId);
        }
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  // Fetch devices on mount (skip for webinar participants and recorder — triggers getUserMedia)
  const isWebinarViewerForDevices = meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
  useEffect(() => {
    if (!isRecorderMode && !isWebinarViewerForDevices) {
      fetchDevices();
    }
  }, [isWebinarViewerForDevices]);

  // Save device selections to localStorage when they change
  useEffect(() => {
    if (selectedAudioInput) {
      try {
        localStorage.setItem('rlink_selectedMic', selectedAudioInput);
      } catch (e) { console.error('Failed to save mic preference:', e); }
    }
  }, [selectedAudioInput]);

  // NOTE: speaker-output side-effect useEffect is declared below, after the
  // useMeeting() hook initializes `mMeeting` — placing it here would be a TDZ
  // violation since it references `mMeeting?.setPlaybackDevice`.

  useEffect(() => {
    if (selectedVideoInput) {
      try {
        localStorage.setItem('rlink_selectedCamera', selectedVideoInput);
      } catch (e) { console.error('Failed to save camera preference:', e); }
    }
  }, [selectedVideoInput]);

  // Re-apply speaker setting when participants change (new audio elements may be created)
  useEffect(() => {
    if (selectedAudioOutput && participantsData.length > 0) {
      // Small delay to ensure audio elements are rendered
      const timeout = setTimeout(() => {
        const audioElements = document.querySelectorAll('audio, video');
        audioElements.forEach((element) => {
          if (element.setSinkId) {
            element.setSinkId(selectedAudioOutput).catch(err => {
              // Ignore AbortError - occurs when element is removed during setSinkId operation
              if (err.name !== 'AbortError') {
                console.error('Error setting audio output on participant change:', err);
              }
            });
          }
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [participantsData, selectedAudioOutput]);

  // const ParticipantMicStream = memo(({ participantId }) => {
  //   // Individual hook for each participant
  //   const { micStream } = useParticipant(participantId);

  //   useEffect(() => {

  //     if (micStream) {
  //       const mediaStream = new MediaStream();
  //       mediaStream.addTrack(micStream.track);

  //       const audioElement = new Audio();
  //       audioElement.srcObject = mediaStream;
  //       audioElement.play();

  //     }
  //   }, [micStream, participantId]);

  //   return null;
  // }, [participantsData]);

  // Audio playback is now handled by Agora's audioTrack.play() in AgoraContext.js
  // ParticipantMicStream kept as a no-op for backward compatibility with render calls
  const ParticipantMicStream = memo(
    () => null,
    [participantsData]
  );

  const {
    useRaisedHandParticipants,
    raisedHandsParticipants,
    setRaisedHandsParticipants,
    brbParticipants,
    setBrbParticipants,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    setUnreadChat,
    sideBarMode,
    setRecentReactions,
    setParticipantReactions,
    transcriptionEnabled,
    translationEnabled,
    setTranslationEnabled,
    setActiveSpeakerId,
    setShowRecordingConsentModal,
    selectedLanguage,
    setSelectedLanguage,
    setShadowBannedParticipants,
    // Presenters state (for screen share permissions)
    presenters,
    setPresenters,
    // Virtual background state
    virtualBackgroundOn,
    setVirtualBackgroundOn,
    blurEnabled,
    setBlurEnabled,
    blurStrength,
    setBlurStrength,
    selectedBackground,
    setSelectedBackground,
    // Branding state
    branding,
    setBranding,
    participantBranding,
    setParticipantBranding,
  } = useMeetingAppContext();
  const rtm = useAgoraRTM();
  const meetingState = useMeetingState();
  const { getMeetingBranding } = useBrandKit();
  const { allMessages: mergedChatMessages, meetingStartedAt: snapshotMeetingStartedAt } = useMeetingStateSync();
  const bottomBarHeight = 60;

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] =
    useState(() => {
      // Recorder bot: allow join immediately
      if (new URLSearchParams(window.location.search).get('recorder') === 'true') return true;
      return null;
    });
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);
  const uiRetryCountRef = useRef(0);
  const [transcriptions, setTranscriptions] = useState(null);
  const [sttLiveText, setSttLiveText] = useState(null); // Live STT text for teleprompter (interim + final)
  const [isTranscriptionPanelVisible, setIsTranscriptionPanelVisible] =
    useState(false);
  // Store the current processed webcam stream (with virtual background applied)
  const [processedWebcamStream, setProcessedWebcamStream] = useState(null);
  const PANEL_FIXED_HEIGHT = 180;
  const PANEL_MOBILE_HEIGHT = 120; // Reduced height for mobile
  const [panelSize, setPanelSize] = useState({
    width: 450,
    height: PANEL_FIXED_HEIGHT,
  });
  const [panelPosition, setPanelPosition] = useState(() => {
    // Default to bottom-centered on desktop
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const panelHeight = PANEL_FIXED_HEIGHT;
    const initialWidth = 450; // matches initial panelSize.width
    return {
      x: Math.max(0, Math.floor((windowWidth - initialWidth) / 2)),
      y: windowHeight - panelHeight - 20,
    };
  });

  const [mobilePanelPosition, setMobilePanelPosition] = useState(() => {
    const windowWidth = window.innerWidth;
    const initialWidth = Math.min(450, windowWidth - 32);
    return {
      x: (windowWidth - initialWidth) / 2,
      y: 20,
    };
  });

  // Panel size constraints
  const PANEL_MIN_WIDTH = 300;
  const PANEL_MAX_WIDTH = window.innerWidth; // Allow expansion up to full browser width
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchDragOffset, setTouchDragOffset] = useState({ x: 0, y: 0 });
  const transcriptionPanelRef = useRef(null);
  const transcriptionListRef = useRef(null);

  // Whiteboard dragging and resizing states
  const [whiteboardPosition, setWhiteboardPosition] = useState(() => {
    const windowHeight = window.innerHeight;
    const whiteboardHeight = 600;
    return {
      x: 20, // 20px from left
      y: (windowHeight - whiteboardHeight) / 2,
    };
  });
  const [whiteboardSize, setWhiteboardSize] = useState({
    width: 800,
    height: 600,
  });
  const [isWhiteboardDragging, setIsWhiteboardDragging] = useState(false);
  const [isWhiteboardResizing, setIsWhiteboardResizing] = useState(false);
  const [whiteboardDragOffset, setWhiteboardDragOffset] = useState({
    x: 0,
    y: 0,
  });
  const [whiteboardResizeHandle, setWhiteboardResizeHandle] = useState(null);
  const whiteboardRef = useRef(null);
  const mMeetingRef = useRef();

  // Ref to prevent auto-mute from re-triggering in a loop
  const autoMuteAppliedRef = useRef(false);

  // ── Rally Token Earning ──
  const hostTokenEarnedRef = useRef(false);
  const attendTokenEarnedRef = useRef(false);
  const soloHostToastShownRef = useRef(false);
  const isLeaveAndAssignRef = useRef(false);
  const isForceDisconnectedRef = useRef(false);
  const isEndExceptHostRef = useRef(false);
  const [showRewardCelebration, setShowRewardCelebration] = useState(false);
  const [rewardCelebrationVariant, setRewardCelebrationVariant] = useState("earned");
  const chaChingSoundRef = useRef(null);

  const SOLO_HOST_MESSAGES = [
    'The host is here, the stage is set, and the Rally token is warming up. Just missing\u2026 literally anyone else.',
    'Waiting for the first attendee like it\'s a pizza delivery: "It\'ll be there in 30\u201345 seconds, probably."',
    'Your host is ready to Rally. Guests are fashionably late. The token is waiting silently.',
    'The meeting has begun. Attendance has not. The Rally token sips tea.',
    'Host: present. Slides: ready. Audience: buffering in real life.',
    'There\'s a Rally token at stake. All we need now is a human-shaped confirmation dialog.',
    'The host pressed "Start." The guests pressed "Snooze." The token pressed "Maybe."',
    'Waiting for the first attendee to arrive and save this meeting from becoming a one-person book club.',
    'The Rally token is like a cat: it\'ll come to you when someone else shows up.',
    'If an amazing host starts a meeting and no one is around to hear it\u2026 does the Rally token still shine? Yes, once someone arrives.',
    'The lobby is emptier than my inbox on a Friday afternoon. Token on hold.',
    'Guest ETA: soon\u2122. Rally token: "I\'ll allow it\u2026 once."',
    'The host is performing soundcheck to an audience of one (the Rally token, emotionally).',
    'We\'re one attendee away from turning this from "monologue" to "meeting." Token standing by.',
    'The first person to join gets to hear the host\'s best hello and unlock the Rally token. No pressure.',
    'Attendance currently set to "stealth mode." Rally token refuses to make eye contact.',
    'Where there\'s a will (and a guest), there\'s a Rally token. We\'re halfway there.',
    'The host is ready to Rally. The guests are practicing their dramatic entrance.',
    'Consider this the pre-credit scene. The main feature starts when the first guest appears\u2014and the token rolls in.',
    'This meeting is like a vending machine: insert one attendee to dispense one Rally token.',
  ];

  // Ref to track if we need to publish whiteboard open after it starts
  const pendingWhiteboardPublishRef = useRef(false);

  // Wrapper function for startWhiteboard that passes meeting context
  const startWhiteboard = useCallback(async () => {
    console.log('[Whiteboard] startWhiteboard called, mMeetingRef:', mMeetingRef.current);
    if (!mMeetingRef.current?.meetingId) {
      console.warn('[Whiteboard] Cannot start whiteboard - meeting not ready');
      return;
    }
    console.log('[Whiteboard] Starting whiteboard with meetingId:', mMeetingRef.current.meetingId);

    // Mark that we need to publish the room UUID after whiteboard opens
    pendingWhiteboardPublishRef.current = true;

    const lid = mMeetingRef.current.localParticipant?.id;
    const lnum = lid ? stringToNumericUid(lid) : null;
    const isLocalPresenter = lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum)));
    const result = await startWhiteboardBase({
      meetingId: mMeetingRef.current.meetingId,
      userId: lid || `user-${Date.now()}`,
      userName: mMeetingRef.current.localParticipant?.displayName || 'Anonymous',
      isHost: meetingRole === 'moderator' || meetingRole === 'co-host' || !!isLocalPresenter,
    });
    // Record this session as "ours" immediately so the Socket.IO sync effect
    // below doesn't treat the stale initial (isOpen=false, roomUUID=null)
    // socket state as a close signal against the freshly-opened local room.
    if (result?.roomUUID) {
      lastPublishedWhiteboardUUID.current = result.roomUUID;
    }
    // Socket.IO redundancy — persist open state in MeetingStateSnapshot so
    // late joiners and participants returning from breakout rooms see the
    // active whiteboard without relying on RTM PubSub persist.
    if (result?.roomUUID && meetingState?.emitWhiteboardOpen) {
      meetingState.emitWhiteboardOpen(result.roomUUID);
    }
  }, [startWhiteboardBase, meetingRole, presenters, meetingState]);

  // Publish whiteboard room UUID to participants when it becomes available
  // This ref tracks the last published UUID to prevent duplicate publishes
  const lastPublishedWhiteboardUUID = useRef(null);
  // Ref to hold the publish function (populated later when usePubSub is called)
  const publishWhiteboardRef = useRef(null);

  // Wrapper for stopWhiteboard that also notifies participants.
  // Called from WhiteboardContainer's onClose for all users.
  // - Moderator / co-host / presenter: authoritative close for everyone.
  //   Presenters already have write access to the board (same SDK role as
  //   host), so it's consistent that their X button also closes for all —
  //   the "Show Whiteboard" hide/unhide dance is only for regular guests.
  // - Regular guest: just hide the panel visually, keep the room connection
  //   alive so they can re-show it without a rejoin.
  const stopWhiteboard = useCallback(async () => {
    const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    const lid = mMeetingRef.current?.localParticipant?.id;
    const lnum = lid ? stringToNumericUid(lid) : null;
    const isLocalPresenter = lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum)));
    const canCloseForAll = isHostOrCoHost || !!isLocalPresenter;
    const roomUUID = whiteboardRoomInfo?.uuid || lastPublishedWhiteboardUUID.current;

    console.log('[Whiteboard] Stopping whiteboard, canCloseForAll:', canCloseForAll, 'roomUUID:', roomUUID);

    if (canCloseForAll) {
      // Host / co-host / presenter: fully close and notify everyone
      if (roomUUID && publishWhiteboardRef.current) {
        console.log('[Whiteboard] Authoritative close - publishing close message to participants');
        publishWhiteboardRef.current(
          JSON.stringify({ action: 'close', roomUUID }),
          { persist: true }
        );
        lastPublishedWhiteboardUUID.current = null;
      }
      // Socket.IO redundant close — persists false in MeetingStateSnapshot
      // so it survives RTM drops or token expiry in long sessions.
      if (meetingState?.emitWhiteboardClose) {
        meetingState.emitWhiteboardClose();
      }
      setGuestHiddenWhiteboard(false);
      await stopWhiteboardBase();
    } else {
      // Regular guest: just hide the panel visually — keep the connection alive
      console.log('[Whiteboard] Guest hiding whiteboard (connection stays alive)');
      setGuestHiddenWhiteboard(true);
    }
  }, [stopWhiteboardBase, meetingRole, whiteboardRoomInfo, meetingState, presenters]);

  const containerRef = createRef();
  const meetingAreaRef = useRef(null);
  const containerHeightRef = useRef();
  const containerWidthRef = useRef();

  // Agora Virtual Background processor
  const vbProcessorRef = useRef(null);
  const vbPipedRef = useRef(false);
  // Monotonic generation counter — bumped on every handleStartVirtualBackground
  // call. A call whose generation is no longer current at the time of a slow
  // resource (image load) finishing will bail out instead of overwriting a
  // later, more recent user choice.
  const vbApplyGenRef = useRef(0);
  // Flag set as soon as the user manually applies a VB choice; prevents the
  // auto-apply from overwriting it if it happens to resolve later.
  const vbUserInteractedRef = useRef(false);
  // Serializes processor mutations: each handleStartVirtualBackground call
  // awaits the previous one before touching the shared processor. Agora's VB
  // extension is not concurrency-safe — overlapping disable/enable cycles
  // leave the publisher track in a bad state and the camera goes black.
  const vbInflightRef = useRef(Promise.resolve());
  // Chroma key (green screen) processor
  const chromaKeyProcessorRef = useRef(null);
  // Logo/QR branding is rendered as HTML overlay in ParticipantView (no canvas processing needed)
  const [greenScreenEnabled, setGreenScreenEnabled] = useState(false);
  const [greenScreenConfig, setGreenScreenConfig] = useState({
    keyColor: '#00ff00',
    similarity: 40,
    smoothness: 10,
    backgroundImageUrl: null,
  });

  // Camera track configuration - maintains resolution when applying virtual backgrounds
  // Resolution is based on the cameraResolution state.
  // Attendees (non host/co-host/presenter) are always capped at 480p regardless of saved setting.
  const getEncoderConfig = (resolution) => {
    const isPrivilegedRole = ["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
    if (!isPrivilegedRole) {
      return "h480p_w640p";
    }
    switch (resolution) {
      case '1080p':
        return "h1080p_w1920p";
      case '720p':
        return "h720p_w1280p";
      case '480p':
        return "h480p_w640p";
      default:
        return "h480p_w640p";
    }
  };

  const hdCameraTrackConfig = {
    optimizationMode: "motion",
    encoderConfig: getEncoderConfig(cameraResolution),
    facingMode: "user",
    multiStream: false,
  };

  // Handler for camera resolution changes from settings modal
  const handleCameraResolutionChange = async (newResolution) => {
    console.log("Changing camera resolution to:", newResolution);
    setCameraResolution(newResolution);

    // If webcam is currently on, re-create the camera track with the new resolution
    if (mMeeting?.localWebcamOn) {
      try {
        const newConfig = {
          optimizationMode: "motion",
          encoderConfig: getEncoderConfig(newResolution),
          facingMode: "user",
          multiStream: false,
        };

        // With Agora extension, virtual background is piped in the track pipeline
        // Just changing resolution doesn't require re-applying the virtual background
        // The Agora VB processor stays piped and continues processing

        toast.success(`Camera resolution changed to ${newResolution}`);
      } catch (error) {
        console.error("Error changing camera resolution:", error);
        toast.error("Failed to change camera resolution");
      }
    }
  };

  // Handler for audio settings changes from settings modal
  const handleAudioSettingsChange = async (audioSettings) => {
    // console.log("Audio settings changed:", audioSettings); // commented out to reduce console noise

    // Apply output volume to all audio/video elements
    if (audioSettings.outputVolume !== undefined) {
      const volumeLevel = audioSettings.outputVolume / 100;
      const audioElements = document.querySelectorAll('audio, video');
      audioElements.forEach((element) => {
        element.volume = volumeLevel;
      });
    }

    // Note: Noise suppression and echo cancellation are typically applied when
    // creating the audio track. To apply these settings in real-time, we would
    // need to recreate the audio track with new constraints. For now, these
    // settings will be applied when the mic is next enabled.
    // The settings are stored in localStorage and will be read by JoiningScreenNew
    // when starting the mic test or when joining a meeting.
  };

  useEffect(() => {
    containerHeightRef.current = containerHeight;
    containerWidthRef.current = containerWidth;
  }, [containerHeight, containerWidth]);

  useEffect(() => {
    if (showJoinScreen) {
      getMeetingSession(meetingSettings.roomId);
    }
  }, [showJoinScreen]);

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const windowSize = useWindowSize();
  const isMobileLike =
    (windowSize?.height || 0) > 0 && windowSize.height < 500 ? true : isMobile;
  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobileLike && isLandscapeOrientation;
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [mobilePanelWidth, setMobilePanelWidth] = useState(0);

  // Detect recorder mode — hides all controls/sidebars for clean recording.
  // Grid-recorder cells pass their own layout via prop (one shared URL for
  // all 4 cells, per-cell layout via props); legacy single-Chrome recorders
  // fall back to the URL `?layout=` param.
  const isRecorderMode = new URLSearchParams(window.location.search).get('recorder') === 'true';
  const recorderLayout = recorderLayoutOverride || new URLSearchParams(window.location.search).get('layout');

  // Grid-recorder shared-Agora optimization: 4 sibling MeetingContainerNew
  // instances live inside one MeetingProvider. By default every useEffect in
  // every cell fires on every state change → 4× the socket listeners, 4×
  // setActiveSpeakerId writes, 4× beforeunload handlers, etc. These flags
  // pick ONE cell (gallery) to own side effects the others don't need.
  //
  // isSecondaryRecorderCell — non-gallery recorder cells. Pure render, no
  //   writes to shared contexts, no side-effecting subscriptions.
  // Real users (no recorderLayoutOverride): both flags are false so every
  //   effect runs exactly as it did before this refactor.
  const isSecondaryRecorderCell = !!recorderLayoutOverride && recorderLayoutOverride !== 'gallery';
  // True when the recorder bot was launched with a specific layout chosen by
  // the host in the "Start recording this meeting?" modal. When this is set,
  // ALL other code paths that would normally call setSelectedLayout (webinar
  // attendee defaults, snapshot hydration, spotlight handlers, host live-sync,
  // etc.) MUST be skipped — otherwise the host's natural activity overwrites
  // the user's recording-layout choice within milliseconds of the bot joining.
  const ALLOWED_RECORDER_LAYOUTS = ['gallery', 'focus', 'speaker-top', 'speaker-side'];
  const isRecorderLayoutPinned = isRecorderMode && !!recorderLayout && ALLOWED_RECORDER_LAYOUTS.includes(recorderLayout);

  // For recorder bots in focus / speaker-top / speaker-side layouts the client
  // wants stage elements (presentation, web/website overlay, CTA, contract)
  // to fit inside the focus tile rather than cover the whole viewport. We
  // achieve this by relocating those renderers into an inline slot inside
  // the layout's main speaker area and wrapping them in a transformed div —
  // `transform: translateZ(0)` reparents `position: fixed` descendants so
  // their `inset: 0` resolves to the wrapper, not the viewport. No edits
  // needed to the individual renderer components.
  const useInlineRecorderElements = isRecorderMode && (
    selectedLayout === 'focus' ||
    selectedLayout === 'speaker-top' ||
    selectedLayout === 'speaker-side'
  );

  // Apply recording layout from URL param when in recorder mode.
  //
  // Resolution order (applies to BOTH meetings and webinars):
  //   1. Explicit ?layout= query param — this is what the host picked in the
  //      "Start recording this meeting?" modal and is the source of truth.
  //   2. Host's current active layout from the meeting-state snapshot — used
  //      if the recorder URL didn't include a layout (older clients) or the
  //      host changes layout mid-recording.
  //   3. 'speaker-top' as a final fallback.
  //
  // Previously webinars unconditionally took branch (2)+(3), which discarded
  // the user's modal choice and made every webinar recording look like
  // Speaker Top regardless of what was selected.
  useEffect(() => {
    if (!isRecorderMode) return;

    let layout = null;
    if (recorderLayout && ALLOWED_RECORDER_LAYOUTS.includes(recorderLayout)) {
      layout = recorderLayout;
      console.log('[Recorder] Using layout from URL param:', layout);
    } else if (meetingState.activeLayout && ALLOWED_RECORDER_LAYOUTS.includes(meetingState.activeLayout)) {
      layout = meetingState.activeLayout;
      console.log('[Recorder] Using host active layout:', layout);
    } else {
      layout = 'speaker-top';
      console.log('[Recorder] Defaulting to speaker-top');
    }
    setSelectedLayout(layout);
  }, [isRecorderMode, recorderLayout, setSelectedLayout, meetingState.activeLayout]);

  const [isBottomParticipantCollapsed, setIsBottomParticipantCollapsed] =
    useState(false);

  const sideBarContainerWidth = isXLDesktop
    ? 400
    : isLGDesktop
      ? 360
      : isTab
        ? 320
        : isMobile
          ? 280
          : 240;

  useEffect(() => {
    if (isMobile) {
      const widthForMobile = Math.min(window.innerWidth - 32, panelSize.width);
      setPanelSize((prev) => ({
        ...prev,
        width: widthForMobile,
        height: PANEL_MOBILE_HEIGHT,
      }));
      setPanelPosition({
        x: (window.innerWidth - widthForMobile) / 2,
        y: window.innerHeight - PANEL_MOBILE_HEIGHT - 20,
      });
    }
  }, [isMobile]);

  // Reserve space when the mobile panel is open in landscape
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setIsMobilePanelOpen(!!detail.open);
      setMobilePanelWidth(detail.width || 0);
    };
    window.addEventListener("rlink:mobile-panel", handler);
    return () => window.removeEventListener("rlink:mobile-panel", handler);
  }, []);

  useEffect(() => {
    containerRef.current?.offsetHeight &&
      setContainerHeight(containerRef.current.offsetHeight);
    containerRef.current?.offsetWidth &&
      setContainerWidth(containerRef.current.offsetWidth);

    const onResize = () => {
      containerRef.current?.offsetHeight &&
        setContainerHeight(containerRef.current.offsetHeight);
      containerRef.current?.offsetWidth &&
        setContainerWidth(containerRef.current.offsetWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [containerRef]);

  const { participantRaisedHand, setParticipantHandRaised } = useRaisedHandParticipants();

  const _handleMeetingLeft = () => {
    // If in a breakout room, return to main room instead of showing leave screen
    const originRoom = localStorage.getItem('rlink_breakout_origin');
    if (originRoom) {
      const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId');
      const breakoutOdId = localStorage.getItem('rlink_breakout_odId');
      const existingName = localStorage.getItem('rlink_breakout_name');
      const currentName = existingName || user?.name || '';

      console.log('[Breakout] Participant leaving breakout room, returning to:', originRoom, 'name:', currentName);

      // Save unassign info so the main room's socket can notify the backend on connect
      // (more reliable than fetch/sendBeacon which can fail due to CORS or timing)
      if (parentMeetingId && breakoutOdId) {
        localStorage.setItem('rlink_returned_from_breakout', JSON.stringify({
          parentMeetingId, odId: breakoutOdId,
        }));
      }

      // Clear breakout state and redirect immediately
      localStorage.removeItem('rlink_breakout_origin');
      localStorage.removeItem('rlink_breakout_meetingId');
      localStorage.removeItem('rlink_breakout_odId');
      localStorage.removeItem('rlink_breakout_roomId');
      localStorage.removeItem('rlink_breakout_roomName');
      localStorage.removeItem('rlink_breakout_moderatorId');
      localStorage.removeItem('rlink_breakout_moderatorName');
      localStorage.removeItem('rlink_breakout_isModerator');
      localStorage.setItem('rlink_breakout_name', currentName);
      window.location.href = `/${originRoom}?fromBreakout=1`;
      return;
    }
    setIsMeetingLeft(true);
  };

  // Redirect to exitUrl if configured, otherwise show the leave screen.
  // Used for all leave/end paths so the exitUrl is always honoured.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const leaveOrRedirect = useCallback(() => {
    // If in a breakout room, return to main room
    const originRoom = localStorage.getItem('rlink_breakout_origin');
    if (originRoom) {
      _handleMeetingLeft();
      return;
    }
    const exitUrl = meetingSettings?.exitUrl;
    if (exitUrl && exitUrl !== 'false') {
      window.location.href = exitUrl;
    } else {
      setIsMeetingLeft?.(true);
    }
  }, [meetingSettings?.exitUrl]);

  const _handleOnRecordingStateChanged = ({ status }) => {
    console.log("onRecordingStateChanged", status);
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      const isStarted = status === Constants.recordingEvents.RECORDING_STARTED;
      // Sync recording state to backend for late joiners.
      // Look up the actual recordingMode from the backend status endpoint so the
      // pause/resume UI can gate on web-mode (only mode that supports pause).
      if (isStarted) {
        (async () => {
          try {
            const meetingId = mMeeting?.meetingId;
            if (!meetingId) {
              meetingState.emitRecordingUpdate(true, 'cloud');
              return;
            }
            const data = await requests.get(`/recording/status/${meetingId}`);
            const mode = data?.recordingMode || 'cloud';
            const paused = !!data?.paused;
            meetingState.emitRecordingUpdate(true, mode, paused);
          } catch (err) {
            console.warn('[Recording] Failed to fetch mode from status, falling back to cloud:', err.message);
            meetingState.emitRecordingUpdate(true, 'cloud');
          }
        })();
      } else {
        meetingState.emitRecordingUpdate(false, null);
      }
      // Note: Recording consent modal is handled by useTopBar.js via PubSub RECORDING_NOTICE
      // Do NOT show it here — this callback also fires from the 10s polling in AgoraContext,
      // which would cause a duplicate modal after the user already dismissed the PubSub-triggered one.
      // toast(
      //   `${status === Constants.recordingEvents.RECORDING_STARTED
      //     ? "Meeting recording is started"
      //     : "Meeting recording is stopped."
      //   }`,
      //   {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   }
      // );
    }
  };

  function onParticipantJoined(participant) {
    console.log("onParticipantJoined", participant, mMeeting);
    // Quality is managed by ParticipantStreamController based on participant count and active speaker

    // Re-publish current branding so the new participant gets it
    // (branding pubsub does not use persist to avoid stale cross-session data)
    setTimeout(() => {
      publishCurrentBrandingRef.current?.();
      // Re-publish display name change so late joiners see updated names
      if (branding?.showLowerThird && branding?.lowerThirdName) {
        publishDisplayNameChangeRef.current?.(JSON.stringify({ newName: branding.lowerThirdName }));
      }
    }, 1500);
  }

  function onMeetingJoined() {
    // Allow participant to join - this enables studio controls for all join flows
    // (both with and without entry approval required)
    setLocalParticipantAllowedJoin(true);

    // Auto-load user's default brand kit as meeting branding (host/moderator only)
    if (["moderator", "co-host"].includes(meetingRole)) {
      const kitBranding = getMeetingBranding();
      if (kitBranding) {
        setBranding((prev) => ({ ...prev, ...kitBranding }));
      }
    }

    // Recorder bot: immediately show meeting view (skip all animations/join screens)
    if (isRecorderMode) {
      console.log('[Recorder] onMeetingJoined — forcing showJoinScreen=false, localParticipantAllowedJoin=true');
      setShowJoinScreen(false);
      if (typeof window.navigator.notifyReady === 'function') {
        window.navigator.notifyReady();
      }
      // Unpublish camera + mic so the recorder doesn't appear in the meeting
      // as a visible/audible participant. The recorder is a silent observer
      // whose only job is to render the meeting for capture.
      try {
        const m = mMeetingRef.current;
        if (m?.disableWebcam) m.disableWebcam();
        if (m?.disableMic) m.disableMic();
        if (m?.toggleWebcam && m?.localParticipant?.webcamOn) m.toggleWebcam();
        if (m?.toggleMic && m?.localParticipant?.micOn) m.toggleMic();
        console.log('[Recorder] Unpublished camera & mic — recorder is now silent observer');
      } catch (e) {
        console.warn('[Recorder] Failed to unpublish camera/mic:', e?.message || e);
      }
    }

    // Start call duration timer — use snapshot start time for late joiners
    setMeetingStartTime(snapshotMeetingStartedAt || Date.now());

    // Mark webinar as live via HTTP when host joins
    if (meetingSettings?.roomType === 'Webinar' && meetingSettings?.roomId &&
        ["moderator", "host"].includes(meetingRole)) {
      startWebinar(meetingSettings.roomId).catch((err) =>
        console.error('[MeetingContainer] Failed to start webinar via HTTP:', err)
      );
    }

    // Initialize PubSub RTM for network messaging (mute all, etc.)
    if (mMeetingRef.current?.meetingId && mMeetingRef.current?.localParticipant?.id) {
      initializePubSubRTM(
        mMeetingRef.current.meetingId,
        mMeetingRef.current.localParticipant.id
      );
    }

    if (
      meetingSettings?.muteParticipants &&
      !["moderator", "co-host"].includes(meetingRole)
    ) {
      setTimeout(() => {
        if (mMeetingRef.current) {
          mMeetingRef.current.muteMic();
          setMicOn(false);
        }
      }, 100);
    }

    // Enforce muteAll for late joiners — if host has muted all, mute this participant
    if (
      meetingState.hostControls?.muteAll &&
      !["moderator", "co-host", "host"].includes(meetingRole)
    ) {
      console.log('[onMeetingJoined] muteAll is active — force muting late joiner');
      setMutedByHost(true);
      setMicOn(false);
      setTimeout(async () => {
        if (mMeetingRef.current?.localMicOn) {
          await mMeetingRef.current.muteMic();
        }
        // Confirm via presence state
        if (rtm?.setPresenceState) {
          await rtm.setPresenceState({ micMuted: "true" });
        }
      }, 300);
    }

    // Phase 2: Set initial presence state on join
    if (rtm?.setPresenceState) {
      const initialMicMuted = meetingSettings?.muteParticipants && !["moderator", "co-host"].includes(meetingRole);
      const presenceState = {
        micMuted: initialMicMuted || !micOn ? "true" : "false",
        camOff: webcamOn ? "false" : "true",
        role: meetingRole || "participant",
        displayName: mMeetingRef.current?.localParticipant?.displayName || "Unknown",
        videoResolution: mMeetingRef.current?.currentVideoProfileLabel || "1280x720",
        optimizationMode: mMeetingRef.current?.currentOptimizationMode || "motion",
      };
      // Include DB userId for logged-in users (used for co-host DB persistence)
      if (user?.id) {
        presenceState.userId = user.id;
      }
      rtm.setPresenceState(presenceState);
    }

    // Set host identity for transcript speaker attribution
    if (["moderator", "host"].includes(meetingRole)) {
      meetingState.emitTranscriptSetHost(mMeetingRef.current?.localParticipant?.id);
    }
  }

  async function onMeetingLeft() {
    // If in a breakout room, redirect back to main room instead of normal leave flow
    const breakoutOrigin = localStorage.getItem('rlink_breakout_origin');
    if (breakoutOrigin) {
      const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId');
      const breakoutOdId = localStorage.getItem('rlink_breakout_odId');
      const existingName = localStorage.getItem('rlink_breakout_name');
      const currentName = existingName || user?.name || '';

      console.log('[Breakout] onMeetingLeft: returning to main room:', breakoutOrigin);

      // Save flag so main room socket can unassign on connect
      if (parentMeetingId && breakoutOdId) {
        localStorage.setItem('rlink_returned_from_breakout', JSON.stringify({
          parentMeetingId, odId: breakoutOdId,
        }));
      }

      localStorage.removeItem('rlink_breakout_origin');
      localStorage.removeItem('rlink_breakout_meetingId');
      localStorage.removeItem('rlink_breakout_odId');
      localStorage.removeItem('rlink_breakout_roomId');
      localStorage.removeItem('rlink_breakout_roomName');
      localStorage.removeItem('rlink_breakout_moderatorId');
      localStorage.removeItem('rlink_breakout_moderatorName');
      localStorage.removeItem('rlink_breakout_isModerator');
      localStorage.setItem('rlink_breakout_name', currentName);
      window.location.href = `/${breakoutOrigin}?fromBreakout=1`;
      return;
    }

    // Cleanup PubSub RTM
    cleanupPubSubRTM();

    // Auto-stop cloud recording if still active when the HOST leaves.
    // Skip for participants — they shouldn't stop the host's recording.
    // Skip if host is transferring via "Leave & Assign Host" — recording should continue.
    const isHostLeaving = ["moderator", "host", "co-host"].includes(meetingRole);
    if (isHostLeaving && !isLeaveAndAssignRef.current && !isForceDisconnectedRef.current &&
        (mMeetingRef.current?.recordingState === Constants?.recordingEvents?.RECORDING_STARTED ||
         mMeetingRef.current?.recordingState === 'RECORDING_STARTED')) {
      try {
        console.log('[MeetingContainer] Auto-stopping recording on meeting leave');
        await mMeetingRef.current?.stopRecording?.();
      } catch (err) {
        console.error('[MeetingContainer] Error auto-stopping recording:', err);
      }
    }

    // If host, end the meeting state for all late joiners
    // Skip if host is leaving via "Leave & Assign Host" (meeting should continue)
    // Skip if host was force-disconnected (joined from another device — meeting continues)
    if (["moderator", "host"].includes(meetingRole) && !isLeaveAndAssignRef.current && !isForceDisconnectedRef.current) {
      // Trigger AI notes generation from the transcript
      meetingState.emitTranscriptMeetingEnded(
        meetingSettings?.roomId || mMeeting?.meetingId,
        mMeeting?.localParticipant?.displayName || 'Host',
        customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        meetingSettings?.roomCreatedBy || ''
      );
      meetingState.emitMeetingEnd();

      // Mark webinar as ended via HTTP (reliable fallback for socket)
      if (meetingSettings?.roomType === 'Webinar' && meetingSettings?.roomId) {
        endWebinar(meetingSettings.roomId).catch((err) =>
          console.error('[MeetingContainer] Failed to end webinar via HTTP:', err)
        );
      }
    }

    // Save session for hosts/moderators/co-hosts
    if (["moderator", "host", "co-host"].includes(meetingRole)) {
      try {
        const participantList = [...(mMeetingRef.current?.participants?.values?.() || [])].map(p => ({
          id: p.id,
          name: p.displayName || 'Guest',
        }));
        const sessionData = {
          roomId: meetingSettings?.roomId,
          meetingId: mMeetingRef.current?.meetingId || meetingSettings?.roomId,
          participants: participantList,
          startedAt: (() => {
            const raw = snapshotMeetingStartedAt || meetingStartTime;
            if (!raw) return new Date().toISOString();
            if (typeof raw === 'number') return new Date(raw).toISOString();
            return raw;
          })(),
          meetingName: customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        };
        console.log('[SaveSession] onMeetingLeft saving session:', sessionData.roomId, 'startedAt:', sessionData.startedAt);
        meetingServices.saveMeetingSession(sessionData).catch(err =>
          console.error('[SaveSession] onMeetingLeft save failed:', err)
        );
      } catch (error) {
        console.error('[SaveSession] onMeetingLeft error:', error);
      }
    }

    // ── Rally Token: Earn attend token for non-host participants ──
    if (!["moderator", "host"].includes(meetingRole) && !attendTokenEarnedRef.current) {
      attendTokenEarnedRef.current = true;
      rewardService.earnTokens({
        actionType: 'attend',
        roomId: meetingSettings?.roomId,
        meetingId: mMeetingRef.current?.meetingId || meetingSettings?.roomId,
      }).then(result => {
        console.log('[Rewards] Attend token earned:', result);
      }).catch(err => {
        console.error('[Rewards] Failed to earn attend token:', err);
      });
    }

    // Clear presenter state so it doesn't persist across meetings
    setPresenters(new Set());

    setSelectedMic({ id: null, label: null });
    setSelectedWebcam({ id: null, label: null });
    setSelectedSpeaker({ id: null, label: null });
    onMeetingLeave();
    setIsMeetingReconnecting(false);
  }

  // Save session on tab/browser close via sendBeacon (works even during unload)
  useEffect(() => {
    // Recorder bot doesn't save sessions on unload — the worker droplet
    // handles its own cleanup. In grid mode this would fire 4 fetch()s on
    // every pageunload.
    if (isRecorderMode) return;
    const handleBeforeUnload = () => {
      const roomId = meetingSettings?.roomId;
      if (!roomId) return;

      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BASE_URL;

      // Save session for hosts/moderators/co-hosts
      if (["moderator", "host", "co-host"].includes(meetingRole)) {
        const sessionData = {
          roomId,
          meetingId: mMeetingRef.current?.meetingId || roomId,
          participants: [...(mMeetingRef.current?.participants?.values?.() || [])].map(p => ({
            id: p.id,
            name: p.displayName || 'Guest',
          })),
          startedAt: (() => {
            const raw = snapshotMeetingStartedAt || meetingStartTime;
            if (!raw) return new Date().toISOString();
            if (typeof raw === 'number') return new Date(raw).toISOString();
            return raw;
          })(),
          meetingName: customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        };

        fetch(`${baseUrl}/rooms/save-meeting-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(sessionData),
          keepalive: true,
        }).catch(() => {});
      }

      // ── Rally Token: Earn attend token on tab close (host token is handled by real-time socket event) ──
      if (!["moderator", "host"].includes(meetingRole) && !attendTokenEarnedRef.current) {
        attendTokenEarnedRef.current = true;
        fetch(`${baseUrl}/rewards/earn`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ actionType: 'attend', roomId, meetingId: mMeetingRef.current?.meetingId || roomId }),
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [meetingRole, meetingSettings, snapshotMeetingStartedAt]);

  const _handleOnError = (data) => {
    const { code, message } = data;
    console.log("meetingErr", code, message);

    // Don't show error modal for retryable join errors — the auto-retry will handle them
    const isRetryableJoinError = code === 'JOIN_FAILED' && (
      message?.includes('UID_BANNED') || message?.includes('WS_ABORT') || message?.includes('OPERATION_ABORTED')
    );
    if (isRetryableJoinError) {
      console.log('[meetingErr] Retryable join error, auto-retry will handle it');
      return;
    }

    setMeetingErrorVisible(true);
    setMeetingError({
      code,
      message: code === 'JOIN_FAILED' ? "Unable to join meeting!" : message,
    });
  };

  const { enableAdaptiveSubscriptions, ...mMeeting } = useMeeting({
    onParticipantJoined,
    onMeetingJoined,
    onMeetingStateChanged: ({ state }) => {
      // toast(`Meeting is in ${state} state`, {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      // });
      if (state === "RECONNECTING") {
        console.log("Meeting is Reconnecting");
        setIsMeetingReconnecting(true);
      }

      if (state === "CONNECTED") {
        console.log("isMeetinngReconnecting", isMeetinngReconnecting);
        let isReconnecting = isMeetinngReconnecting;
        setIsMeetingReconnecting((prev) => {
          console.log("prev", prev);
          isReconnecting = prev;
          return prev;
        });
        setIsMeetingReconnecting(false);
        if (!isReconnecting) {
          if (isRecorderMode) {
            setShowJoinScreen(false);
            // Signal Agora's headless browser that the page is ready
            if (typeof window.navigator.notifyReady === 'function') {
              window.navigator.notifyReady();
              console.log('[Recorder] notifyReady sent to Agora web recorder');
            }
          } else {
            setShowJoinScreen(true);
            console.log("meetingSettings.roomId", meetingSettings.roomId);
            setTimeout(() => {
              setShowJoinScreen(false);
            }, 3500);
          }
        }
      }

      if (state === "DISCONNECTED") {
        setIsMeetingReconnecting(false);
      }
    },
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
    onSpeakerChanged: (newActiveSpeakerId) => {
      if (newActiveSpeakerId) {
        console.log("Active Speaker participantId", newActiveSpeakerId);
        setActiveSpeakerId(newActiveSpeakerId);
      }
    },
    onPresenterChanged: (presenterId) => {
      // Handle audio recovery when screen share starts/stops
      // This helps prevent audio dropout issues in some browsers
      if (presenterId) {
        console.log("Presenter started:", presenterId);
        // If local user started presenting, ensure mic remains active
        // Use setEnabled() (Agora API) for reliable recovery instead of raw .enabled
        setTimeout(async () => {
          const localParticipant = mMeeting?.localParticipant;
          if (localParticipant?.id === presenterId && localParticipant?.micOn) {
            const micTrack = localParticipant?.micStream?.track;
            if (micTrack && !micTrack.enabled) {
              console.log("Re-enabling mic track after screen share (onPresenterChanged)");
              try {
                await micTrack.setEnabled(true);
              } catch (e) {
                micTrack.enabled = true; // fallback
              }
            }
          }
        }, 300);
      } else {
        console.log("Presenter stopped");
      }
    },
  });

  // Keep mic/cam state in localStorage so breakout redirects preserve it.
  // Uses actual Agora state (localMicOn/localWebcamOn) instead of Conference.js props,
  // which only reflect the initial state and don't update when user toggles mic/cam.
  useEffect(() => {
    localStorage.setItem('rlink_meeting_micOn', mMeeting.localMicOn ? '1' : '0');
    localStorage.setItem('rlink_meeting_webcamOn', mMeeting.localWebcamOn ? '1' : '0');
  }, [mMeeting.localMicOn, mMeeting.localWebcamOn]);

  // Persist speaker selection and route remote audio through it. Declared here
  // (not alongside the other device useEffects above) because it depends on
  // `mMeeting`, which is initialized by useMeeting() further up — referencing
  // it before that point caused a TDZ ReferenceError at runtime.
  useEffect(() => {
    if (selectedAudioOutput) {
      try {
        localStorage.setItem('rlink_selectedSpeaker', selectedAudioOutput);
        // Route Agora-rendered remote audio (regular + screen share) to the chosen
        // device. setSinkId() on DOM elements does NOT affect Agora's internally
        // played remote audio tracks — only setPlaybackDevice() on the track does.
        if (mMeeting?.setPlaybackDevice) {
          mMeeting.setPlaybackDevice(selectedAudioOutput);
        }
        // Apply to any DOM-rendered audio/video (test sounds, local previews, etc.).
        const audioElements = document.querySelectorAll('audio, video');
        audioElements.forEach((element) => {
          if (element.setSinkId) {
            element.setSinkId(selectedAudioOutput).catch(err => {
              // Ignore AbortError - occurs when element is removed during setSinkId operation
              if (err.name !== 'AbortError') {
                console.error('Error setting audio output:', err);
              }
            });
          }
        });
      } catch (e) { console.error('Failed to save speaker preference:', e); }
    }
  }, [selectedAudioOutput, mMeeting?.setPlaybackDevice]);

  // Safety net: if Agora reports CONNECTED but our callback-driven state hasn't updated,
  // force the meeting UI to render.
  useEffect(() => {
    if (mMeeting.meetingState === 'CONNECTED' && localParticipantAllowedJoin !== true) {
      console.log('[SafetyNet] CONNECTED but localParticipantAllowedJoin not true — forcing');
      setLocalParticipantAllowedJoin(true);
    }
    if (mMeeting.meetingState === 'CONNECTED' && showJoinScreen === null) {
      if (isRecorderMode) {
        // Recorder: skip join animation, go straight to meeting view
        console.log('[SafetyNet] Recorder mode — skipping join animation');
        setShowJoinScreen(false);
        // Signal Agora's headless browser that the page is ready
        if (typeof window.navigator.notifyReady === 'function') {
          window.navigator.notifyReady();
          console.log('[Recorder] notifyReady sent to Agora web recorder');
        }
        // Signal backend to unpause recording (page is ready)
        try {
          requests.post(`/recording/ready`, {
            meetingId: meetingSettings?.roomId
          }).catch(() => {});
        } catch (_) {}
      } else {
        console.log('[SafetyNet] CONNECTED but showJoinScreen null — triggering cycle');
        setShowJoinScreen(true);
        setTimeout(() => setShowJoinScreen(false), 3500);
      }
    }

    // Auto-retry on FAILED state
    if (mMeeting.meetingState === 'FAILED' && mMeeting.join && uiRetryCountRef.current < 2) {
      uiRetryCountRef.current += 1;
      const retryNum = uiRetryCountRef.current;
      console.log(`[SafetyNet] FAILED — UI retry ${retryNum}/2 in 3s`);
      const retryTimer = setTimeout(() => mMeeting.join(), 3000);
      return () => clearTimeout(retryTimer);
    }

    if (mMeeting.meetingState === 'CONNECTED') {
      uiRetryCountRef.current = 0;
    }
  }, [mMeeting.meetingState, localParticipantAllowedJoin, showJoinScreen, mMeeting.join]);

  // Ultimate fallback: if the meeting UI hasn't rendered within 8 seconds of mount,
  // force it to render regardless of state. This catches any scenario where callbacks
  // fail to fire, join hangs, or state transitions are missed.
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (localParticipantAllowedJoin !== true || showJoinScreen !== false) {
        console.warn('[SafetyNet] Fallback: forcing meeting UI after 8s timeout. States:', {
          localParticipantAllowedJoin,
          showJoinScreen,
          meetingState: mMeeting.meetingState,
        });
        setLocalParticipantAllowedJoin(true);
        setShowJoinScreen(false);
      }
    }, 8000);
    return () => clearTimeout(fallbackTimer);
  }, []); // Run once on mount

  // Only show presenter view if there's both a presenter AND an actual screen share stream
  const isPresenting = mMeeting.presenterId && mMeeting.hasActiveScreenShare;

  // ── Recording Metadata: track participant UIDs for multi-layout recording ──
  // Re-emits on every participants change so names are updated once resolved
  const prevParticipantIdsRef = useRef(new Set());
  const prevParticipantNamesRef = useRef(new Map());
  useEffect(() => {
    if (!mMeeting.participants) return;
    const currentIds = new Set();
    const participants = [...(mMeeting.participants.values?.() || [])];
    for (const p of participants) {
      if (p.id) currentIds.add(p.id);
      const name = p.displayName || 'Guest';
      const prevName = prevParticipantNamesRef.current.get(p.id);
      // Emit join for new participants OR re-emit when name changes from generic to real
      if (p.id && (!prevParticipantIdsRef.current.has(p.id) || (prevName !== name && !name.startsWith('Participant ')))) {
        meetingState.emitRecordingMetadataParticipantJoined?.(p.id, name);
      }
      prevParticipantNamesRef.current.set(p.id, name);
    }
    // Emit leave for departed participants
    for (const prevId of prevParticipantIdsRef.current) {
      if (!currentIds.has(prevId)) {
        meetingState.emitRecordingMetadataParticipantLeft?.(prevId);
        prevParticipantNamesRef.current.delete(prevId);
      }
    }
    prevParticipantIdsRef.current = currentIds;
  }, [mMeeting.participants]);

  // Track screen share start/stop for recording metadata
  const prevPresenterIdRef = useRef(null);
  useEffect(() => {
    const presenterId = mMeeting.presenterId;
    if (presenterId && !prevPresenterIdRef.current) {
      // Screen share started
      const presenter = mMeeting.participants?.get?.(presenterId);
      meetingState.emitRecordingMetadataScreenShareStarted?.(
        presenterId, presenterId, presenter?.displayName || 'Presenter'
      );
    } else if (!presenterId && prevPresenterIdRef.current) {
      // Screen share stopped
      meetingState.emitRecordingMetadataScreenShareStopped?.(
        prevPresenterIdRef.current, prevPresenterIdRef.current
      );
    }
    prevPresenterIdRef.current = presenterId || null;
  }, [mMeeting.presenterId]);

  // Default focus mode + hide self for webinar participants (RECV_ONLY viewers)
  const isWebinarParticipant = meetingSettings?.roomType === "Webinar" &&
    !["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
  const hasSetWebinarDefaultsRef = useRef(false);
  useEffect(() => {
    if (isWebinarParticipant && !hasSetWebinarDefaultsRef.current && !showJoinScreen) {
      hasSetWebinarDefaultsRef.current = true;
      // Don't apply the webinar-attendee 'focus' default to a recorder bot that
      // was launched with an explicit layout choice from the recording modal.
      if (!isRecorderLayoutPinned) {
        setSelectedLayout('focus');
      }
      // Hide self view for webinar participants — they are in RECV_ONLY mode
      if (typeof setHideSelfView === 'function') {
        setHideSelfView(true);
      }
    }
  }, [isWebinarParticipant, showJoinScreen, isRecorderLayoutPinned]);

  // Webinar: use stage-only participant grid (filters to SEND_AND_RECV/presenters only)
  const isWebinarSession = meetingSettings?.roomType === "Webinar";
  const WebinarGridComponent = isWebinarSession ? MemorizedWebinarParticipantView : MemorizedParticipantView;

  // Webinar attendees & recorder bot: receive layout changes from host in real-time.
  // EXCEPTION: if the recorder bot was launched with an explicit ?layout= URL
  // param, that param is the user's choice from the "Start recording this
  // meeting?" modal and must NOT be overwritten by host activity. Otherwise the
  // host's broadcast activeLayout (often 'focus' by default) clobbers the
  // selection a moment after the recorder applies it, and every recording ends
  // up rendered in the host's layout regardless of what was picked.
  useEffect(() => {
    if (!isWebinarSession) return;
    if (!isWebinarParticipant && !isRecorderMode) return;
    if (isRecorderLayoutPinned) {
      return; // Honor the modal choice; don't sync to host changes.
    }
    if (meetingState.activeLayout && meetingState.activeLayout !== selectedLayout) {
      console.log('[Webinar] Applying host layout:', meetingState.activeLayout);
      setSelectedLayout(meetingState.activeLayout);
    }
  }, [isWebinarParticipant, isRecorderMode, isWebinarSession, meetingState.activeLayout, isRecorderLayoutPinned]);

  // Determine if an active CTA uses horizontal display mode (affects meeting-stage-area flex direction)
  const ctaIsHorizontal = (() => {
    if (!activeElement || activeElement.type !== 'cta') return false;
    if (activeElement.displayMode === 'horizontal') return true;
    if (activeElement.settings) {
      try {
        const s = typeof activeElement.settings === 'string' ? JSON.parse(activeElement.settings) : activeElement.settings;
        return s.displayMode === 'horizontal';
      } catch (e) { return false; }
    }
    return false;
  })();

  // Recorder mode: always hide self view so the recorder's own (empty) tile
  // never shows up in any layout, and NEVER gets picked for focus/speaker views.
  useEffect(() => {
    if (isRecorderMode && typeof setHideSelfView === 'function') {
      setHideSelfView(true);
    }
  }, [isRecorderMode, setHideSelfView]);

  // Recorder mode: tell Agora's web_recorder_service the page is ready to
  // capture. Without this signal, the bot either captures from page-load
  // (with onhold:false) — recording the loading screen for 5+ seconds — or
  // sits idle until readyTimeout (with onhold:true). Two-trigger logic:
  //   1. PRIMARY: first non-system participant with video appears → page
  //      has actual content the recorder should capture
  //   2. SAFETY: 8s timeout from mount → covers all-audio meetings + cases
  //      where video subscription is delayed; better to start capturing
  //      with whatever's painted than to hang indefinitely
  // Once fired, the flag prevents re-fires; AGORA expects exactly one call.
  const notifyReadyFiredRef = useRef(false);
  useEffect(() => {
    if (!isRecorderMode || notifyReadyFiredRef.current) return;
    const SYSTEM_UID_MIN = 999000;
    const hasContent = participantsData.some((p) => {
      const idNum = Number(p?.id);
      const isSystem = Number.isFinite(idNum) && idNum >= SYSTEM_UID_MIN;
      return !isSystem && p?.webcamOn;
    });
    if (hasContent) {
      notifyReadyFiredRef.current = true;
      try {
        if (typeof window.navigator.notifyReady === 'function') {
          window.navigator.notifyReady();
          console.log('[MeetingContainerNew] notifyReady sent (first remote video)');
        } else {
          console.log('[MeetingContainerNew] notifyReady not available — bot likely on onhold:false');
        }
      } catch (e) {
        console.warn('[MeetingContainerNew] notifyReady threw:', e);
      }
    }
  }, [isRecorderMode, participantsData]);

  useEffect(() => {
    if (!isRecorderMode) return;
    const timer = setTimeout(() => {
      if (notifyReadyFiredRef.current) return;
      notifyReadyFiredRef.current = true;
      try {
        if (typeof window.navigator.notifyReady === 'function') {
          window.navigator.notifyReady();
          console.log('[MeetingContainerNew] notifyReady sent (safety 8s)');
        }
      } catch (e) {
        console.warn('[MeetingContainerNew] notifyReady safety threw:', e);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [isRecorderMode]);

  // Publisher HD bump for recording: when a cloud/grid/web recording is
  // active AND the local user is a host/co-host, bump the local video
  // encoder from default 720p to 1080p. The recorder then receives a
  // 1080p HIGH stream (AgoraContext already forces streamType=0 in
  // recorder mode), captured directly into the 1600x900 grid cell —
  // no upscaling. Goes back to 720p when recording stops.
  //
  // Skipped for:
  //   - Non-host/co-host users (they rarely publish; 1080p wastes bandwidth)
  //   - The recorder bot itself (it's RECV_ONLY, no local track to change)
  //   - Secondary grid cells (only the primary should issue encoder changes
  //     to avoid 4x setEncoderConfiguration calls per toggle)
  useEffect(() => {
    if (isSecondaryRecorderCell) return;
    if (isRecorderMode) return; // recv-only, no local track
    const isHostOrCoHost = ['moderator', 'host', 'co-host'].includes(meetingRole) || isCoHost?.(mMeeting.localParticipant?.id);
    if (!isHostOrCoHost) return;
    const track = mMeeting.localParticipant?.webcamStream?.agoraTrack;
    if (!track || typeof track.setEncoderConfiguration !== 'function') return;
    const recordingActive = !!meetingState?.recording?.isRecording;
    const target = recordingActive
      ? { width: 1920, height: 1080, frameRate: 30, bitrateMin: 1500, bitrateMax: 3000 }
      : { width: 1280, height: 720, frameRate: 30, bitrateMin: 800, bitrateMax: 2000 };
    track.setEncoderConfiguration(target).then(
      () => console.log('[HD-bump] host encoder set to ' + target.width + 'x' + target.height + ' (recording=' + recordingActive + ')'),
      (err) => console.warn('[HD-bump] setEncoderConfiguration failed:', err?.message),
    );
    // Deps: when recording toggles OR when the local track actually materializes
    // (webcam off→on), reapply. Debouncing isn't needed because recording state
    // toggles are infrequent (start/stop), not continuous like active-speaker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingState?.recording?.isRecording, meetingRole, mMeeting.localParticipant?.webcamStream?.agoraTrack, isSecondaryRecorderCell, isRecorderMode]);

  // Sync active speaker from Agora SDK to MeetingAppContext
  // In recorder mode, NEVER let the recorder itself become the active speaker
  useEffect(() => {
    // Grid-recorder: all 4 cells would otherwise set the SAME activeSpeakerId
    // into the shared MeetingAppContext. Gallery cell is enough — siblings
    // read from the same context and get the update.
    if (isSecondaryRecorderCell) return;
    if (mMeeting.activeSpeakerId !== undefined) {
      const speakerId = mMeeting.activeSpeakerId;
      if (isRecorderMode && speakerId === mMeeting.localParticipant?.id) {
        // Pick any other participant instead
        const others = Array.from(mMeeting.participants?.keys?.() || [])
          .filter(id => id !== mMeeting.localParticipant?.id);
        setActiveSpeakerId(others[0] || null);
      } else {
        setActiveSpeakerId(speakerId);
      }
    }
  }, [mMeeting.activeSpeakerId, setActiveSpeakerId, isRecorderMode, mMeeting.localParticipant?.id, isSecondaryRecorderCell]);

  // ==========================================================================
  // Recorder mode: capture per-participant audio via MediaRecorder.
  // Only the "gallery" layout recorder runs this (to avoid 4x duplicate audio).
  // Guarded heavily so this NEVER affects normal meeting users.
  // ==========================================================================
  const mMeetingRefForRecorder = useRef(null);
  useEffect(() => { mMeetingRefForRecorder.current = mMeeting; }, [mMeeting]);

  useEffect(() => {
    // Bail for everyone except the gallery recorder browser.
    if (!isRecorderMode || recorderLayout !== 'gallery') return;
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return;

    // STREAMING MODE: when the worker passes recordingId + workerBase in
    // the URL (Egress path, where we have no puppeteer.evaluate handle on
    // the in-container Chrome), each MediaRecorder chunk is uploaded
    // immediately to /participant-audio-chunk on the worker's HTTP
    // server. Worker stores chunks per participant on disk and concats
    // them at finalize time. Bypasses the pagehide-flush approach that
    // proved unreliable inside Egress's Chrome teardown sequence.
    //
    // LEGACY MODE: when those params aren't in the URL (in-house pool
    // browser path), chunks accumulate in memory and are flushed at
    // stop time via puppeteer's page.evaluate(__rlinkFlushParticipantAudio).
    // Same behavior as before.
    const _streamParams = new URLSearchParams(window.location.search);
    const streamRecordingId = _streamParams.get('recordingId');
    const streamWorkerBase = _streamParams.get('workerBase');
    const useStreaming = !!(streamRecordingId && streamWorkerBase);

    // The window fns and the recorders map live at module scope (see top
    // of file) so they survive this cell's unmount — critical for the
    // "host ends meeting, cell unmounts, worker tries to flush" race.
    const tryStartRecorder = (p) => {
      try {
        if (!p || !p.id) return;
        const m = mMeetingRefForRecorder.current;
        if (!m) return;
        if (p.id === m.localParticipant?.id) return;
        if (__rlinkRecorderRecorders.has(p.id)) return;
        const track = p.micStream && p.micStream.track;
        if (!track) return;

        const stream = new MediaStream([track]);
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
        const mr = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 96000 });
        const chunks = [];
        const participantName = p.displayName || `Participant ${p.id}`;
        let chunkIndex = 0;

        if (useStreaming) {
          // Stream each chunk to the worker as it arrives. Chunks are raw
          // bytes from one continuous MediaRecorder stream — only the
          // first one carries EBML+segment headers, the rest are cluster
          // data. The worker concatenates chunks back into a single WebM
          // file at finalize time (cat is enough; ffmpeg parses cleanly).
          mr.ondataavailable = async (e) => {
            if (!e.data || e.data.size === 0) return;
            const idx = chunkIndex++;
            try {
              const form = new FormData();
              form.append('chunk', e.data, p.id + '-' + idx + '.webm');
              form.append('participantId', p.id);
              form.append('participantName', participantName);
              form.append('chunkIndex', String(idx));
              const url = streamWorkerBase + '/participant-audio-chunk/' + streamRecordingId;
              const resp = await fetch(url, { method: 'POST', body: form });
              if (!resp.ok) {
                console.warn('[Recorder] audio chunk ' + idx + ' for ' + participantName + ' rejected: ' + resp.status);
              }
            } catch (err) {
              console.warn('[Recorder] audio chunk ' + idx + ' upload failed for ' + participantName + ':', err?.message || err);
            }
          };
        } else {
          // Legacy in-memory accumulation. Used by the in-house pool
          // browser path where puppeteer.evaluate triggers the flush.
          mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        }

        mr.onerror = (e) => console.warn('[Recorder] audio recorder error', p.id, e);
        // 5-second chunks: balances upload chattiness against the worst-
        // case data loss if the recorder is killed mid-chunk. (Was 2s
        // when we accumulated in memory; less critical there since flush
        // would catch everything.)
        mr.start(useStreaming ? 5000 : 2000);
        __rlinkRecorderRecorders.set(p.id, {
          recorder: mr,
          chunks,
          name: participantName,
          streaming: useStreaming,
        });
        console.log('[Recorder] audio capture started for ' + participantName + ' (' + (useStreaming ? 'streaming' : 'in-memory') + ')');
      } catch (err) {
        console.warn('[Recorder] tryStartRecorder error', err?.message || err);
      }
    };

    const pollInterval = setInterval(() => {
      try {
        const m = mMeetingRefForRecorder.current;
        const parts = m && m.participants;
        if (!parts) return;
        if (typeof parts.forEach === 'function') {
          parts.forEach(p => tryStartRecorder(p));
        } else if (typeof parts[Symbol.iterator] === 'function') {
          for (const entry of parts) {
            const p = Array.isArray(entry) ? entry[1] : entry;
            tryStartRecorder(p);
          }
        }
      } catch (err) {
        console.warn('[Recorder] poll loop error', err?.message || err);
      }
    }, 2000);

    return () => {
      // Stop the polling loop but DO NOT stop recorders or delete window
      // fns — the recorders keep capturing in the background and flush
      // still works from module scope even after this cell unmounts.
      // The worker's final flush call is what stops each MediaRecorder
      // and uploads its blobs.
      try { clearInterval(pollInterval); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecorderMode, recorderLayout]);

  // ==========================================================================
  // Recorder mode: per-participant VIDEO MediaRecorders.
  // Mirror of the audio path above but for video tracks. Captures each
  // remote participant's video MediaStreamTrack DIRECTLY from Agora,
  // bypassing Chrome's Xvfb compositor. Chunks stream live to the worker
  // (5s segments) so memory stays bounded for long meetings.
  //
  // Gated on URL param `?perTrack=1` so this is a no-op for normal
  // recordings. The backend only sets perTrackCapture=true (which the
  // worker translates to ?perTrack=1) for users on PER_TRACK_RECORDING_EMAILS.
  //
  // PHASE 1 SCOPE: capture and store. The existing Xvfb pipeline still
  // produces today's gallery_view/focus/etc MP4s — those are what the
  // user sees. Per-track WebMs are additive, used by Phase 2's compose
  // pipeline (separate work).
  // ==========================================================================
  useEffect(() => {
    if (!isRecorderMode || recorderLayout !== 'gallery') return;
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return;
    const perTrackEnabled = new URLSearchParams(window.location.search).get('perTrack') === '1';
    if (!perTrackEnabled) return;

    const workerBase = new URLSearchParams(window.location.search).get('workerBase')
      || `http://127.0.0.1:3053`;
    const recordingId = new URLSearchParams(window.location.search).get('recordingId') || '';

    const tryStartVideoRecorder = (p) => {
      try {
        if (!p || !p.id) return;
        const m = mMeetingRefForRecorder.current;
        if (!m) return;
        if (p.id === m.localParticipant?.id) return;
        if (__rlinkRecorderVideoRecorders.has(p.id)) return;
        const track = p.webcamStream && p.webcamStream.track;
        if (!track || track.readyState !== 'live') return;
        // Allocate the next segment index for this participant. First
        // recorder is segment 0; each subsequent track replacement gets
        // segment 1, 2, ... The compose pipeline concatenates segments
        // in order so the final per-participant video is continuous.
        const segmentIndex = __rlinkRecorderVideoSegments.get(p.id) || 0;
        __rlinkRecorderVideoSegments.set(p.id, segmentIndex + 1);

        const stream = new MediaStream([track]);
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
          ? 'video/webm;codecs=vp8'
          : 'video/webm';
        // 2.5 Mbps target — matches Agora's HIGH stream rate so we don't
        // re-compress aggressively. Chrome will adapt downward if the
        // source track is sending less.
        const mr = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
        let chunkIndex = 0;
        const rec = {
          recorder: mr,
          track,
          segmentIndex,
          name: p.displayName || ('Participant ' + p.id),
          chunkCount: 0,
        };
        mr.ondataavailable = async (e) => {
          if (!e.data || e.data.size === 0) return;
          const idx = chunkIndex++;
          rec.chunkCount = idx + 1;
          // Stream chunks live to the worker. WebM chunks from ONE
          // MediaRecorder session concatenate cleanly (first chunk has
          // EBML/segment headers; subsequent chunks are cluster data).
          // segmentIndex differentiates ACROSS MediaRecorder sessions
          // (one per track replacement) — worker concats them in order
          // at compose time.
          try {
            const form = new FormData();
            form.append('chunk', e.data, p.id + '-s' + segmentIndex + '-' + idx + '.webm');
            form.append('participantId', p.id);
            form.append('participantName', rec.name);
            form.append('chunkIndex', String(idx));
            form.append('segmentIndex', String(segmentIndex));
            await fetch(workerBase + '/participant-video-chunk/' + recordingId, {
              method: 'POST',
              body: form,
            });
          } catch (err) {
            console.warn('[Recorder] video chunk ' + idx + ' (seg ' + segmentIndex + ') upload failed for ' + rec.name + ':', err.message);
          }
        };
        mr.onerror = (e) => console.warn('[Recorder] video recorder error', p.id, e);
        mr.start(5000);  // 5-second chunks
        __rlinkRecorderVideoRecorders.set(p.id, rec);
        console.log('[Recorder] video segment ' + segmentIndex + ' started for ' + rec.name + ' (track ' + track.id + ')');

        // Track-end handler. Fires when:
        //   - Agora replaces the underlying MediaStreamTrack (e.g. on
        //     setRemoteVideoStreamType switch to HIGH, or QoE downgrade)
        //   - Participant turns video off
        //   - Network drops the subscriber
        // Removes this recorder from the map so the polling loop spawns
        // a fresh MediaRecorder for the NEW track at segmentIndex+1.
        const onTrackEnded = () => {
          console.log('[Recorder] track ended for ' + rec.name + ' segment ' + segmentIndex + ' — flushing and respawning');
          try {
            if (mr.state === 'recording') {
              mr.requestData();
              setTimeout(() => { try { mr.stop(); } catch (_) {} }, 200);
            }
          } catch (_) {}
          // Remove ASAP so the polling loop sees the gap and respawns.
          // Defer the actual delete by 500ms to let the final ondataavailable
          // chunk fire, otherwise the last 5s of this segment is lost.
          setTimeout(() => {
            __rlinkRecorderVideoRecorders.delete(p.id);
          }, 500);
        };
        try { track.addEventListener('ended', onTrackEnded); } catch (_) {}
      } catch (err) {
        console.warn('[Recorder] tryStartVideoRecorder error', err?.message || err);
      }
    };

    const pollInterval = setInterval(() => {
      try {
        const m = mMeetingRefForRecorder.current;
        const parts = m && m.participants;
        if (!parts) return;
        const iter = (typeof parts.forEach === 'function')
          ? (cb) => parts.forEach(cb)
          : (cb) => { for (const e of parts) cb(Array.isArray(e) ? e[1] : e); };
        iter((p) => tryStartVideoRecorder(p));
      } catch (err) {
        console.warn('[Recorder] video poll loop error', err?.message || err);
      }
    }, 2000);

    return () => {
      try { clearInterval(pollInterval); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecorderMode, recorderLayout]);

  // Bridge MeetingStateContext socket-based recording state into AgoraContext
  // so ALL participants see the correct indicator instantly (instead of waiting for 10s HTTP poll)
  useEffect(() => {
    if (!meetingState?.recording) return;
    const socketSaysRecording = meetingState.recording.isRecording;
    const currentState = mMeeting.recordingState;
    if (socketSaysRecording && currentState !== Constants.recordingEvents.RECORDING_STARTED && currentState !== Constants.recordingEvents.RECORDING_STARTING) {
      mMeeting.setRecordingState(Constants.recordingEvents.RECORDING_STARTED);
      mMeeting.setIsRecording?.(true);
    } else if (!socketSaysRecording && currentState !== Constants.recordingEvents.RECORDING_STOPPED) {
      mMeeting.setRecordingState(Constants.recordingEvents.RECORDING_STOPPED);
      mMeeting.setIsRecording?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingState?.recording]);

  // When host ends meeting for all, force every client to leave the Agora channel.
  // The backend broadcasts 'meeting-state:ended' via Socket.IO, MeetingStateContext
  // sets isMeetingEnded=true, and this effect tears down the Agora RTC connection.
  useEffect(() => {
    if (meetingState?.isMeetingEnded) {
      // If this host triggered "End for All Except Host", ignore the broadcast
      if (isEndExceptHostRef.current) {
        console.log('[MeetingContainer] Ignoring meeting-state:ended (host stays)');
        isEndExceptHostRef.current = false;
        return;
      }
      console.log('[MeetingContainer] Meeting ended by host — forcing Agora disconnect');
      try {
        mMeeting?.leave?.();
      } catch (err) {
        console.warn('[MeetingContainer] Error during forced leave:', err);
      }
      leaveOrRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingState?.isMeetingEnded]);

  // Selective kick: host kept co-hosts/presenters but kicked everyone else
  useEffect(() => {
    // Recorder bot can't be "kicked" in any meaningful sense — it's the
    // worker's Chrome and leaves when the worker tells it to. Skip to
    // avoid 4× window.addEventListener registrations in grid mode.
    if (isRecorderMode) return;
    const handleSelectiveKick = () => {
      // Check role — hosts, co-hosts stay
      const isHostOrCoHost = ['moderator', 'host', 'co-host'].includes(meetingRole);

      // Check if local participant is a presenter
      const localId = mMeeting?.localParticipant?.id;
      const localNumId = localId ? stringToNumericUid(localId) : null;
      const isLocalPresenter = localId && (
        presenters?.has(localId) ||
        presenters?.has(String(localId)) ||
        (localNumId != null && presenters?.has(localNumId))
      );

      const shouldStay = isHostOrCoHost || isLocalPresenter;

      if (!shouldStay) {
        try {
          mMeeting?.leave?.();
        } catch (err) {
          console.warn('[MeetingContainer] Error during selective kick leave:', err);
        }
        leaveOrRedirect();
      }
    };
    window.addEventListener('rlink-selective-kick', handleSelectiveKick);
    return () => window.removeEventListener('rlink-selective-kick', handleSelectiveKick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mMeeting, meetingRole, presenters]);

  // When this user joins from another device, force-disconnect this session cleanly.
  // The ref prevents onMeetingLeft from ending the meeting for all other participants.
  useEffect(() => {
    if (meetingState?.isForceDisconnected) {
      console.log('[MeetingContainer] Force-disconnected — joined from another device');
      isForceDisconnectedRef.current = true;
      try {
        mMeeting?.leave?.();
      } catch (err) {
        console.warn('[MeetingContainer] Error during force-disconnect leave:', err);
      }
      setIsMeetingLeft(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingState?.isForceDisconnected]);

  // ============================================
  // WAITING ROOM - Host connection for managing waiting participants
  // ============================================
  const isHost = useMemo(() =>
    ["moderator", "co-host", "host"].includes(meetingRole),
    [meetingRole]
  );

  // Build activeElements map for StudioLeftSidebar to show active state on element items
  const sidebarActiveElements = useMemo(() => {
    const map = {};
    if (activeElement) {
      const elId = activeElement._id || activeElement.id;
      if (elId) map[activeElement.type] = { id: elId };
    }
    if (activePoll) {
      const pollId = activePoll._id || activePoll.id;
      if (pollId) map['poll'] = { id: pollId };
    }
    if (activeTimer) {
      const timerId = activeTimer._id || activeTimer.id;
      if (timerId) map['timer'] = { id: timerId };
    }
    if (activePresentation) {
      const presId = activePresentation._id || activePresentation.id;
      if (presId) map['presentation'] = { id: presId };
    }
    if (prompterElement) {
      const prompterId = prompterElement._id || prompterElement.id;
      if (prompterId) map['prompter'] = { id: prompterId };
    }
    if (activeLeadCapture) {
      const lcId = activeLeadCapture._id || activeLeadCapture.id;
      if (lcId) map['leadcapture'] = { id: lcId };
      if (lcId) map['lead_capture'] = { id: lcId };
    }
    if (activeContractView) {
      const contractId = activeContractView._id || activeContractView.id;
      if (contractId) map['contract'] = { id: contractId };
    }
    return map;
  }, [activeElement, activePoll, activeTimer, activePresentation, prompterElement, activeLeadCapture, activeContractView]);

  // Callback for StudioLeftSidebar when host deactivates an element via toggle
  const handleSidebarDeactivateElement = useCallback((element) => {
    const elType = element.type;
    if (elType === 'poll') {
      setShowPollModal(false);
      setActivePoll(null);
      setIsPollLauncher(false);
    } else if (elType === 'timer') {
      setActiveTimer(null);
      setTimerState({ status: 'idle' });
      setIsTimerLauncher(false);
    } else if (elType === 'presentation') {
      setActivePresentation(null);
      setIsPresentationLauncher(false);
      setPresentationState({ currentSlide: 1, status: 'idle' });
    } else if (elType === 'prompter' || elType === 'teleprompter') {
      setPrompterElement(null);
    } else if (elType === 'leadcapture' || elType === 'lead_capture') {
      setShowLeadCaptureModal(false);
      setActiveLeadCapture(null);
      setIsLeadCaptureLauncher(false);
    } else if (elType === 'contract') {
      setActiveContractView(null);
      setActiveElement(null);
    } else {
      // Generic elements (video, audio, website, weboverlay, cta, document, talking_point, etc.)
      setActiveElement(null);
    }
  }, [setPrompterElement, setActiveElement]);

  const waitingRoomEnabled = useMemo(() => {
    const isWebinar = meetingSettings?.roomType === 'Webinar';
    // For webinars, waiting room is enabled by default (unless explicitly disabled)
    if (isWebinar) {
      return meetingSettings?.waitingRoom !== false && meetingSettings?.hostApprovalRequired !== false;
    }
    return meetingSettings?.hostApprovalRequired || meetingSettings?.waitingRoom;
  }, [meetingSettings]);

  // Connect to waiting room socket as host
  useEffect(() => {
    // Recorder bot never admits participants — skip the socket entirely.
    // In grid mode this would otherwise open 4 parallel socket.IO connections
    // (one per cell) and fire every waiting-room event 4 times.
    if (isRecorderMode) return;
    // Only hosts connect to manage waiting room
    console.log('[WaitingRoom-Host] useEffect check:', { isHost, waitingRoomEnabled, roomId: meetingSettings?.roomId, roomType: meetingSettings?.roomType, waitingRoom: meetingSettings?.waitingRoom, hostApprovalRequired: meetingSettings?.hostApprovalRequired });
    // The host always opens the waiting-room socket — even when the meeting's
    // waitingRoom toggle is off. The backend uses this socket as the signal
    // that a host/cohost is present, so it can release guests held by the
    // "no host yet" security gate. Without this connection the gate would
    // strand guests in the waiting room indefinitely on rooms where waiting
    // room is otherwise disabled.
    if (!isHost || !meetingSettings?.roomId) {
      console.log('[WaitingRoom-Host] Skipping socket connection:', { isHost, hasRoomId: !!meetingSettings?.roomId });
      return;
    }

    const roomId = meetingSettings.roomId;
    const baseUrl = process.env.REACT_APP_BASE_URL?.replace('/api', '') || '';
    console.log('[WaitingRoom-Host] Creating socket connection for room:', roomId, 'baseUrl:', baseUrl);
    const socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    waitingRoomSocketRef.current = socket;

    socket.on('connect', () => {
      console.log('[WaitingRoom-Host] Connected, socket id:', socket.id);
      // Join as host to receive waiting room updates
      socket.emit('host-join', {
        meetingId: roomId,
        hostId: mMeeting?.localParticipant?.id || 'host'
      });
    });

    const playWaitingChime = () => {
      const now = Date.now();
      if (now - waitingChimeLastRef.current < 10000) return;
      // Honor host's per-browser mute preference (toggled in WaitingRoomSection).
      try {
        if (localStorage.getItem('rlink_waiting_room_chime_muted') === '1') return;
      } catch (_) {}
      waitingChimeLastRef.current = now;
      try {
        const audio = new Audio('/audio/chime.wav');
        audio.volume = 0.6;
        audio.play().catch(() => {});
      } catch (_) {}
    };

    // Single shared toast id — keeps the waiting-room alert as one
    // updating toast in the bottom-right corner, never a stack.
    const WAITING_TOAST_ID = 'waiting-room-alert';

    // Cross-room: backend tells us guests are waiting in ANOTHER room we host
    socket.on('cross-room-waiting', (data) => {
      const { sourceMeetingId, sourceMeetingName, waitingList } = data || {};
      if (!sourceMeetingId) return;
      console.log('[WaitingRoom-Host] cross-room queue update:', sourceMeetingId, waitingList?.length);
      setCrossRoomQueues((prev) => {
        const others = prev.filter((q) => q.meetingId !== sourceMeetingId);
        if (!waitingList || waitingList.length === 0) return others;
        return [...others, {
          meetingId: sourceMeetingId,
          meetingName: sourceMeetingName || 'another room',
          waitingList,
        }];
      });
    });

    socket.on('waiting-room-update', (data) => {
      console.log('[WaitingRoom-Host] Update received:', data);
      const newList = data.waitingList || [];

      // Detect new arrivals and track join times
      const now = Date.now();
      let hasNewArrival = false;
      newList.forEach((p) => {
        const pid = p.odId || p.id;
        if (!waitingJoinTimesRef.current[pid]) {
          waitingJoinTimesRef.current[pid] = now;
          hasNewArrival = true;
        }
      });
      // Clean up departed participants
      const newIds = new Set(newList.map((p) => p.odId || p.id));
      Object.keys(waitingJoinTimesRef.current).forEach((pid) => {
        if (!newIds.has(pid)) delete waitingJoinTimesRef.current[pid];
      });

      if (hasNewArrival) playWaitingChime();

      setWaitingParticipants(newList);

      // Open waiting room panel when there are people waiting
      if (data.type === 'participant-joined' && data.participant) {
        setShowWaitingRoomPanel(true);
        // Auto-admit registered users if setting is enabled — no toast needed,
        // they pass straight through.
        if (data.participant.isRegistered && meetingSettingsRef.current?.autoAdmitRegistered) {
          const rid = meetingSettingsRef.current.roomId;
          if (waitingRoomSocketRef.current && rid) {
            waitingRoomSocketRef.current.emit('admit-participant', { meetingId: rid, odId: data.participant.odId });
          }
        }
      }
      // Also auto-open when host joins and there are already people waiting
      if (data.type === 'initial-list' && data.waitingList?.length > 0) {
        setShowWaitingRoomPanel(true);
      }

      // Single bottom-right toast that reflects current waiting-room state.
      // Updates in place on each event so the host never sees a stack.
      const count = newList.length;
      if (count === 0) {
        toast.dismiss(WAITING_TOAST_ID);
      } else {
        const latest = data.participant?.odName
          || newList[newList.length - 1]?.odName
          || 'Someone';
        const msg = count === 1
          ? `${latest} is waiting to join`
          : `${latest} + ${count - 1} more waiting to join`;
        const opts = {
          toastId: WAITING_TOAST_ID,
          position: 'bottom-left',
          autoClose: 5000,
          closeOnClick: false,
          draggable: false,
          onClick: () => setShowWaitingRoomPanel(true),
        };
        if (toast.isActive(WAITING_TOAST_ID)) {
          toast.update(WAITING_TOAST_ID, { render: msg, ...opts });
        } else {
          toast.info(msg, opts);
        }
      }
    });

    // Reminder chime if someone has been waiting >20s
    const chimeInterval = setInterval(() => {
      const now = Date.now();
      const hasLongWaiter = waitingParticipantsRef.current.some((p) => {
        const pid = p.odId || p.id;
        const joinedAt = waitingJoinTimesRef.current[pid];
        return joinedAt && now - joinedAt >= 20000;
      });
      if (hasLongWaiter) playWaitingChime();
    }, 5000);

    // Listen for chat messages from waiting participants
    socket.on('waiting-room-chat-from-participant', (data) => {
      console.log('[WaitingRoom-Host] Chat from participant:', data);
      setWaitingRoomChatMessages((prev) => {
        const odId = data.senderId;
        const existing = prev[odId] || [];
        if (existing.some((m) => m.id === data.id)) return prev;
        return { ...prev, [odId]: [...existing, data] };
      });
    });

    // Echo of host's own messages
    socket.on('waiting-room-chat-from-host', (data) => {
      const { targetOdId, ...msg } = data;
      setWaitingRoomChatMessages((prev) => {
        const existing = prev[targetOdId] || [];
        if (existing.some((m) => m.id === msg.id)) return prev;
        return { ...prev, [targetOdId]: [...existing, msg] };
      });
    });

    socket.on('disconnect', (reason) => {
      console.log('[WaitingRoom-Host] Disconnected, reason:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('[WaitingRoom-Host] Connection error:', error.message);
    });

    return () => {
      clearInterval(chimeInterval);
      if (socket) {
        socket.disconnect();
      }
    };
    // Don't include mMeeting.localParticipant.id - it changes frequently and causes reconnections
  }, [isHost, waitingRoomEnabled, meetingSettings?.roomId]);

  // Admit participant function
  const admitParticipant = useCallback((odId) => {
    if (waitingRoomSocketRef.current && meetingSettings?.roomId) {
      waitingRoomSocketRef.current.emit('admit-participant', {
        meetingId: meetingSettings.roomId,
        odId
      });
    }
  }, [meetingSettings?.roomId]);

  // Admit all participants function
  const admitAllParticipants = useCallback(() => {
    if (waitingRoomSocketRef.current && meetingSettings?.roomId) {
      waitingRoomSocketRef.current.emit('admit-all-participants', {
        meetingId: meetingSettings.roomId
      });
      setWaitingParticipants([]);
      setShowWaitingRoomPanel(false);
    }
  }, [meetingSettings?.roomId]);

  // Deny participant function
  const denyParticipant = useCallback((odId) => {
    if (waitingRoomSocketRef.current && meetingSettings?.roomId) {
      waitingRoomSocketRef.current.emit('deny-participant', {
        meetingId: meetingSettings.roomId,
        odId,
        reason: 'The host has denied your request to join.'
      });
    }
  }, [meetingSettings?.roomId]);

  // Send message to a specific waiting room participant
  const sendMessageToWaiting = useCallback((odId, message) => {
    if (waitingRoomSocketRef.current && meetingSettings?.roomId) {
      waitingRoomSocketRef.current.emit('send-message-to-waiting', {
        meetingId: meetingSettings.roomId,
        odId,
        message,
        senderName: mMeeting?.localParticipant?.displayName || 'Host',
      });
    }
  }, [meetingSettings?.roomId, mMeeting?.localParticipant?.displayName]);

  // Helper function to convert image URL to base64 (to avoid CORS issues)
  const imageToBase64 = (url) => {
    return new Promise((resolve, reject) => {
      // If already a data URL, return as-is
      if (url.startsWith('data:')) {
        resolve(url);
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(dataUrl);
        } catch (e) {
          // If canvas is tainted due to CORS, reject
          reject(new Error('CORS error converting image'));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Add timestamp to bypass cache and try with CORS
      img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
    });
  };

  // Persist the user's virtual background choice for this meeting (best-effort, non-blocking)
  const persistVbPreference = (pref) => {
    try {
      const roomId = meetingSettingsRef.current?.roomId || meetingSettings?.roomId;
      if (!roomId) return;
      saveVirtualBackgroundPreference(roomId, pref).catch((e) =>
        console.warn("Failed to save virtual background preference:", e?.message || e)
      );
    } catch (e) {
      console.warn("persistVbPreference error:", e);
    }
  };

  // Handle virtual background changes - using Agora Virtual Background Extension
  // Accepts: null (remove), { type: 'blur', blurStrength } or { type: 'image', imageUrl }
  const handleStartVirtualBackground = async (backgroundConfig, opts = {}) => {
    if (!opts.fromAutoApply) {
      vbUserInteractedRef.current = true;
    }
    // Every call bumps the generation; slow async work (image load) bails out
    // if a newer call has superseded it.
    const myGen = ++vbApplyGenRef.current;
    const isCurrent = () => vbApplyGenRef.current === myGen;

    // Serialize against any prior in-flight VB mutation. Agora's VB extension
    // is not concurrency-safe — overlapping disable/enable cycles leave the
    // publisher track in a bad state and the camera goes black.
    const prev = vbInflightRef.current;
    let resolveSelf;
    vbInflightRef.current = new Promise((r) => { resolveSelf = r; });
    try { await prev; } catch (_) { /* ignore prior errors */ }
    // If a newer call has superseded us while we were queued, bail out without
    // touching the shared processor.
    if (!isCurrent()) { resolveSelf(); return; }

    console.log("handleStartVirtualBackground called with:", backgroundConfig);
    const localVideoTrack = mMeeting?.localVideoTrack;

    if (!localVideoTrack) {
      console.error("No local video track available for virtual background");
      if (!opts.fromAutoApply) {
        toast.error("Camera must be enabled to apply virtual background");
      }
      resolveSelf();
      return;
    }

    try {
      // Initialize processor if needed
      if (!vbProcessorRef.current) {
        console.log("Creating Agora VB processor...");
        vbProcessorRef.current = vbExtension.createProcessor();
        vbProcessorRef.current.onoverload = () => {
          console.warn("Agora VB processor overloaded - consider reducing camera resolution");
        };
        await vbProcessorRef.current.init();
        console.log("Agora VB processor initialized");
      }

      // Pipe processor into the video track pipeline (only once per session)
      if (!vbPipedRef.current) {
        localVideoTrack.pipe(vbProcessorRef.current).pipe(localVideoTrack.processorDestination);
        vbPipedRef.current = true;
        console.log("Agora VB processor piped into video track");
      }

      const processor = vbProcessorRef.current;

      // Handle removing virtual background
      if (!backgroundConfig) {
        console.log("Disabling virtual background...");
        await processor.disable();
        if (!isCurrent()) return;
        setVirtualBackgroundOn(false);
        setSelectedBackground(null);
        setBlurEnabled(false);
        setProcessedWebcamStream(null);
        persistVbPreference({ type: 'none' });
        return;
      }

      // Apply based on background type
      if (backgroundConfig.type === 'blur') {
        const strength = backgroundConfig.blurStrength || 50;
        const blurDegree = strength > 66 ? 3 : strength > 33 ? 2 : 1;
        // Disable→setOptions→enable: Agora's VB extension does not reliably
        // pick up new options on an already-enabled processor.
        try { await processor.disable(); } catch {}
        if (!isCurrent()) return;
        processor.setOptions({ type: 'blur', blurDegree });
        setBlurEnabled(true);
        setSelectedBackground(null);
      } else if (backgroundConfig.type === 'image' || typeof backgroundConfig === 'string') {
        const imageUrl = backgroundConfig.imageUrl || backgroundConfig;
        // Load image into an HTMLImageElement (required by Agora extension)
        const img = new Image();
        // Only set crossOrigin for external URLs, not for data URLs or blob URLs
        if (!imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
          img.crossOrigin = 'anonymous';
        }
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load background image'));
          img.src = imageUrl;
        });
        // If the user triggered a newer VB action while this image was loading,
        // abort this stale call instead of overwriting their newer choice.
        if (!isCurrent()) return;

        // Resize image to reduce per-frame GPU processing (improves segmentation stability)
        const MAX_W = 640, MAX_H = 360;
        let finalImg = img;
        if (img.naturalWidth > MAX_W || img.naturalHeight > MAX_H) {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = MAX_W;
            canvas.height = MAX_H;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, MAX_W, MAX_H);
            const resized = new Image();
            await new Promise((resolve, reject) => {
              resized.onload = resolve;
              resized.onerror = reject;
              resized.src = canvas.toDataURL('image/jpeg', 0.85);
            });
            finalImg = resized;
            console.log(`Background image resized from ${img.naturalWidth}x${img.naturalHeight} to ${MAX_W}x${MAX_H}`);
          } catch (resizeErr) {
            console.warn("Could not resize background image, using original:", resizeErr);
          }
        }

        // Tight disable→setOptions→enable cycle: this is the only reliable
        // way to swap the background image in Agora's VB extension.
        try { await processor.disable(); } catch {}
        if (!isCurrent()) return;
        processor.setOptions({ type: 'img', source: finalImg, fit: 'cover' });
        setBlurEnabled(false);
        setSelectedBackground(imageUrl);
      } else {
        console.error("Invalid background config type");
        return;
      }

      // A superseded call must not re-enable a processor the newer call just
      // disabled — that's how the camera ends up black after rapid toggles.
      if (!isCurrent()) return;
      await processor.enable();
      setVirtualBackgroundOn(true);

      // Persist the applied preference
      if (backgroundConfig.type === 'blur') {
        persistVbPreference({ type: 'blur', blurStrength: backgroundConfig.blurStrength || 50 });
      } else if (backgroundConfig.type === 'image' || typeof backgroundConfig === 'string') {
        const imageUrl = backgroundConfig.imageUrl || backgroundConfig;
        // Only persist stable http(s) URLs — skip data:/blob: URLs which won't survive reloads
        if (typeof imageUrl === 'string' && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
          persistVbPreference({ type: 'image', imageUrl });
        }
      }

      // Get processed track for preview
      try {
        const processedTrack = await processor.getProcessedTrack();
        if (processedTrack && isCurrent()) {
          setProcessedWebcamStream(new MediaStream([processedTrack]));
        }
      } catch (previewErr) {
        console.log("Could not get processed track for preview:", previewErr);
      }

      console.log("Virtual background applied successfully via Agora extension");
    } catch (error) {
      console.error("Error applying virtual background:", error);
      toast.error("Failed to apply virtual background");

      // Try to recover
      try {
        if (vbProcessorRef.current) {
          await vbProcessorRef.current.disable();
        }
        setVirtualBackgroundOn(false);
        setProcessedWebcamStream(null);
      } catch (recoveryErr) {
        console.error("Recovery failed:", recoveryErr);
      }
    } finally {
      resolveSelf();
    }
  };

  // Re-apply blur when blur strength changes (only if blur is enabled).
  // Routes through handleStartVirtualBackground so the strength change uses
  // the serialized disable→setOptions→enable cycle (Agora's VB extension does
  // not reliably pick up new options on an already-enabled processor) and
  // can't collide with an in-flight toggle.
  useEffect(() => {
    if (virtualBackgroundOn && blurEnabled && vbProcessorRef.current) {
      handleStartVirtualBackground({ type: 'blur', blurStrength });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blurStrength]);

  // Auto-apply saved virtual background preference when joining this meeting.
  // Runs once per (roomId + localVideoTrack available) — persists user's last choice.
  // Shows a loader overlay (vbApplyPending=true) while the background is being
  // applied so the user doesn't see raw camera for a moment before VB kicks in.
  const vbPrefAppliedRef = useRef(false);
  const [vbApplyPending, setVbApplyPending] = useState(false);
  useEffect(() => {
    const roomId = meetingSettings?.roomId;
    const localVideoTrack = mMeeting?.localVideoTrack;
    if (!roomId || !localVideoTrack || vbPrefAppliedRef.current) return;

    vbPrefAppliedRef.current = true;
    let cancelled = false;
    setVbApplyPending(true);

    // Hard safety cap so the loader never stays stuck forever if something
    // goes wrong with the preference fetch or processor start.
    const safetyTimer = setTimeout(() => setVbApplyPending(false), 8000);

    (async () => {
      try {
        const res = await getVirtualBackgroundPreference(roomId);
        const pref = res?.data?.data ?? res?.data ?? null;
        if (cancelled) return;
        if (!pref || !pref.type || pref.type === 'none') {
          setVbApplyPending(false);
          return;
        }

        // Let the camera pipeline warm up before piping the processor — too
        // early produces poor segmentation on the first frames.
        await new Promise((r) => setTimeout(r, 800));
        if (cancelled || vbUserInteractedRef.current) {
          setVbApplyPending(false);
          return;
        }

        if (pref.type === 'blur') {
          if (typeof pref.blurStrength === 'number') {
            setBlurStrength?.(pref.blurStrength);
          }
          await handleStartVirtualBackground(
            { type: 'blur', blurStrength: pref.blurStrength || 50 },
            { fromAutoApply: true }
          );
        } else if (pref.type === 'image' && pref.imageUrl) {
          await handleStartVirtualBackground(
            { type: 'image', imageUrl: pref.imageUrl },
            { fromAutoApply: true }
          );
        }
      } catch (err) {
        console.warn("Failed to load saved virtual background preference:", err?.message || err);
      } finally {
        if (!cancelled) setVbApplyPending(false);
        clearTimeout(safetyTimer);
      }
    })();

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, [meetingSettings?.roomId, mMeeting?.localVideoTrack]);

  // Re-pipe the VB processor when the local video track changes.
  // When the user disables + re-enables the camera, Agora creates a new
  // video track. The VB processor was piped into the OLD track, so the
  // new track has no processor and the background is lost. Detect the
  // new track and re-pipe the existing processor into it.
  const lastPipedTrackRef = useRef(null);
  useEffect(() => {
    const track = mMeeting?.localVideoTrack;
    if (!track) {
      lastPipedTrackRef.current = null;
      return;
    }
    const processor = vbProcessorRef.current;
    const vbWasActive = virtualBackgroundOn || blurEnabled;
    if (!processor || !vbWasActive) return;
    // If this is the same track we already piped, skip.
    if (lastPipedTrackRef.current === track) return;

    try {
      // Unpipe any stale pipeline on the new track, then re-pipe the processor
      try { track.unpipe?.(); } catch (_) { /* ignore */ }
      track.pipe(processor).pipe(track.processorDestination);
      lastPipedTrackRef.current = track;
      vbPipedRef.current = true;
      console.log('[Whiteboard/VB] Re-piped VB processor into new local video track');
    } catch (err) {
      console.error('[Whiteboard/VB] Failed to re-pipe VB processor:', err);
    }
  }, [mMeeting?.localVideoTrack, virtualBackgroundOn, blurEnabled]);

  // Release the VB processor on component unmount so the camera can be
  // fully closed by the Agora SDK (otherwise the browser camera indicator
  // can linger after leaving the meeting).
  useEffect(() => {
    return () => {
      (async () => {
        try {
          const track = mMeetingRef.current?.localVideoTrack;
          if (vbProcessorRef.current) {
            try { await vbProcessorRef.current.disable(); } catch {}
            try { vbProcessorRef.current.release?.(); } catch {}
          }
          if (track && vbPipedRef.current) {
            try { track.unpipe?.(); } catch {}
          }
        } finally {
          vbProcessorRef.current = null;
          vbPipedRef.current = false;
        }
      })();
    };
  }, []);

  // Handle green screen (chroma key) processing
  // config: { enabled, keyColor, similarity, smoothness, backgroundImageUrl }
  const handleStartGreenScreen = async (config) => {
    console.log("handleStartGreenScreen called with:", config);

    try {
      // If disabling green screen
      if (!config || !config.enabled) {
        console.log("Disabling green screen...");

        // Stop chroma key processor if running
        if (chromaKeyProcessorRef.current) {
          await chromaKeyProcessorRef.current.stop();
        }

        // Also disable Agora virtual background processor if it was being used
        try {
          if (vbProcessorRef.current) await vbProcessorRef.current.disable();
        } catch (e) {
          // Ignore
        }

        setGreenScreenEnabled(false);
        setVirtualBackgroundOn(false);
        setProcessedWebcamStream(null);
        return;
      }

      // Enable green screen
      console.log("Enabling green screen with config:", config);

      // Disable Agora virtual background processor first
      try {
        if (vbProcessorRef.current) await vbProcessorRef.current.disable();
      } catch (e) {
        // Ignore
      }

      // Initialize chroma key processor if needed
      if (!chromaKeyProcessorRef.current) {
        chromaKeyProcessorRef.current = new ChromaKeyProcessor();
        await chromaKeyProcessorRef.current.init();
      } else if (chromaKeyProcessorRef.current.processorRunning) {
        await chromaKeyProcessorRef.current.stop();
      }

      // Update green screen config state
      setGreenScreenConfig({
        keyColor: config.keyColor || '#00ff00',
        similarity: config.similarity || 40,
        smoothness: config.smoothness || 10,
        backgroundImageUrl: config.backgroundImageUrl || null,
      });

      // Get camera stream with HD resolution
      const stream = await createCameraVideoTrack(hdCameraTrackConfig);

      // Start chroma key processing
      const processedStream = await chromaKeyProcessorRef.current.start(stream, {
        keyColor: config.keyColor || '#00ff00',
        similarity: config.similarity || 40,
        smoothness: config.smoothness || 10,
        backgroundImageUrl: config.backgroundImageUrl,
      });

      // Apply to webcam
      mMeeting?.changeWebcam(processedStream);
      setGreenScreenEnabled(true);
      setVirtualBackgroundOn(true);
      setProcessedWebcamStream(processedStream);

      console.log("Green screen applied successfully");

    } catch (error) {
      console.error("Error applying green screen:", error);
      toast.error("Failed to apply green screen effect");

      // Try to recover
      try {
        if (chromaKeyProcessorRef.current) {
          await chromaKeyProcessorRef.current.stop();
        }
        const stream = await createCameraVideoTrack(hdCameraTrackConfig);
        mMeeting?.changeWebcam(stream);
        setGreenScreenEnabled(false);
        setProcessedWebcamStream(null);
      } catch (recoveryErr) {
        console.error("Recovery failed:", recoveryErr);
      }
    }
  };

  // Logo overlay is rendered as an HTML overlay in ParticipantView (same as brand menu).
  // The logo/QR are synced to all participants via PubSub participantBranding state,
  // so every viewer sees them positioned on the correct tile without touching the video stream.

  // Periodically fetch meeting statistics for settings panel
  // Writes to a ref (not state) to avoid re-rendering the entire component tree every 2s
  useEffect(() => {
    if (!mMeeting?.localParticipant) return;

    const fetchStats = async () => {
      try {
        const localP = mMeeting.localParticipant;
        const videoStats = localP.webcamOn ? await localP.getVideoStats?.() : null;
        const audioStats = localP.micOn ? await localP.getAudioStats?.() : null;

        localParticipantStatsRef.current = {
          video: videoStats,
          audio: audioStats,
          webcamOn: localP.webcamOn,
          micOn: localP.micOn,
          timestamp: Date.now(),
        };
      } catch (error) {
        console.error("Error fetching participant stats:", error);
      }
    };

    // Fetch immediately and then every 2 seconds
    fetchStats();
    const interval = setInterval(fetchStats, 2000);

    return () => clearInterval(interval);
  }, [mMeeting?.localParticipant, mMeeting?.localParticipant?.webcamOn, mMeeting?.localParticipant?.micOn]);

  // Transform raised hands data to include participant names for RaisedHandsQueue
  // Sorted by raisedHandOn ascending (chronological order - first raised appears first)
  const raisedHandsWithNames = useMemo(() => {
    if (!raisedHandsParticipants || !mMeeting?.participants) return [];
    return [...raisedHandsParticipants]
      .sort((a, b) => a.raisedHandOn - b.raisedHandOn)
      .map((rh) => {
        const participant =
          mMeeting?.participants?.get?.(rh.participantId) ||
          mMeeting?.participants?.[rh.participantId];
        const name =
          participant?.displayName ||
          participant?.value?.displayName ||
          "Participant";
        return {
          id: rh.participantId,
          name,
          raisedAt: rh.raisedHandOn,
        };
      });
  }, [raisedHandsParticipants, mMeeting?.participants]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      let participantIds = Array.from(mMeeting.participants.keys());
      // In recorder mode, filter out the recorder's own local participant from the grid
      if (isRecorderMode && mMeeting.localParticipant?.id) {
        participantIds = participantIds.filter(id => id !== mMeeting.localParticipant.id);
      }
      setParticipantsData(participantIds);
      // if (isPresenting && videoResolution == 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('low');
      // } else if (participantIds.length > 10 && videoResolution == 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('high');
      // } else if (participantIds.length <= 10 && videoResolution != 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('low');
      // }
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [mMeeting.participants]);

  // ── Rally Token: Show solo-host toast after 10s if host is alone ──
  useEffect(() => {
    if (!["moderator", "host"].includes(meetingRole)) return;
    if (soloHostToastShownRef.current || hostTokenEarnedRef.current) return;

    const soloTimer = setTimeout(() => {
      const currentSize = mMeetingRef.current?.participants?.size || 0;
      if (currentSize <= 1 && !hostTokenEarnedRef.current && !soloHostToastShownRef.current) {
        soloHostToastShownRef.current = true;
        const msg = SOLO_HOST_MESSAGES[Math.floor(Math.random() * SOLO_HOST_MESSAGES.length)];
        toast.info(`${msg} AKA ...Your token will be awarded when the first visitor arrives.`, {
          position: "bottom-left",
          autoClose: 10000,
          hideProgressBar: false,
          closeButton: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });
      }
    }, 10000);
    return () => clearTimeout(soloTimer);
  }, [meetingRole]);

  // ── Rally Token: Host earns token via real-time Socket.IO event ──
  // (Handled by onHostRewardEarned callback registered in registerMuteCallbacks below)

  useEffect(() => {
    // console.log("meetingSession", meetingSession);
    // console.log("mMeeting", mMeeting);
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  // Set full initial presence state once RTM is actually connected and channel-ready
  // (onMeetingJoined fires before RTM login completes, so the initial setPresenceState silently fails)
  const hasSetInitialPresenceRef = useRef(false);
  useEffect(() => {
    if (!rtm?.isRealContext || !rtm?.isLoggedIn || !rtm?.isChannelReady) return;
    if (hasSetInitialPresenceRef.current) return;
    if (!mMeetingRef.current?.localParticipant) return;
    hasSetInitialPresenceRef.current = true;

    const initialMicMuted = meetingSettings?.muteParticipants && !["moderator", "co-host"].includes(meetingRole);
    const initialPresence = {
      micMuted: initialMicMuted || !micOn ? "true" : "false",
      camOff: webcamOn ? "false" : "true",
      role: meetingRole || "participant",
      displayName: mMeetingRef.current?.localParticipant?.displayName || "Unknown",
      videoResolution: webcamOn ? (mMeetingRef.current?.currentVideoProfileLabel || "1280x720") : "cam-off",
      optimizationMode: mMeetingRef.current?.currentOptimizationMode || "motion",
    };
    // Include DB userId for logged-in users (used for co-host DB persistence)
    if (user?.id) {
      initialPresence.userId = user.id;
    }
    rtm.setPresenceState(initialPresence);
    console.log('[MeetingContainer] Initial presence state set (RTM ready)', { userId: user?.id });

    // Hosts clean up all stale persisted channel attributes from previous sessions
    // so participants joining later don't see elements that are no longer active.
    if (["moderator", "co-host"].includes(meetingRole) && rtm?.deleteChannelAttribute) {
      const staleAttrs = [
        'pubsub_SHOW_ELEMENT_SECTION',
        'pubsub_CLOSE_ELEMENT_SECTION',
        'pubsub_AUDIO_CONTROL',
        'pubsub_WHITEBOARD',
        'pubsub_BRANDING_SETTINGS',
        'pubsub_MEETING_SETTINGS',
      ];
      staleAttrs.forEach(attr => rtm.deleteChannelAttribute(attr).catch(() => {}));
      console.log('[MeetingContainer] Host cleared stale RTM channel attributes on join');
    }
  }, [rtm?.isRealContext, rtm?.isLoggedIn, rtm?.isChannelReady, meetingRole, meetingSettings?.muteParticipants]);

  // Update presence state when adaptive video profile changes or camera toggles
  useEffect(() => {
    if (rtm?.setPresenceState && mMeeting?.currentVideoProfileLabel) {
      const resolution = mMeeting.localWebcamOn ? mMeeting.currentVideoProfileLabel : 'cam-off';
      rtm.setPresenceState({ videoResolution: resolution });
    }
  }, [mMeeting?.currentVideoProfileLabel, mMeeting?.localWebcamOn]);

  // Update presence state when active-speaker optimizationMode flips ('motion' <-> 'detail')
  useEffect(() => {
    if (rtm?.setPresenceState && mMeeting?.currentOptimizationMode) {
      rtm.setPresenceState({ optimizationMode: mMeeting.currentOptimizationMode });
    }
  }, [mMeeting?.currentOptimizationMode]);

  // Initialize PubSub RTM when meeting is connected
  useEffect(() => {
    if (mMeeting?.meetingId && mMeeting?.localParticipant?.id) {
      console.log('[MeetingContainer] Initializing PubSub RTM with:', {
        meetingId: mMeeting.meetingId,
        participantId: mMeeting.localParticipant.id
      });
      initializePubSubRTM(mMeeting.meetingId, mMeeting.localParticipant.id);
    }

    return () => {
      // Cleanup will be handled by onMeetingLeft
    };
  }, [mMeeting?.meetingId, mMeeting?.localParticipant?.id]);

  // Load notetaker settings from user preferences
  useEffect(() => {
    if (notetakerSettingsLoadedRef.current) return;
    if (!mMeeting?.meetingId) return;

    const loadNotetakerSettings = async () => {
      try {
        notetakerSettingsLoadedRef.current = true;
        const response = await requests.get('/notetaker/settings');
        if (response?.settings) {
          setNotetakerSettings(response.settings);
          console.log('[Notetaker] Settings loaded:', {
            enabled: response.settings.enabled_by_default,
            autoJoin: response.settings.auto_join_meetings,
            meetingType: response.settings.default_meeting_type,
          });
        }
      } catch (error) {
        console.log('[Notetaker] Using default settings (could not load)');
        // Use defaults — notetakerSettings remains null, auto-start will proceed
      }
    };

    loadNotetakerSettings();
  }, [mMeeting?.meetingId]);

  // Privacy-mode notice — replaces the AI Notetaker prompt at meeting start
  // when the room has privacy on, and is reused by any UI guard that intercepts
  // a recording / AI Notes attempt.
  const [privacyNoticeOpen, setPrivacyNoticeOpen] = useState(false);
  const privacyNoticePromptShownRef = useRef(false);
  const privacyMode = !!(meetingState.privacy || meetingSettings?.privacy);

  // Prompt host/co-host to enable AI Notetaker via a 30-second toast,
  // OR show the privacy notice modal once when privacy mode is on.
  // Fires once per meeting; skipped if another host has already started the bot.
  const notetakerAutoStartedRef = useRef(false);
  const notetakerPromptShownRef = useRef(false);
  useEffect(() => {
    if (!mMeeting?.meetingId || !mMeeting?.localParticipant?.id) return;
    if (!["moderator", "host", "co-host"].includes(meetingRole)) return;
    if (meetingState.notetaker?.isActive) return; // Someone already started it

    if (privacyMode) {
      if (privacyNoticePromptShownRef.current) return;
      privacyNoticePromptShownRef.current = true;
      const timer = setTimeout(() => setPrivacyNoticeOpen(true), 3000);
      return () => clearTimeout(timer);
    }

    if (notetakerPromptShownRef.current) return;
    notetakerPromptShownRef.current = true;
    let activeToastId = null;
    const timer = setTimeout(() => {
      if (meetingState.notetaker?.isActive) return;
      activeToastId = toast(
        ({ closeToast }) => (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-base">✨</span>
              <span className="text-sm font-semibold text-white">Enable AI Notetaker?</span>
            </div>
            <p className="text-xs text-gray-300 leading-snug">
              Record &amp; transcribe this meeting so AI can generate a summary, action items and decisions.
            </p>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={async () => {
                  const ok = await handleStartNotetaker();
                  if (ok) toast.success('AI Notetaker started');
                  else toast.error('Failed to start AI Notetaker');
                  closeToast();
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90"
              >
                Enable
              </button>
              <button
                onClick={closeToast}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 bg-white/10 hover:bg-white/20"
              >
                Not now
              </button>
            </div>
          </div>
        ),
        {
          autoClose: 30000,
          closeOnClick: false,
          draggable: false,
          hideProgressBar: false,
          theme: 'dark',
        }
      );
      console.log('[STT Bot] Prompted host to enable AI Notetaker (toast)', activeToastId);
    }, 3000);

    // Dismiss the toast on unmount (user navigates away from the meeting view).
    // Without this the toast keeps showing on subsequent pages because it lives
    // in the global ToastContainer.
    return () => {
      clearTimeout(timer);
      if (activeToastId !== null) {
        try { toast.dismiss(activeToastId); } catch (_) { /* ignore */ }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mMeeting?.meetingId, mMeeting?.localParticipant?.id, meetingRole, privacyMode]);

  // Manual start/stop notetaker (called from ToolsPanel toggle and bottom-bar transcription button)
  // Idempotent across hosts: backend dedupes by channelName, broadcast keeps all clients in sync.
  const notetakerTransitioningRef = useRef(false);
  const handleStartNotetaker = async () => {
    const meetingId = mMeeting?.meetingId;
    if (!meetingId) {
      console.warn('[STT Bot] No meetingId available');
      return false;
    }
    // Privacy gate: when on, surface the notice modal and refuse to start.
    // The /agora/stt/start endpoint also returns 403 PRIVACY_MODE_ENABLED, so
    // this is defense-in-depth, not the sole guard.
    if (privacyMode) {
      setPrivacyNoticeOpen(true);
      return false;
    }
    if (notetakerTransitioningRef.current) {
      console.log('[STT Bot] Start ignored — transition in flight');
      return true;
    }
    notetakerTransitioningRef.current = true;
    try {
      const agoraSttLang = toAgoraSttLanguage(spokenLanguage || 'en');
      console.log('[STT Bot] Starting for meeting:', meetingId, 'language:', agoraSttLang);
      // Backend is idempotent: if an agent already exists for this channel it returns
      // { alreadyRunning: true, agentId, pubBotUid } instead of creating a duplicate.
      const result = await startStt(meetingId, [agoraSttLang]);
      if (result.success) {
        const botUid = Number(result.pubBotUid);
        setSttPubBotUid(botUid);
        sttPubBotUidRef.current = botUid;
        setSttAgentId(result.agentId);
        setSttBotActive(true);
        sttHasTranslateRef.current = null; // notetaker start has no translateConfig
        notetakerAutoStartedRef.current = true;
        meetingState.emitNotetakerUpdate(true);
        console.log('[STT Bot] Started, pubBotUid:', botUid, 'alreadyRunning:', !!result.alreadyRunning);
        return true;
      }
      console.warn('[STT Bot] Start returned unsuccessful');
      return false;
    } catch (error) {
      console.error('[STT Bot] Failed to start:', error);
      return false;
    } finally {
      notetakerTransitioningRef.current = false;
    }
  };

  const handleStopNotetaker = async () => {
    const meetingId = mMeeting?.meetingId;
    if (!meetingId) return;
    if (notetakerTransitioningRef.current) {
      console.log('[STT Bot] Stop ignored — transition in flight');
      return;
    }
    notetakerTransitioningRef.current = true;
    try {
      // Always call stop — backend looks up the agent by channelName, not by local state.
      // This lets co-hosts stop a bot started by the host even though their local sttAgentId is null.
      await stopStt(meetingId);
      setSttAgentId(null);
      setSttPubBotUid(null);
      sttPubBotUidRef.current = null;
      setSttBotActive(false);
      meetingState.emitNotetakerUpdate(false);
      console.log('[STT Bot] Stopped');
    } catch (error) {
      console.error('[STT Bot] Failed to stop:', error);
    } finally {
      notetakerTransitioningRef.current = false;
    }
  };

  // Auto-mute participants when muteParticipants setting is enabled
  // Uses a ref to prevent infinite loop (mMeeting changes when mic state changes)
  useEffect(() => {
    // Only run once when settings indicate mute should be applied
    // Skip if already applied or if user is moderator/co-host
    if (
      mMeeting &&
      mMeeting.localParticipant &&
      meetingSettings?.muteParticipants &&
      !meetingSettings?.guestCanUnmute &&
      !["moderator", "co-host"].includes(meetingRole) &&
      !autoMuteAppliedRef.current
    ) {
      if (mMeeting.localParticipant.micOn) {
        mMeeting.muteMic();
        setMicOn(false);
      }
      autoMuteAppliedRef.current = true;
    }

    // Reset when settings change to allow unmuting
    if (!meetingSettings?.muteParticipants || meetingSettings?.guestCanUnmute) {
      autoMuteAppliedRef.current = false;
    }
  }, [
    meetingSettings?.muteParticipants,
    meetingSettings?.guestCanUnmute,
    meetingRole,
  ]); // Note: Removed mMeeting from dependencies to prevent infinite loop

  const { publish: publishRaiseHand } = usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName, message } = data;

      // Convert senderId to numeric UID for remote participant matching
      // RTM uses string IDs, but RTC uses numeric UIDs from stringToNumericUid()
      const numericSenderId = stringToNumericUid(senderId);
      const isLocal = senderId === localParticipantId || numericSenderId === stringToNumericUid(localParticipantId);

      // IMPORTANT: Use the correct ID format based on participant type:
      // - LOCAL participant: stored in participants map with STRING participantId
      // - REMOTE participants: stored in participants map with NUMERIC uid
      // So we need to use string for local, numeric for remote
      const participantIdToStore = isLocal ? localParticipantId : numericSenderId;

      // New payload shape: {action: "raise" | "lower"} → idempotent absolute
      // setter. This avoids the toggle-desync bug where dropped or
      // out-of-order RTM messages flipped a participant's view out of sync
      // with the room (the symptom: a host sometimes missed a hand raise, or
      // a lowered hand stuck "raised" on other participants' screens).
      //
      // Legacy payload (plain "Raise Hand" string from publishers that
      // haven't been rebuilt yet) still falls through to the toggle so
      // mixed-bundle deployments keep working during the rollout window.
      const isAbsolutePayload =
        message && typeof message === "object" &&
        (message.action === "raise" || message.action === "lower");
      const action = isAbsolutePayload
        ? setParticipantHandRaised(participantIdToStore, message.action === "raise")
        : participantRaisedHand(participantIdToStore);
      const emoji = action === "lowered" ? "✋" : "🖐🏼";

      // Update local isHandRaised state for UI
      if (isLocal) {
        setIsHandRaised(action === "raised");
        // Sync hand raise state to backend for late joiners
        if (action === "raised") {
          meetingState.emitHandRaise();
        } else {
          meetingState.emitHandLower();
        }
      }

      // new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      // ).play();

      // toast(
      //   `${
      //     isLocal ? "You" : nameTructed(senderName, 15)
      //   } ${action} hand ${emoji}`,
      //   {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   }
      // );
    },
  });

  // LOWER_HAND pubsub for hosts to lower participant hands
  const { publish: publishLowerHand } = usePubSub("LOWER_HAND", {
    onMessageReceived: (data) => {
      const { message } = data;
      const targetParticipantId = message?.participantId;

      if (targetParticipantId) {
        // Convert to numeric UID for remote participant matching
        const numericTargetId = stringToNumericUid(targetParticipantId);
        const localId = mMeeting?.localParticipant?.id;
        const isTargetLocal = targetParticipantId === localId || numericTargetId === stringToNumericUid(localId);

        // Remove from raised hands list - check both string and numeric IDs
        // Local user is stored with string ID, remote users with numeric ID
        setRaisedHandsParticipants((prev) =>
          prev.filter((p) => {
            // For local target, check against string ID
            if (isTargetLocal) {
              return p.participantId !== localId;
            }
            // For remote target, check against numeric ID
            return p.participantId !== numericTargetId;
          })
        );

        // If it's the local participant's hand being lowered, update local state
        if (isTargetLocal) {
          setIsHandRaised(false);
        }
        // Sync to backend for late joiners
        meetingState.emitHandLower(targetParticipantId);
      } else if (message === "LOWER_ALL") {
        // Lower all hands
        setRaisedHandsParticipants([]);
        setIsHandRaised(false);
        // Sync to backend for late joiners
        meetingState.emitHandsLowerAll();
      }
    },
  });

  // Sync raised hands state to RTM channel attributes so late joiners can hydrate
  useEffect(() => {
    if (!rtm?.isRealContext || !rtm?.isChannelReady) return;
    const timer = setTimeout(async () => {
      try {
        await rtm.setChannelAttribute('raisedHands', raisedHandsParticipants);
        console.log('[RaisedHands] Synced to channel attributes:', raisedHandsParticipants.length, 'hands');
      } catch (e) {
        console.error('[RaisedHands] Failed to sync to channel attributes:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [raisedHandsParticipants, rtm?.isRealContext, rtm?.isChannelReady]);

  // Hydrate raised hands from RTM channel attributes when joining (for late joiners)
  const hasHydratedRaisedHandsRef = useRef(false);
  useEffect(() => {
    if (!rtm?.isRealContext || !rtm?.isChannelReady || hasHydratedRaisedHandsRef.current) return;
    hasHydratedRaisedHandsRef.current = true;

    const hydrateRaisedHands = async () => {
      try {
        const attributes = await rtm.getChannelAttributes();
        const raisedHandsAttr = attributes?.raisedHands;
        if (!raisedHandsAttr?.value) return;

        const parsed = JSON.parse(raisedHandsAttr.value);
        if (!Array.isArray(parsed) || parsed.length === 0) return;

        // Filter out participants who are no longer in the meeting
        const currentIds = mMeeting?.participants
          ? new Set(Array.from(mMeeting.participants.keys()).map(String))
          : new Set();

        const validEntries = parsed.filter(p => {
          const pidStr = String(p.participantId);
          return currentIds.has(pidStr) || currentIds.has(String(stringToNumericUid(pidStr)));
        });

        if (validEntries.length === 0) return;

        console.log('[RaisedHands] Hydrating from channel attributes:', validEntries.length, 'hands');

        setRaisedHandsParticipants(prev => {
          if (prev.length > 0) {
            // Merge: keep existing entries, add missing ones from attributes
            const existingIds = new Set(prev.map(p => p.participantId));
            const newEntries = validEntries.filter(p => !existingIds.has(p.participantId));
            return newEntries.length > 0 ? [...prev, ...newEntries] : prev;
          }
          return validEntries;
        });

        // Update local isHandRaised if the local user's hand was raised before joining
        const localId = mMeeting?.localParticipant?.id;
        if (localId) {
          const localNumericId = stringToNumericUid(localId);
          const isLocalHandRaised = validEntries.some(
            p => p.participantId === localId || p.participantId === localNumericId
          );
          if (isLocalHandRaised) {
            setIsHandRaised(true);
          }
        }
      } catch (error) {
        console.error('[RaisedHands] Failed to hydrate from channel attributes:', error);
      }
    };

    hydrateRaisedHands();
  }, [rtm?.isRealContext, rtm?.isChannelReady]);

  // ─── Hydrate state from meeting state snapshot (late joiner) ───
  const hasHydratedSnapshotRef = useRef(false);
  useEffect(() => {
    if (hasHydratedSnapshotRef.current || !meetingState.isSnapshotLoaded) return;
    hasHydratedSnapshotRef.current = true;

    // Hydrate layout from snapshot — but never override an explicit recorder
    // layout choice. If the recorder bot was launched with ?layout=, that
    // selection wins; otherwise fall back to the host's snapshot layout.
    if (!isRecorderLayoutPinned && meetingState.activeLayout && meetingState.activeLayout !== 'gallery') {
      console.log('[MeetingState] Hydrating layout from snapshot:', meetingState.activeLayout);
      setSelectedLayout(meetingState.activeLayout);
    }

    // Hydrate raised hands from snapshot
    if (meetingState.raisedHands?.length > 0) {
      console.log('[MeetingState] Hydrating raised hands from snapshot:', meetingState.raisedHands.length);
      setRaisedHandsParticipants(prev => {
        if (prev.length > 0) return prev; // Don't overwrite if already populated
        return meetingState.raisedHands.map(h => ({
          participantId: h.participantId,
          name: h.participantName,
          raisedAt: h.raisedAt,
        }));
      });
    }

    // Hydrate host controls — if muteAll is active, set mutedByHost immediately
    // so the participant cannot unmute themselves even before the mic is initialized
    // Presenters are exempt (like hosts/co-hosts) — presenter state may load later via persisted PubSub
    if (meetingState.hostControls?.muteAll && !["moderator", "co-host", "host"].includes(meetingRole)) {
      const hydrateLocalId = mMeeting?.localParticipant?.id;
      const hydrateNumId = hydrateLocalId ? stringToNumericUid(hydrateLocalId) : null;
      const isPresenterAtHydrate = hydrateLocalId && (
        presenters?.has(hydrateLocalId) ||
        presenters?.has(String(hydrateLocalId)) ||
        (hydrateNumId != null && presenters?.has(hydrateNumId))
      );
      if (!isPresenterAtHydrate) {
        console.log('[MeetingState] muteAll is active — marking late joiner as mutedByHost');
        setMutedByHost(true);
        setMicOn(false);
        // Also try to mute the mic if already initialized
        if (mMeeting?.localParticipant?.micOn) {
          mMeeting.muteMic();
        }
      }
    }

    // Hosts clear all stale active elements from the Socket.IO snapshot so
    // participants joining later don't see elements from a previous session.
    if (["moderator", "co-host"].includes(meetingRole) && meetingState.activeElements?.length > 0) {
      console.log('[MeetingState] Host clearing', meetingState.activeElements.length, 'stale active element(s) from snapshot');
      meetingState.activeElements.forEach(el => {
        if (el.id) meetingState.emitElementRemove(el.id);
      });
    }
  }, [meetingState.isSnapshotLoaded]);

  // ─── Listen for real-time host controls updates ───
  useEffect(() => {
    if (!["moderator", "co-host", "host"].includes(meetingRole)) {
      // Presenters are exempt from muteAll (like hosts/co-hosts)
      const localId = mMeeting?.localParticipant?.id;
      const localNumId = localId ? stringToNumericUid(localId) : null;
      const isLocalPresenter = localId && (
        presenters?.has(localId) ||
        presenters?.has(String(localId)) ||
        (localNumId != null && presenters?.has(localNumId))
      );
      if (isLocalPresenter) return;

      if (meetingState.hostControls?.muteAll) {
        // Always set mutedByHost immediately — blocks the unmute button even if mic isn't initialized yet
        setMutedByHost(true);
        setMicOn(false);
        if (mMeeting?.localMicOn) {
          mMeeting.muteMic();
        }
        // Retry after a short delay to catch mic that initializes after this effect
        const retryTimer = setTimeout(() => {
          if (mMeeting?.localMicOn) {
            mMeeting.muteMic();
            setMicOn(false);
          }
        }, 1000);
        return () => clearTimeout(retryTimer);
      } else {
        // muteAll was released — clear the lock
        setMutedByHost(false);
      }
    }
  }, [meetingState.hostControls?.muteAll, presenters]);

  // ─── Register Socket.IO-based mute callbacks (reliable, replaces PubSub for muting) ───
  useEffect(() => {
    if (!meetingState.registerMuteCallbacks) return;

    const localId = mMeeting?.localParticipant?.id;
    const localNumericUid = localId ? stringToNumericUid(localId) : null;
    const isHostOrCoHost = ["moderator", "co-host", "host"].includes(meetingRole);

    meetingState.registerMuteCallbacks({
      // Individual force mute: host muted a specific participant
      onForceMuted: async (data) => {
        const { targetParticipantId, senderName, requestId, senderSocketId } = data;
        const targetNum = typeof targetParticipantId === 'number' ? targetParticipantId : Number(targetParticipantId);
        const isTarget = localId != null && (
          String(targetParticipantId) === String(localId) ||
          targetParticipantId === localId ||
          (localNumericUid != null && targetNum === localNumericUid)
        );

        if (isTarget) {
          // Hosts/co-hosts can be muted by other hosts/co-hosts, but they can freely unmute
          const lockUnmute = !isHostOrCoHost;
          console.log(`[Mute] Force muted by ${senderName || 'host'} via Socket.IO (lockUnmute=${lockUnmute}${requestId ? `, req=${requestId}` : ''})`);
          let ackSuccess = false;
          let ackVerified = false;
          let ackReason = null;
          try {
            const result = await mMeeting?.muteMic();  // AWAIT the actual mute
            setMicOn(false);
            if (lockUnmute) setMutedByHost(true);
            // Confirm via presence state
            if (rtm?.setPresenceState) {
              await rtm.setPresenceState({ micMuted: "true" });
            }
            toast.info(`Your microphone has been muted by ${senderName || 'the host'}.`, {
              position: "bottom-left",
              autoClose: 4000,
              theme: "dark",
            });
            // `result` has the verified/retried shape from the updated muteMic,
            // or is undefined on legacy builds — treat legacy as success.
            if (result && typeof result === 'object') {
              ackSuccess = result.ok !== false;
              ackVerified = result.verified !== false;
              ackReason = result.reason || null;
            } else {
              ackSuccess = true;
              ackVerified = true;
            }
          } catch (err) {
            console.error('[Mute] Failed to mute mic:', err);
            ackReason = err?.message || 'mute-threw';
          }
          // Reply to host so their UI can show real feedback. Legacy hosts
          // don't send requestId, so we skip the ack in that case.
          if (requestId && senderSocketId && meetingState.emitForceMutedAck) {
            meetingState.emitForceMutedAck({
              requestId,
              senderSocketId,
              success: ackSuccess,
              verified: ackVerified,
              reason: ackReason,
            });
          }
        }
      },

      // Force mute ALL: host pressed mute-all
      onForceMuteAll: async (data) => {
        if (isHostOrCoHost) return; // Hosts/co-hosts exempt

        // Presenters are exempt from mute-all (like hosts/co-hosts)
        const isLocalPresenter = localId && (
          presenters?.has(localId) ||
          presenters?.has(String(localId)) ||
          (localNumericUid != null && presenters?.has(localNumericUid))
        );
        if (isLocalPresenter) return;

        const { senderParticipantId } = data;
        // Don't mute if we are the sender
        if (senderParticipantId && (
          String(senderParticipantId) === String(localId) ||
          (localNumericUid != null && Number(senderParticipantId) === localNumericUid)
        )) return;

        console.log('[Mute] Force mute ALL received via Socket.IO');
        try {
          if (mMeeting?.localMicOn) {
            await mMeeting.muteMic();  // AWAIT the actual mute
            setMicOn(false);
          }
          setMutedByHost(true);
          // Confirm via presence state
          if (rtm?.setPresenceState) {
            await rtm.setPresenceState({ micMuted: "true" });
          }
          toast.info('Your microphone has been muted by the host.', {
            position: "bottom-left",
            autoClose: 4000,
            theme: "dark",
          });
        } catch (err) {
          console.error('[Mute] Failed to mute mic on MUTE_ALL:', err);
        }
      },

      // Unmute permission granted for individual participant
      onUnmuteGranted: (data) => {
        const { targetParticipantId } = data;
        const targetNum = typeof targetParticipantId === 'number' ? targetParticipantId : Number(targetParticipantId);
        const isTarget = localId != null && (
          String(targetParticipantId) === String(localId) ||
          targetParticipantId === localId ||
          (localNumericUid != null && targetNum === localNumericUid)
        );

        if (isTarget) {
          console.log('[Mute] Unmute permission granted via Socket.IO');
          setMutedByHost(false);
          setShowMicPermissionModal(true);
        }
      },

      // Unmute permission granted for ALL — just clears the host-mute lock
      // so attendees CAN unmute themselves if they choose.
      // Does NOT show the permission modal (that's only for individual unmute grants).
      onUnmuteGrantedAll: () => {
        if (isHostOrCoHost) return;
        console.log('[Mute] Unmute ALL granted via Socket.IO — clearing mutedByHost');
        setMutedByHost(false);
      },

      // Spotlight changed via Socket.IO — pin/unpin the participant
      onSpotlightChanged: (data) => {
        const { participantId, action, displayName } = data;
        if (!participantId) return;
        console.log('[Spotlight] Socket.IO spotlight changed:', { participantId, action, displayName });
        if (action === 'spotlight') {
          mMeeting?.pin?.(participantId);
          // Auto-switch to focus view when someone is spotlighted — but never
          // override an explicit recorder layout choice from the modal.
          if (!isRecorderLayoutPinned) {
            // Save the user's current layout the FIRST time a spotlight kicks in
            // so we can restore it on unspotlight. Don't overwrite on subsequent
            // spotlights — we want the original pre-spotlight layout.
            if (preSpotlightLayoutRef.current === null) {
              preSpotlightLayoutRef.current = selectedLayout;
            }
            setSelectedLayout('focus');
          }
        } else if (action === 'unspotlight') {
          mMeeting?.unpin?.(participantId);
          const remaining = data.spotlightedParticipants?.length ?? 0;
          // Only revert when the LAST spotlight is removed. Skip the revert if
          // the user manually switched layouts during the spotlight (respect
          // their intent — don't yank them around).
          if (
            remaining === 0 &&
            preSpotlightLayoutRef.current !== null &&
            !isRecorderLayoutPinned &&
            selectedLayout === 'focus' &&
            preSpotlightLayoutRef.current !== 'focus'
          ) {
            setSelectedLayout(preSpotlightLayoutRef.current);
          }
          if (remaining === 0) {
            preSpotlightLayoutRef.current = null;
          }
        }
      },

      // Host reward: real-time notification from backend when unique-IP visitor joins
      onHostRewardEarned: async (data) => {
        if (!["moderator", "host"].includes(meetingRole)) return;
        if (hostTokenEarnedRef.current) return;

        console.log('[Rewards] Host reward earned via Socket.IO:', data);
        hostTokenEarnedRef.current = true;

        // Earn the token via API first to check daily cap
        let earned = false;
        let capped = false;
        try {
          const agoraMeetingId = mMeetingRef.current?.meetingId;
          const result = await rewardService.earnTokens({
            actionType: 'host',
            roomId: meetingSettings?.roomId,
            meetingId: agoraMeetingId,
          });
          console.log('[Rewards] Host token earned:', result);
          earned = true;
        } catch (err) {
          const msg = (err.message || '').toLowerCase();
          if (msg.includes('daily') || msg.includes('cap') || msg.includes('weekly')) {
            console.log('[Rewards] Daily/weekly hosting cap reached');
            capped = true;
          } else if (msg.includes('already earned')) {
            // Duplicate call for same meeting session — treat as already earned
            console.log('[Rewards] Token already earned for this meeting session');
            earned = true;
          } else {
            console.error('[Rewards] Failed to earn host token:', err);
            // Still show celebration — the socket event confirmed eligibility
            earned = true;
          }
        }

        // Play cha-ching sound (only if actually earned, not capped)
        if (earned && chaChingSoundRef.current) {
          chaChingSoundRef.current.play().catch((err) => {
            console.warn('[Rewards] Failed to play cha-ching sound:', err);
          });
        }

        // Show celebration overlay
        setRewardCelebrationVariant(capped ? "capped" : "earned");
        setShowRewardCelebration(true);

        // Dispatch custom event so TopBar can refresh its balance
        if (earned) {
          window.dispatchEvent(new CustomEvent('rally-token-earned'));
        }
      },

      // Broadcast message from host — show notification based on target audience
      onBroadcastMessage: (data) => {
        const { message, senderName, target } = data;
        if (!message) return;

        // Filter based on target audience
        const isHostOrCoHost = ["moderator", "co-host", "host"].includes(meetingRole);
        const isPanelist = isHostOrCoHost || meetingRole === 'speaker' || meetingRole === 'presenter';
        const localId = mMeeting?.localParticipant?.id;
        const localNumericUid = localId ? stringToNumericUid(localId) : null;
        const isLocalPresenter = localId && (
          presenters?.has(localId) ||
          presenters?.has(String(localId)) ||
          (localNumericUid != null && presenters?.has(localNumericUid))
        );

        if (target === 'panelists' && !isPanelist && !isLocalPresenter) {
          // "Panelists only" — skip regular attendees
          return;
        }
        // 'everyone' — show to all (no filtering)

        console.log('[Broadcast] Message received:', message, 'from', senderName, 'target:', target);
        toast.info(
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>📢 {senderName || 'Host'}</div>
            <div>{message}</div>
          </div>,
          {
            position: "top-center",
            autoClose: 10000,
            theme: "dark",
            style: { background: '#1a1a2e', border: '1px solid rgba(106, 31, 191, 0.5)' },
          }
        );
      },
    });
  }, [mMeeting?.localParticipant?.id, meetingRole, mMeeting?.localMicOn, presenters]);

  // Webinar layout sync: host emits scene-change on layout switch (see onLayoutChange handlers).
  // Attendees + recorder receive via meeting-state:scene-changed socket → meetingState.activeLayout.

  // BRB (Be Right Back) pubsub for syncing BRB status across participants
  const { publish: publishBRB } = usePubSub("BRB", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;
      const { senderId, senderName, message } = data;

      // Convert senderId to numeric UID for remote participant matching
      const numericSenderId = stringToNumericUid(senderId);
      const isLocal = senderId === localParticipantId || numericSenderId === stringToNumericUid(localParticipantId);
      const action = message?.action; // "brb" or "back"

      // IMPORTANT: Use the correct ID format based on participant type:
      // - LOCAL participant: stored in participants map with STRING participantId
      // - REMOTE participants: stored in participants map with NUMERIC uid
      const participantIdToStore = isLocal ? localParticipantId : numericSenderId;

      if (action === "brb") {
        // Add to BRB list with appropriate ID
        setBrbParticipants((prev) => {
          if (prev.find((p) => p.participantId === participantIdToStore)) return prev;
          return [...prev, { participantId: participantIdToStore, brbAt: new Date().getTime() }];
        });
        if (isLocal) {
          setIsBRB(true);
        }
      } else if (action === "back") {
        // Remove from BRB list
        setBrbParticipants((prev) =>
          prev.filter((p) => p.participantId !== participantIdToStore)
        );
        if (isLocal) {
          setIsBRB(false);
        }
      }
    },
  });

  // BRANDING_SETTINGS pubsub - syncs branding settings across participants
  // When a host/co-host changes branding, all participants receive the full settings
  // NOTE: persist is NOT used to avoid stale branding from previous sessions.
  // Instead, existing participants re-publish their branding when a new participant joins.
  const lastPublishedBrandingRef = useRef(null);
  // Track the highest _rev seen per sender so out-of-order RTM deliveries
  // can't clobber a fresher branding update with a stale one.  This is the
  // common failure mode when the host rapidly switches banner styles —
  // messages can arrive out of order and the last-applied state wins,
  // which may not be the host's final choice.
  const lastBrandingRevBySenderRef = useRef({});
  const { publish: publishBrandingSettings } = usePubSub("BRANDING_SETTINGS", {
    onMessageReceived: (data) => {
      const { senderId, message } = data;
      const localId = mMeeting?.localParticipant?.id;

      // Convert senderId to numeric UID for remote participant matching
      // RTM uses string IDs, but RTC uses numeric UIDs from stringToNumericUid()
      const numericSenderId = stringToNumericUid(senderId);
      const isLocal = senderId === localId || numericSenderId === stringToNumericUid(localId);

      // Skip messages from self - we handle local state separately
      if (isLocal) return;

      // Drop stale out-of-order deliveries.  Messages published without a
      // _rev (older clients, initial payloads) are always accepted so we
      // don't regress on participants running the prior build.
      if (typeof message?._rev === 'number') {
        const lastRev = lastBrandingRevBySenderRef.current[senderId] || 0;
        if (message._rev <= lastRev) return;
        lastBrandingRevBySenderRef.current[senderId] = message._rev;
      }

      // Use the correct ID format: numeric UID for remote participants
      // so it matches participantId in ParticipantView
      const participantIdToStore = numericSenderId;

      // Apply meeting-wide branding (colors, styles, frames, lower third) from host/co-host
      if (message?.senderRole === 'moderator' || message?.senderRole === 'co-host') {
        setBranding((prev) => ({
          ...prev,
          // Colors
          primaryColor: message.primaryColor ?? prev.primaryColor,
          accentColor: message.accentColor ?? prev.accentColor,
          backgroundColor: message.backgroundColor ?? prev.backgroundColor,
          // Typography
          font: message.font ?? prev.font,
          // Video frame
          frame: message.frame ?? prev.frame,
          frameColor: message.frameColor ?? prev.frameColor,
          frameBorderWidth: message.frameBorderWidth ?? prev.frameBorderWidth,
          frameShadow: message.frameShadow ?? prev.frameShadow,
          gradientColor1: message.gradientColor1 ?? prev.gradientColor1,
          gradientColor2: message.gradientColor2 ?? prev.gradientColor2,
          // Lower third
          showLowerThird: message.showLowerThird ?? prev.showLowerThird,
          lowerThirdTemplate: message.lowerThirdTemplate ?? prev.lowerThirdTemplate,
          lowerThirdBackground: message.lowerThirdBackground ?? prev.lowerThirdBackground,
          lowerThirdBgColor: message.lowerThirdBgColor ?? prev.lowerThirdBgColor,
          lowerThirdTextColor: message.lowerThirdTextColor ?? prev.lowerThirdTextColor,
          lowerThirdTitleColor: message.lowerThirdTitleColor ?? prev.lowerThirdTitleColor,
          lowerThirdUnderline: message.lowerThirdUnderline ?? prev.lowerThirdUnderline,
          lowerThirdBorder: message.lowerThirdBorder ?? prev.lowerThirdBorder,
          lowerThirdBorderColor: message.lowerThirdBorderColor ?? prev.lowerThirdBorderColor,
          lowerThirdShape: message.lowerThirdShape ?? prev.lowerThirdShape,
          lowerThirdAnimation: message.lowerThirdAnimation ?? prev.lowerThirdAnimation,
          // AI Background
          activeBackground: message.activeBackground ?? prev.activeBackground,
          // NOTE: Logo and QR Code are NOT applied meeting-wide.
          // They are per-participant only (stored in participantBranding below)
          // so they only appear on the sender's own tile.
        }));
      }

      // Store per-participant branding for logo/QR overlays and lower third on tiles.
      // Always overwrite with the latest message — render code gates on
      // `showLogo && logoUrl`, `showQrCode && qrUrl`, etc., so a payload with
      // those toggles set to false correctly hides the overlays.  Avoid the
      // earlier "delete when all off" branch: it left a window where the
      // remote tile could keep showing a stale QR/logo if the snapshot
      // hydration effect later re-applied a pre-toggle entry.
      setParticipantBranding((prev) => {
        const next = { ...prev };
        // Drop any string-keyed legacy entry for this sender so the numeric
        // key written below is the single source of truth.
        if (senderId !== participantIdToStore) delete next[senderId];
        next[participantIdToStore] = message;
        return next;
      });
    },
  });

  // Helper to build the full branding message with all properties
  const buildFullBrandingMessage = useCallback(() => {
    return {
      senderRole: meetingRole,
      // Colors
      primaryColor: branding?.primaryColor || '#6a1fbf',
      accentColor: branding?.accentColor || '#00c853',
      backgroundColor: branding?.backgroundColor || '#001233',
      // Typography
      font: branding?.font || 'Inter',
      // Video frame
      frame: branding?.frame || 'none',
      frameColor: branding?.frameColor || '#6a1fbf',
      frameBorderWidth: branding?.frameBorderWidth ?? 2,
      frameShadow: branding?.frameShadow ?? true,
      gradientColor1: branding?.gradientColor1 || '#6a1fbf',
      gradientColor2: branding?.gradientColor2 || '#00c853',
      // Lower third
      showLowerThird: branding?.showLowerThird || false,
      lowerThirdTemplate: branding?.lowerThirdTemplate || 'modern',
      lowerThirdBackground: branding?.lowerThirdBackground ?? true,
      lowerThirdBgColor: branding?.lowerThirdBgColor || '#000000',
      lowerThirdTextColor: branding?.lowerThirdTextColor || '#ffffff',
      lowerThirdTitleColor: branding?.lowerThirdTitleColor || '#9ca3af',
      lowerThirdUnderline: branding?.lowerThirdUnderline || false,
      lowerThirdBorder: branding?.lowerThirdBorder || false,
      lowerThirdBorderColor: branding?.lowerThirdBorderColor || '#6a1fbf',
      lowerThirdShape: branding?.lowerThirdShape || 'minimal',
      lowerThirdAnimation: branding?.lowerThirdAnimation || 'none',
      // Per-participant lower third name/title (personal to each participant)
      lowerThirdName: branding?.lowerThirdName || '',
      lowerThirdTitle: branding?.lowerThirdTitle || '',
      // Logo
      showLogo: branding?.showLogo || false,
      logoUrl: branding?.logoUrl || '',
      logoPosition: branding?.logoPosition || 'top-right',
      // QR Code
      showQrCode: branding?.showQrCode || false,
      qrUrl: branding?.qrUrl || '',
      qrPosition: branding?.qrPosition || 'bottom-right',
      qrSize: branding?.qrSize || 'medium',
      qrColor: branding?.qrColor || '#001233',
      qrBgColor: branding?.qrBgColor || '#ffffff',
      qrLogo: branding?.qrLogo || '',
      // AI Background
      activeBackground: branding?.activeBackground || '',
    };
  }, [branding, meetingRole]);

  // Helper to build and publish the current branding message
  const publishCurrentBranding = useCallback(() => {
    const localId = mMeeting?.localParticipant?.id;
    if (!localId) return;

    const brandingMessage = buildFullBrandingMessage();
    // Stamp a fresh _rev so the receiver's revision check accepts this
    // re-publish even when the actual fields are unchanged.  Without _rev,
    // the receiver still applies it (legacy path), but with _rev we want
    // it to win over any earlier delivery the new joiner may have already
    // applied from the snapshot.
    brandingMessage._rev = Date.now();
    publishBrandingSettings(brandingMessage);
    // Also re-emit to the Socket.IO channel so the new joiner gets the
    // host's per-participant branding even if their RTM client hasn't
    // finished handshaking yet.  This is the same data the snapshot would
    // serve, but pushing it live closes the gap where the snapshot fetch
    // raced with a stale `state.participantBranding` (e.g., from a host
    // that joined on an older build with mis-keyed entries).
    const localNumericUid = stringToNumericUid(localId);
    meetingState?.emitParticipantBrandingUpdate?.(localNumericUid, brandingMessage);
    return brandingMessage;
  }, [
    mMeeting?.localParticipant?.id, publishBrandingSettings, buildFullBrandingMessage,
    meetingState,
  ]);

  // Effect to publish branding settings and update local state
  // Uses a ref to prevent duplicate publishes under rapid host clicks
  useEffect(() => {
    const localId = mMeeting?.localParticipant?.id;
    if (!localId) return;

    const brandingMessage = buildFullBrandingMessage();
    // Attach a monotonic revision so receivers can reject out-of-order
    // deliveries.  Without this, RTM reordering under rapid publishes can
    // cause a later (correct) state to be clobbered by a stale re-delivery.
    brandingMessage._rev = Date.now();

    // Dedup by payload so React re-renders that don't actually change
    // branding don't spam RTM.  We strip _rev before comparing — every call
    // has a fresh timestamp, so including it would defeat dedup entirely.
    const { _rev: _, ...comparable } = brandingMessage;
    const brandingStr = JSON.stringify(comparable);
    if (lastPublishedBrandingRef.current === brandingStr) return;

    // Server persistence first — it's synchronous w.r.t. the socket.emit
    // call and guarantees rejoining guests hydrate correctly even if the
    // RTM publish below fails.  Only hosts/co-hosts write meeting-wide
    // branding; other roles skip.
    if (["moderator", "co-host"].includes(meetingRole)) {
      // Strip per-participant fields before persisting to the meeting-wide
      // snapshot.  The receiver already discards these on hydration, but
      // keeping them out of the meeting-wide payload prevents the host's
      // personal logo/QR/lowerThird from polluting the persistent meeting
      // state and racing with the per-participant channel below.
      const {
        showLogo: _showLogo,
        logoUrl: _logoUrl,
        logoPosition: _logoPosition,
        showQrCode: _showQrCode,
        qrUrl: _qrUrl,
        qrPosition: _qrPosition,
        qrSize: _qrSize,
        qrColor: _qrColor,
        qrBgColor: _qrBgColor,
        qrLogo: _qrLogo,
        lowerThirdName: _lowerThirdName,
        lowerThirdTitle: _lowerThirdTitle,
        ...meetingWideBranding
      } = brandingMessage;
      meetingState?.emitBrandingUpdate?.(meetingWideBranding);
    }
    // Per-participant branding (logo/QR/personal lower third) is written by
    // every participant regardless of role, keyed on their own ID.  We must
    // key on the NUMERIC Agora UID — that's what ParticipantView uses to
    // look up `participantBranding[participantId]` on remote tiles.  Keying
    // on the raw string `localId` causes the host's QR/logo to land under a
    // string key (e.g. "kartik-patidar") that no remote tile lookup matches,
    // so guests don't see the QR/logo on the host's tile.  RTM already keys
    // by numeric UID (line ~3959), so this brings the Socket.IO channel in
    // line and lets the snapshot hydration work for late joiners too.
    const localNumericUid = stringToNumericUid(localId);
    meetingState?.emitParticipantBrandingUpdate?.(localNumericUid, brandingMessage);

    // Publish to other participants via RTM.  Only advance the dedup ref
    // after publishBrandingSettings is invoked — if the RTM call throws, we
    // want the NEXT branding change (even if the user reverted to the same
    // colors) to retry rather than being silently skipped.
    try {
      publishBrandingSettings(brandingMessage);
      lastPublishedBrandingRef.current = brandingStr;
    } catch (err) {
      console.warn('[Branding] RTM publish failed, will retry on next change:', err);
      // leave lastPublishedBrandingRef unchanged so a subsequent identical
      // setBranding call can retry
    }

    // Update local participantBranding state (for logo/QR on tiles).  Key
    // on the numeric UID for the same reason as the emit above — it keeps
    // local and remote keying consistent so other code (snapshot hydration,
    // RTM receivers) can't accidentally write a duplicate entry under a
    // different key.
    const hasBranding = (brandingMessage.showLogo && brandingMessage.logoUrl) ||
                        (brandingMessage.showQrCode && brandingMessage.qrUrl) ||
                        brandingMessage.showLowerThird;
    if (hasBranding) {
      setParticipantBranding((prev) => ({
        ...prev,
        [localNumericUid]: brandingMessage,
      }));
    } else {
      setParticipantBranding((prev) => {
        if (!prev[localNumericUid]) return prev;
        const newState = { ...prev };
        delete newState[localNumericUid];
        return newState;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    branding,
    mMeeting?.localParticipant?.id,
  ]);

  // Store publishCurrentBranding in a ref so onParticipantJoined can access it
  const publishCurrentBrandingRef = useRef(publishCurrentBranding);
  useEffect(() => {
    publishCurrentBrandingRef.current = publishCurrentBranding;
  }, [publishCurrentBranding]);

  // Hydrate from server-persisted branding for late/rejoining participants.
  // This is the durable fallback that guarantees branding is visible to a
  // guest who rejoins, independent of whether the host's RTM re-publish
  // lands before their RTM handshake completes.  Hosts skip — their own
  // picker-driven state is authoritative and must not be clobbered by the
  // snapshot they themselves wrote.
  // Track the highest branding revision we've applied from the server
  // snapshot channel so late socket-delivered updates for the same state
  // don't clobber a fresher RTM-delivered state (or vice versa).
  const appliedSnapshotRevRef = useRef(0);
  useEffect(() => {
    if (["moderator", "co-host"].includes(meetingRole)) return;
    const snap = meetingState?.snapshotBranding;
    if (!snap || Object.keys(snap).length === 0) return;
    // Reject stale server updates — if the snapshot's _rev is older than
    // the most recent one we already applied, skip it.
    const incomingRev = typeof snap._rev === 'number' ? snap._rev : 0;
    if (incomingRev && incomingRev <= appliedSnapshotRevRef.current) return;
    if (incomingRev) appliedSnapshotRevRef.current = incomingRev;
    // Strip per-participant fields. The host persists their full branding
    // (including their personal lowerThirdName/Title and logo/QR) to the
    // meeting-wide snapshot. Without this filter, guests would hydrate the
    // host's name into their own branding.lowerThirdName, which the local
    // tile then renders as if it were the guest's own name. Mirrors the
    // field selection in the BRANDING_SETTINGS pubsub receiver above.
    const sanitized = { ...snap };
    delete sanitized.lowerThirdName;
    delete sanitized.lowerThirdTitle;
    delete sanitized.showLogo;
    delete sanitized.logoUrl;
    delete sanitized.logoPosition;
    delete sanitized.showQrCode;
    delete sanitized.qrUrl;
    delete sanitized.qrPosition;
    delete sanitized.qrSize;
    delete sanitized.qrColor;
    delete sanitized.qrBgColor;
    delete sanitized.qrLogo;
    delete sanitized.senderRole;
    setBranding((prev) => ({ ...prev, ...sanitized }));
  }, [meetingState?.snapshotBranding, meetingRole, setBranding]);

  // Hydrate per-participant branding (logos/QR/personal lower third) from
  // the snapshot so rejoining guests see remote tiles correctly without
  // waiting on each remote's RTM re-publish.  Per-participant snapshot
  // entries carry a monotonic `_rev` (set by the publisher); we track the
  // highest one applied per participant and drop stale deliveries.  Without
  // this, a delayed Socket.IO snapshot can clobber a fresher RTM-delivered
  // logo position, which is the inconsistent-sync bug observed when the
  // host rapidly toggles position.  The LOCAL participant's own entry is
  // never overwritten from the snapshot — the local picker is authoritative.
  const appliedParticipantRevRef = useRef({});
  useEffect(() => {
    const snap = meetingState?.snapshotParticipantBranding;
    if (!snap || Object.keys(snap).length === 0) return;
    const localId = mMeeting?.localParticipant?.id;
    const localIdStr = localId != null ? String(localId) : null;
    setParticipantBranding((prev) => {
      const next = { ...prev };
      for (const [key, entry] of Object.entries(snap)) {
        if (localIdStr && String(key) === localIdStr) continue; // never clobber local
        const incomingRev = typeof entry?._rev === 'number' ? entry._rev : 0;
        const lastRev = appliedParticipantRevRef.current[key] || 0;
        // Entries without _rev (legacy senders) are always applied so we
        // don't regress; entries with _rev must be strictly newer.
        if (incomingRev && incomingRev <= lastRev) continue;
        if (incomingRev) appliedParticipantRevRef.current[key] = incomingRev;
        next[key] = entry;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingState?.snapshotParticipantBranding, mMeeting?.localParticipant?.id]);

  // DISPLAY_NAME_CHANGE - session-only name update visible to all participants
  const { publish: publishDisplayNameChange } = usePubSub("DISPLAY_NAME_CHANGE", {
    onMessageReceived: (data) => {
      const { senderId, message } = data;
      const localId = mMeeting?.localParticipant?.id;

      // Convert senderId to numeric UID (remote participants use numeric UIDs)
      const numericSenderId = stringToNumericUid(senderId);
      const isLocal = senderId === localId || numericSenderId === stringToNumericUid(localId);

      if (isLocal) return; // We already update local name via setLocalDisplayNameOverride

      // Parse the message (published as JSON string)
      let parsed;
      try {
        parsed = typeof message === 'string' ? JSON.parse(message) : message;
      } catch (e) {
        return;
      }

      // Update remoteUserMetadata so the name reflects everywhere (tiles + attendees list)
      if (parsed?.newName && mMeeting?.setRemoteUserMetadata) {
        mMeeting.setRemoteUserMetadata(prev => {
          const newMap = new Map(prev);
          // Update both numeric and string keys
          const existing = newMap.get(numericSenderId) || newMap.get(String(numericSenderId)) || newMap.get(senderId) || {};
          newMap.set(numericSenderId, { ...existing, displayName: parsed.newName });
          newMap.set(String(numericSenderId), { ...existing, displayName: parsed.newName });
          return newMap;
        });
      }
    },
  });

  // Store ref so onParticipantJoined can re-publish name changes for late joiners
  const publishDisplayNameChangeRef = useRef(publishDisplayNameChange);
  useEffect(() => {
    publishDisplayNameChangeRef.current = publishDisplayNameChange;
  }, [publishDisplayNameChange]);

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName, message } = data;

      const isLocal = senderId === localParticipantId;

      if (!isLocal) {
        console.log("SIDE BAR MODE CHAT", sideBarMode, sideBarModes.CHAT);
        if (sideBarMode != sideBarModes.CHAT) {
          setUnreadChat(true);
        } else {
          setUnreadChat(false);
        }

        // new Audio(
        //     `https://static.videosdk.live/prebuilt/notification.mp3`
        // ).play();

        // toast(
        //     `${trimSnackBarText(
        //         `${nameTructed(senderName, 15)} says: ${message}`
        //     )}`,
        //     {
        //         position: "bottom-left",
        //         autoClose: 4000,
        //         hideProgressBar: true,
        //         closeButton: false,
        //         pauseOnHover: true,
        //         draggable: true,
        //         progress: undefined,
        //         theme: "light",
        //     }
        // );
      }
    },
  });

  const { publish: publishMeetingSettings } = usePubSub("MEETING_SETTINGS", {
    onMessageReceived: (data) => {
      console.log("MEETING_SETTINGS", data);
      // Skip local messages — the toggle already updated state locally
      if (data.isLocal) return;
      const newSettings = data.message.data;
      setMeetingSettings(newSettings);

      // If waiting room was just disabled, admit all waiting participants (same as "Admit All" button)
      const waitingRoomNowDisabled = !newSettings?.hostApprovalRequired && !newSettings?.waitingRoom;
      if (waitingRoomNowDisabled) {
        admitAllParticipants();
      }

      // Host relay: if this client is the authenticated host, persist the settings change to DB
      // This enables guest co-hosts (who have no auth token) to toggle settings via PubSub
      if (user?.id && ["moderator", "host"].includes(meetingRole) && newSettings?.roomId) {
        meetingServices.updateMeetingSessionSettings({
          meetingId: newSettings.roomId,
          sessionId: meetingSession,
          guestCanMute: newSettings.guestCanUnmute,
          waitingRoomEnabled: newSettings.hostApprovalRequired || newSettings.waitingRoom,
        }).catch((err) => {
          console.error('Host relay: failed to persist settings from co-host:', err);
        });
      }
    },
  });

  // Listener-only: we no longer publish MUTE_MIC ourselves (Socket.IO is the
  // sole sender), but we still listen so legacy clients can mute us.
  usePubSub("MUTE_MIC", {
    onMessageReceived: async (data) => {
      const isLocalHostOrCoHost = ["moderator", "co-host"].includes(meetingRole);

      // Presenters are exempt from mute commands (like hosts/co-hosts)
      const pubsubLocalId = mMeeting?.localParticipant?.id;
      const pubsubLocalNumUid = pubsubLocalId ? stringToNumericUid(pubsubLocalId) : null;
      const isLocalPresenter = pubsubLocalId && (
        presenters?.has(pubsubLocalId) ||
        presenters?.has(String(pubsubLocalId)) ||
        (pubsubLocalNumUid != null && presenters?.has(pubsubLocalNumUid))
      );
      if (isLocalPresenter) return;

      const { participantId, muteAll, action, senderName } = data.message;

      // Handle muteAll broadcast (from RTM Monitor or host)
      // Hosts/co-hosts are exempt from mute-all (only individual mute between hosts/co-hosts is allowed)
      if (muteAll) {
        if (isLocalHostOrCoHost) return;
        if (action === 'unmute') {
          // Unmute-all: clear muted-by-host lock so participants can unmute
          console.log("MUTE_MIC: Unmute-all received via PubSub — clearing mutedByHost");
          setMutedByHost(false);
          return;
        }
        // Mute-all
        if (mutedByHost) return; // Already muted
        console.log("MUTE_MIC: Mute-all received via PubSub");
        try {
          if (mMeeting?.localMicOn) {
            await mMeeting.muteMic();
            setMicOn(false);
          }
          setMutedByHost(true);
          if (rtm?.setPresenceState) {
            await rtm.setPresenceState({ micMuted: "true" });
          }
          toast.info("Your microphone has been muted by the host.", {
            position: "bottom-left",
            autoClose: 4000,
            theme: "dark",
          });
        } catch (err) {
          console.error("Failed to mute mic on mute-all:", err);
        }
        return;
      }

      // Individual participant mute — skip if already muted by host (unless host/co-host who can always be individually muted)
      if (mutedByHost && !isLocalHostOrCoHost) return;

      const localParticipantId = mMeeting?.localParticipant?.id;

      // Compare both string ID and numeric UID (Agora uses numeric UIDs)
      const numericTargetId = typeof participantId === 'number' ? participantId : stringToNumericUid(participantId);
      const numericLocalId = stringToNumericUid(localParticipantId);
      const isMatch = participantId === localParticipantId ||
                      String(participantId) === String(localParticipantId) ||
                      numericTargetId === numericLocalId;

      // Handle individual unmute grant
      if (action === 'unmute' && isMatch) {
        console.log("MUTE_MIC: Unmute granted via PubSub for", localParticipantId);
        setMutedByHost(false);
        return;
      }

      // Only mute if this is the target participant and mic is currently ON
      if (isMatch && mMeeting?.localMicOn) {
        // Hosts/co-hosts can be muted by other hosts/co-hosts, but they can freely unmute
        const lockUnmute = !isLocalHostOrCoHost;
        console.log(`MUTE_MIC: Muting local mic (lockUnmute=${lockUnmute})`);
        try {
          await mMeeting.muteMic();  // AWAIT the actual mute
          setMicOn(false);
          if (lockUnmute) setMutedByHost(true);
          // Confirm via presence state
          if (rtm?.setPresenceState) {
            await rtm.setPresenceState({ micMuted: "true" });
          }
          toast.info(`Your microphone has been muted by ${senderName || 'the host'}.`, {
            position: "bottom-left",
            autoClose: 4000,
            theme: "dark",
          });
        } catch (err) {
          console.error("Failed to mute mic:", err);
        }
      }
    },
  });

  // Listen for disable webcam requests from host/co-host
  usePubSub("DISABLE_WEBCAM", {
    onMessageReceived: (data) => {
      console.log("DISABLE_WEBCAM received:", data);

      // Moderators and co-hosts should NEVER have webcam disabled by PubSub commands
      if (["moderator", "co-host"].includes(meetingRole)) {
        console.log("DISABLE_WEBCAM: Ignoring - local user is moderator/co-host");
        return;
      }

      const localParticipantId = mMeeting?.localParticipant?.id;
      const { participantId } = data.message;

      // Compare both string ID and numeric UID (Agora uses numeric UIDs)
      const numericTargetId = typeof participantId === 'number' ? participantId : stringToNumericUid(participantId);
      const numericLocalId = stringToNumericUid(localParticipantId);
      const isMatch = participantId === localParticipantId ||
                      String(participantId) === String(localParticipantId) ||
                      numericTargetId === numericLocalId;

      console.log("DISABLE_WEBCAM comparison:", {
        participantId,
        localParticipantId,
        numericTargetId,
        numericLocalId,
        isMatch,
        localWebcamOn: mMeeting?.localWebcamOn
      });

      if (isMatch && mMeeting?.localWebcamOn) {
        console.log("DISABLE_WEBCAM: Disabling local webcam");
        mMeeting.disableWebcam();
        setWebcamOn(false);
        setCameraOffByHost(true); // Track that user's camera was disabled by host
        toast.info("Your camera has been disabled by the host. You can enable it when the host allows.", {
          position: "bottom-left",
          autoClose: 4000,
          theme: "dark",
        });
      }
    },
  });

  // Listen for stop screen share requests from host/co-host
  usePubSub("STOP_SCREEN_SHARE", {
    onMessageReceived: (data) => {
      console.log("STOP_SCREEN_SHARE received:", data);

      const localParticipantId = mMeeting?.localParticipant?.id;
      const { participantId } = data.message;

      // Compare both string ID and numeric UID (Agora uses numeric UIDs)
      // presenterId for remote shares is a numeric UID (number or numeric string),
      // while localParticipantId is the original string ID. Handle both cases:
      const numericLocalId = stringToNumericUid(localParticipantId);
      const parsedTargetId = typeof participantId === 'number' ? participantId : Number(participantId);
      const numericTargetId = typeof participantId === 'number' ? participantId : stringToNumericUid(participantId);
      const isMatch = participantId === localParticipantId ||
                      String(participantId) === String(localParticipantId) ||
                      numericTargetId === numericLocalId ||
                      (!isNaN(parsedTargetId) && parsedTargetId === numericLocalId);

      console.log("STOP_SCREEN_SHARE comparison:", {
        participantId,
        localParticipantId,
        parsedTargetId,
        numericTargetId,
        numericLocalId,
        isMatch,
        presenterId: mMeeting?.presenterId
      });

      // Check if local user is the current presenter
      // presenterId can be string participantId OR numeric UID (from user-published/polling)
      const currentPresenterId = mMeeting?.presenterId;
      const isLocalPresenter = currentPresenterId === localParticipantId ||
                               String(currentPresenterId) === String(numericLocalId) ||
                               Number(currentPresenterId) === numericLocalId;

      // Only stop screen share if this is the target participant and they are currently presenting
      if (isMatch && isLocalPresenter) {
        console.log("STOP_SCREEN_SHARE: Stopping local screen share");
        mMeeting.toggleScreenShare();
        toast.info("Your screen share has been stopped by the host", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "dark",
        });
      }
    },
  });

  // Listen for spotlight participant events (from chat Feature/Spotlight button)
  usePubSub("SPOTLIGHT_PARTICIPANT", {
    onMessageReceived: (data) => {
      try {
        const message = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        const { participantId, action, displayName } = message;

        console.log("SPOTLIGHT_PARTICIPANT received:", { participantId, action, displayName });

        if (participantId) {
          // For Agora, use mMeeting.pin/unpin instead of participant.pin/unpin
          // The pin/unpin methods on individual participants are stubs in Agora context
          if (action === 'spotlight') {
            // Use mMeeting.pin() which updates pinnedParticipants state
            mMeeting?.pin?.(participantId);
            // Auto-switch to focus view when someone is spotlighted — but
            // never override an explicit recorder layout choice from the modal.
            if (!isRecorderLayoutPinned) {
              setSelectedLayout('focus');
            }
            toast.info(`${displayName || 'Participant'} is now spotlighted`, {
              position: "bottom-left",
              autoClose: 2000,
            });
            console.log("Spotlight applied to:", participantId);
          } else if (action === 'unspotlight') {
            // Use mMeeting.unpin() which removes from pinnedParticipants state
            mMeeting?.unpin?.(participantId);
            toast.info(`Spotlight removed from ${displayName || 'participant'}`, {
              position: "bottom-left",
              autoClose: 2000,
            });
            console.log("Spotlight removed from:", participantId);
          }
        }
      } catch (error) {
        console.error("Error handling spotlight event:", error);
      }
    },
  });

  // Listen for shadow ban events to sync across ALL participants
  // This allows filtering of shadow banned messages on the client side
  const { messages: persistedShadowBans } = usePubSub("SHADOW_BAN", {
    onMessageReceived: (data) => {
      try {
        const message = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        const { participantId, action, displayName } = message;

        console.log("SHADOW_BAN received", { participantId, action, displayName });

        if (participantId) {
          setShadowBannedParticipants(prev => {
            const newSet = new Set(prev);
            const strId = String(participantId);
            // Compute the alternate form: if this looks numeric, find string IDs that hash to it, and vice versa
            const numericUid = String(stringToNumericUid(strId));
            if (action === 'ban') {
              newSet.add(strId);
              // Also add numeric UID so attendees panel (which uses numeric participant.id) matches
              if (numericUid && numericUid !== '0' && numericUid !== strId) newSet.add(numericUid);
            } else if (action === 'unban') {
              newSet.delete(participantId);
              newSet.delete(strId);
              // Also remove numeric UID form
              if (numericUid && numericUid !== '0') newSet.delete(numericUid);
              // Remove any entry whose stringToNumericUid matches this ID (reverse lookup)
              // This handles: unban numeric UID → also remove the original string ID
              for (const existingId of newSet) {
                if (String(stringToNumericUid(existingId)) === strId) {
                  newSet.delete(existingId);
                }
              }
            }
            console.log("Shadow ban state after update:", [...newSet]);
            return newSet;
          });
        }
      } catch (error) {
        console.error("Error handling shadow ban event:", error);
      }
    },
  });

  // Load persisted shadow ban messages when joining the meeting
  // This ensures participants who join later know about existing shadow bans
  useEffect(() => {
    if (persistedShadowBans && persistedShadowBans.length > 0) {
      console.log("Loading persisted shadow bans:", persistedShadowBans.length);

      // Build the shadow ban Set from persisted messages
      // Process in order to handle ban/unban sequences correctly
      const shadowBanSet = new Set();

      persistedShadowBans.forEach((msg) => {
        try {
          const message = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
          const { participantId, action } = message;

          if (participantId) {
            const strId = String(participantId);
            const numericUid = String(stringToNumericUid(strId));
            if (action === 'ban') {
              shadowBanSet.add(strId);
              if (numericUid && numericUid !== '0' && numericUid !== strId) shadowBanSet.add(numericUid);
            } else if (action === 'unban') {
              shadowBanSet.delete(participantId);
              shadowBanSet.delete(strId);
              if (numericUid && numericUid !== '0') shadowBanSet.delete(numericUid);
              for (const existingId of shadowBanSet) {
                if (String(stringToNumericUid(existingId)) === strId) {
                  shadowBanSet.delete(existingId);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error parsing persisted shadow ban:", error);
        }
      });

      if (shadowBanSet.size > 0) {
        console.log("Setting shadow banned participants from persisted:", [...shadowBanSet]);
        setShadowBannedParticipants(shadowBanSet);
      }
    }
  }, [persistedShadowBans, setShadowBannedParticipants]);

  // Listen for MAKE_PRESENTER events - allows attendees to share screen
  // This is separate from SPOTLIGHT_PARTICIPANT which only highlights video
  const { messages: persistedPresenters } = usePubSub("MAKE_PRESENTER", {
    onMessageReceived: (data) => {
      try {
        const message = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        const { participantId, action, displayName } = message;

        console.log("MAKE_PRESENTER received", { participantId, action, displayName });

        if (participantId) {
          // Only store original ID and string version (not numeric hash to avoid collisions)
          setPresenters(prev => {
            const newSet = new Set(prev);
            if (action === 'add') {
              newSet.add(participantId);
              newSet.add(String(participantId));
            } else if (action === 'remove') {
              newSet.delete(participantId);
              newSet.delete(String(participantId));
            }
            console.log("Presenters state after update:", [...newSet]);
            return newSet;
          });

          // For ID comparison, convert to numeric UID
          const numericTargetId = typeof participantId === 'number' ? participantId : stringToNumericUid(participantId);

          // Check if this is about the local participant
          // Use the same robust comparison pattern as DISABLE_WEBCAM and STOP_SCREEN_SHARE handlers
          const localParticipantId = mMeeting?.localParticipant?.id;
          const numericLocalId = stringToNumericUid(localParticipantId);
          const isAboutMe = localParticipantId && (
            participantId === localParticipantId ||
            String(participantId) === String(localParticipantId) ||
            numericTargetId === numericLocalId
          );

          console.log("MAKE_PRESENTER isAboutMe check:", {
            participantId,
            localParticipantId,
            numericTargetId,
            numericLocalId,
            isAboutMe
          });

          // Show toast only to the presenter being affected (not to other participants)
          if (isAboutMe) {
            if (action === 'add') {
              // Clear mutedByHost so the presenter can unmute themselves
              setMutedByHost(false);
              toast.success(`You can now share your screen!`, {
                position: "bottom-left",
                autoClose: 4000,
                theme: "dark",
              });
            } else if (action === 'remove') {
              toast.warning(`Your screen sharing permission has been removed`, {
                position: "bottom-left",
                autoClose: 4000,
                theme: "dark",
              });
              // If the local participant is currently sharing screen, stop it
              if (mMeeting?.localScreenShareOn) {
                console.log("Stopping screen share - presenter permission removed");
                mMeeting?.toggleScreenShare();
              }
            }
          }
        }
      } catch (error) {
        console.error("Error handling MAKE_PRESENTER event:", error);
      }
    },
  });

  // Load persisted presenter messages when joining the meeting
  // Load persisted presenter messages when joining the meeting
  useEffect(() => {
    if (persistedPresenters && persistedPresenters.length > 0) {
      console.log("Loading persisted presenters:", persistedPresenters.length);

      const presenterSet = new Set();
      persistedPresenters.forEach(msg => {
        try {
          const message = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
          const { participantId, action } = message;

          if (participantId) {
            // Only store original ID and string version (not numeric hash to avoid collisions)
            if (action === 'add') {
              presenterSet.add(participantId);
              presenterSet.add(String(participantId));
            } else if (action === 'remove') {
              presenterSet.delete(participantId);
              presenterSet.delete(String(participantId));
            }
          }
        } catch (error) {
          console.error("Error parsing persisted presenter:", error);
        }
      });

      if (presenterSet.size > 0) {
        console.log("Setting presenters from persisted:", [...presenterSet]);
        setPresenters(presenterSet);

        // If the local user is a presenter, clear mutedByHost so they can unmute
        const localId = mMeeting?.localParticipant?.id;
        if (localId && (presenterSet.has(localId) || presenterSet.has(String(localId)))) {
          console.log('[Presenter] Local user is a presenter from persisted state — clearing mutedByHost');
          setMutedByHost(false);
        }
      }
    }
  }, [persistedPresenters, setPresenters]);

  // Publish and listen for microphone permission requests
  const { publish: publishMicPermissionRequest } = usePubSub("MIC_PERMISSION_REQUEST", {
    onMessageReceived: (data) => {
      console.log("MIC_PERMISSION_REQUEST received:", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const { targetParticipantId, granted } = data.message;

      // Handle Agora ID comparison (string vs numeric UID)
      const numericTargetId = typeof targetParticipantId === 'number' ? targetParticipantId : stringToNumericUid(targetParticipantId);
      const numericLocalId = stringToNumericUid(localParticipantId);
      const isTargetMe = targetParticipantId === localParticipantId ||
                         String(targetParticipantId) === String(localParticipantId) ||
                         numericTargetId === numericLocalId;

      console.log("MIC_PERMISSION_REQUEST ID check:", {
        targetParticipantId,
        localParticipantId,
        numericTargetId,
        numericLocalId,
        isTargetMe,
        granted
      });

      if (isTargetMe && granted) {
        console.log("Showing mic permission modal for local participant");
        setMutedByHost(false); // Clear muted by host flag - host has granted permission to unmute
        setShowMicPermissionModal(true);
      }
    },
  });

  // Publish for unmute permission
  const { publish: publishUnmutePermission } = usePubSub("PARTICIPANT_UNMUTE_PERMISSION");

  // Listen for video permission requests
  usePubSub("VIDEO_PERMISSION_REQUEST", {
    onMessageReceived: (data) => {
      console.log("VIDEO_PERMISSION_REQUEST received:", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const { targetParticipantId, granted } = data.message;

      // Handle Agora ID comparison (string vs numeric UID)
      const numericTargetId = typeof targetParticipantId === 'number' ? targetParticipantId : stringToNumericUid(targetParticipantId);
      const numericLocalId = stringToNumericUid(localParticipantId);
      const isTargetMe = targetParticipantId === localParticipantId ||
                         String(targetParticipantId) === String(localParticipantId) ||
                         numericTargetId === numericLocalId;

      console.log("VIDEO_PERMISSION_REQUEST ID check:", {
        targetParticipantId,
        localParticipantId,
        numericTargetId,
        numericLocalId,
        isTargetMe,
        granted
      });

      if (isTargetMe && granted) {
        console.log("Showing video permission modal for local participant");
        setCameraOffByHost(false); // Clear camera off by host flag - host has granted permission to enable camera
        setShowVideoPermissionModal(true);
      }
    },
  });

  // Helper function to convert string ID to numeric UID (same logic as AgoraContext)
  const stringToNumericUid = (str) => {
    if (!str) return 0;
    if (typeof str === 'number') return str;
    // Convert string to a numeric hash (positive integer)
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  // Publish function for role changes (co-host management)
  const { publish: publishRoleChange, messages: persistedRoleChanges } = usePubSub("ROLE_CHANGE", {
    onMessageReceived: (data) => {
      console.log("=== ROLE_CHANGE received ===");
      console.log("ROLE_CHANGE full data:", JSON.stringify(data, null, 2));

      const localParticipantId = mMeeting?.localParticipant?.id;
      const message = data.message || {};
      const { targetParticipantId, newRole, action } = message;

      console.log("ROLE_CHANGE parsed:", {
        targetParticipantId,
        targetType: typeof targetParticipantId,
        localParticipantId,
        localType: typeof localParticipantId,
        action,
        newRole
      });

      // IMPORTANT: Agora ID mismatch fix
      // - Local participant stores their original string ID (e.g., "user-abc123")
      // - Remote participants see the local user with their numeric Agora UID (e.g., 123456789)
      // - When host sends ROLE_CHANGE, they send the numeric UID
      // - So we need to compare both the string ID AND the numeric UID
      const targetStr = targetParticipantId != null ? String(targetParticipantId) : null;
      const localStr = localParticipantId != null ? String(localParticipantId) : null;
      const localNumericUid = localParticipantId ? stringToNumericUid(localParticipantId) : null;
      const localNumericStr = localNumericUid != null ? String(localNumericUid) : null;

      // Check if target matches either our string ID or our numeric UID
      const isTargetMe = targetStr && (
        (localStr && targetStr === localStr) ||
        (localNumericStr && targetStr === localNumericStr)
      );

      console.log("ROLE_CHANGE ID comparison:", {
        targetStr,
        localStr,
        localNumericStr,
        isTargetMe,
        stringMatch: targetStr === localStr,
        numericMatch: targetStr === localNumericStr
      });

      // Update local co-hosts list based on action
      if (action === 'promote') {
        console.log("ROLE_CHANGE: Calling addCoHost for:", targetParticipantId);
        addCoHost(targetParticipantId);
        // If this user was promoted to co-host, update their local meetingRole
        if (isTargetMe) {
          console.log("*** ROLE_CHANGE: THIS IS ME! Promoting self to co-host ***");
          // Use startTransition to prevent blocking audio thread during re-render cascade
          startTransition(() => {
            setMeetingRole('co-host');
          });
          // Update RTM presence so monitor sees the role change
          if (rtm?.setPresenceState) {
            rtm.setPresenceState({ role: 'co-host' });
          }
          toast.success("You have been promoted to Co-Host!", {
            position: "bottom-left",
            autoClose: 3000,
            theme: "dark",
          });
        } else {
          console.log("ROLE_CHANGE: Not me, skipping self promotion");
        }
      } else if (action === 'demote') {
        console.log("ROLE_CHANGE: Calling removeCoHost for:", targetParticipantId);
        removeCoHost(targetParticipantId);
        // If this user was demoted from co-host, update their local meetingRole
        if (isTargetMe) {
          console.log("*** ROLE_CHANGE: THIS IS ME! Demoting self from co-host ***");
          // Use startTransition to prevent blocking audio thread during re-render cascade
          startTransition(() => {
            setMeetingRole('participant');
          });
          // Update RTM presence so monitor sees the role change
          if (rtm?.setPresenceState) {
            rtm.setPresenceState({ role: 'participant' });
          }
          toast.info("You have been removed from Co-Host role.", {
            position: "bottom-left",
            autoClose: 3000,
            theme: "dark",
          });
        } else {
          console.log("ROLE_CHANGE: Not me, skipping self demotion");
        }
      } else {
        console.log("ROLE_CHANGE: No valid action found:", action);
      }
    },
  });

  // Load persisted role change messages when joining the meeting
  // This ensures participants who join later see the current co-hosts
  useEffect(() => {
    if (persistedRoleChanges && persistedRoleChanges.length > 0) {
      console.log("Loading persisted role changes:", persistedRoleChanges.length);
      const localParticipantId = mMeeting?.localParticipant?.id;

      // Build the co-hosts Set from persisted messages
      // Process in order to handle promote/demote sequences correctly
      const coHostsSet = new Set();

      persistedRoleChanges.forEach((msg) => {
        try {
          const message = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
          const { targetParticipantId, action } = message;

          if (targetParticipantId) {
            if (action === 'promote') {
              coHostsSet.add(targetParticipantId);
            } else if (action === 'demote') {
              coHostsSet.delete(targetParticipantId);
            }
          }
        } catch (error) {
          console.error("Error parsing persisted role change:", error);
        }
      });

      if (coHostsSet.size > 0) {
        console.log("Setting co-hosts from persisted:", [...coHostsSet]);
        // Calculate numeric UID for comparison
        const localNumericUid = localParticipantId ? stringToNumericUid(localParticipantId) : null;

        // Add each co-host
        coHostsSet.forEach((participantId) => {
          addCoHost(participantId);
          // If this user is a co-host, update their local meetingRole
          // Compare both string ID and numeric UID (same fix as ROLE_CHANGE handler)
          const targetStr = String(participantId);
          const localStr = localParticipantId ? String(localParticipantId) : null;
          const localNumericStr = localNumericUid != null ? String(localNumericUid) : null;

          const isMe = localParticipantId && (
            targetStr === localStr ||
            targetStr === localNumericStr
          );

          if (isMe) {
            console.log("Self is co-host from persisted messages, updating meetingRole");
            startTransition(() => {
              setMeetingRole('co-host');
            });
          }
        });
      }
    }
  }, [persistedRoleChanges, mMeeting?.localParticipant?.id, addCoHost, setMeetingRole]);

  // KICK_PARTICIPANT handler - removes participant from meeting when host kicks them
  usePubSub("KICK_PARTICIPANT", {
    onMessageReceived: (data) => {
      console.log("KICK_PARTICIPANT received:", data);
      const localParticipantId = mMeeting?.localParticipant?.id;

      try {
        const messageData = typeof data.message === 'string'
          ? JSON.parse(data.message)
          : data.message;
        const { targetParticipantId, kickedBy } = messageData;

        // Check if this participant is the one being kicked
        // Use same ID comparison logic as ROLE_CHANGE (compare both string and numeric UID)
        const targetStr = targetParticipantId != null ? String(targetParticipantId) : null;
        const localStr = localParticipantId != null ? String(localParticipantId) : null;
        const localNumericUid = localParticipantId ? stringToNumericUid(localParticipantId) : null;
        const localNumericStr = localNumericUid != null ? String(localNumericUid) : null;

        const isTargetMe = targetStr && (
          (localStr && targetStr === localStr) ||
          (localNumericStr && targetStr === localNumericStr)
        );

        if (isTargetMe) {
          console.log("This participant is being kicked by:", kickedBy);

          // Show notification
          toast.error(`You have been removed from the meeting by ${kickedBy || 'the host'}.`, {
            position: "top-center",
            autoClose: 5000,
            theme: "dark",
          });

          // Leave the meeting after a short delay to show the toast
          setTimeout(() => {
            mMeeting?.leave();
            leaveOrRedirect();
          }, 1500);
        }
      } catch (error) {
        console.error("Error processing KICK_PARTICIPANT message:", error);
      }
    },
  });

  // Handlers for accepting/declining microphone permission
  const handleAcceptMicPermission = async () => {
    if (mMeeting && !mMeeting.localMicOn) {
      await mMeeting.unmuteMic();
      setMicOn(true);
      if (rtm?.setPresenceState) {
        await rtm.setPresenceState({ micMuted: "false" });
      }
    }
    setShowMicPermissionModal(false);
  };

  const handleDeclineMicPermission = () => {
    setShowMicPermissionModal(false);
  };

  // Handlers for accepting/declining video permission
  const handleAcceptVideoPermission = () => {
    if (mMeeting && !mMeeting.localWebcamOn) {
      mMeeting.enableWebcam();
      setWebcamOn(true);
    }
    setShowVideoPermissionModal(false);
  };

  const handleDeclineVideoPermission = () => {
    setShowVideoPermissionModal(false);
  };

  // Handler for toggling captions (uses Agora STT, falls back to browser Web Speech API)
  const handleToggleCaptions = async (enable) => {
    if (enable) {
      const meetingId = mMeeting?.meetingId;
      // [NEW] Guard against starting captions with an unsupported spoken language.
      if (!isAgoraSttTranscribable(spokenLanguage)) {
        const badLabel = getLanguageLabel(spokenLanguage);
        toast.warning(`${badLabel} is not supported for speech recognition. Reverted to English. Please pick a supported language for spoken audio.`);
        setRestrictSpokenToAgora(true);
        setSpokenLanguage('en');
        // Don't proceed — the language change effect will start STT once
        // spokenLanguage settles to 'en'.
        return;
      }
      console.log('[Captions] Starting captions with languages:', spokenLanguage, '->', captionLanguage, 'meetingId:', meetingId);

      // If AI captions are active, stop them first to avoid conflicts
      if (aiCaptionsEnabled) {
        aiCaptionSession?.stop();
        setAICaptionSession(null);
        setAICaptionsEnabled(false);
        aiCaptionsEnabledRef.current = false;
        setAICaptions([]);
        console.log('[Captions] Stopped AI captions before starting regular captions');
      }

      setCaptionsEnabled(true);

      // Try Agora STT first (server-side, more reliable)
      if (meetingId) {
        try {
          const agoraSttLang = toAgoraSttLanguage(spokenLanguage);
          const sttOptions = {};
          // Send only the user's selected caption language as translation target
          const allTargets = buildAllTargetsTranslateConfig(spokenLanguage, captionLanguage);
          if (allTargets) {
            sttOptions.translateConfig = allTargets;
          }
          // If STT is already running without translateConfig, force restart
          if (sttAgentId && !sttHasTranslateRef.current) {
            sttOptions.forceRestart = true;
            sttIntentionalRestartRef.current = true;
          }
          const result = await startStt(meetingId, [agoraSttLang], sttOptions);
          sttIntentionalRestartRef.current = false;

          if (result.success) {
            const botUid = Number(result.pubBotUid);
            setSttPubBotUid(botUid);
            sttPubBotUidRef.current = botUid;
            setSttAgentId(result.agentId);
            sttHasTranslateRef.current = allTargets ? new Set(allTargets.languages[0].target) : null;
            // Agora STT is working — disable Google Translate fallback
            setUseGoogleTranslateFallback(false);
            useGoogleTranslateFallbackRef.current = false;
            // [NEW] Reset Agora-translate fallback so we start fresh with
            // Google Translate as the primary translator for this session.
            setUseAgoraTranslateFallback(false);
            useAgoraTranslateFallbackRef.current = false;
            console.log('[Captions] Agora STT started, pubBotUid:', botUid, 'agentId:', result.agentId);
            toast.success(result.alreadyRunning ? 'Captions enabled (session active)' : 'Captions started');
            return;
          }
        } catch (error) {
          console.error('[Captions] Agora STT failed, falling back to browser:', error);
          // Mark that we need Google Translate for translation since Agora is unavailable
          setUseGoogleTranslateFallback(true);
          useGoogleTranslateFallbackRef.current = true;
        }
      }

      // Fallback: browser Web Speech API
      console.log('[Captions] Using browser speech recognition fallback');
      const session = createCaptionSession({
        spokenLanguage,
        captionLanguage,
        onCaption: (caption) => {
          if (caption.isFinal) {
            setLiveCaptions(prev => [...prev.slice(-10), caption]);
          } else {
            setLiveCaptions(prev => {
              const updated = [...prev];
              if (updated.length > 0 && !updated[updated.length - 1].isFinal) {
                updated[updated.length - 1] = caption;
              } else {
                updated.push(caption);
              }
              return updated.slice(-10);
            });
          }
        },
        onError: (error) => {
          console.error('[Captions] Browser speech error:', error);
          toast.error(error);
        }
      });

      if (!session.isSupported) {
        toast.error('Speech recognition is not supported in this browser');
        setCaptionsEnabled(false);
        return;
      }

      session.start();
      setCaptionSession(session);
      toast.warning('Using browser speech recognition (server STT unavailable)');
    } else {
      // Stop captions
      const meetingId = mMeeting?.meetingId;

      // Stop Agora STT agent if running
      if (meetingId && sttAgentId) {
        sttIntentionalRestartRef.current = true;
        try {
          await stopStt(meetingId);
          console.log('[Captions] Agora STT stopped');
        } catch (error) {
          console.error('[Captions] Error stopping Agora STT:', error);
        }
        sttIntentionalRestartRef.current = false;
      }
      setSttPubBotUid(null);
      sttPubBotUidRef.current = null;
      setSttAgentId(null);
      sttHasTranslateRef.current = null;

      // Stop browser fallback if running
      captionSession?.stop();
      setCaptionSession(null);

      setCaptionsEnabled(false);
      setLiveCaptions([]);
    }
  };

  // Update caption session when languages change (browser fallback only)
  useEffect(() => {
    if (captionsEnabled && captionSession) {
      captionSession.updateLanguages?.(spokenLanguage, captionLanguage);
    }
  }, [spokenLanguage, captionLanguage, captionsEnabled, captionSession]);

  // When language changes, restart STT with new translateConfig so translation activates immediately.
  // Also auto-enables captions if the user picked a different caption language (implying they want translation).
  const prevSpokenRef = useRef(spokenLanguage);
  const prevCaptionRef = useRef(captionLanguage);
  useEffect(() => {
    const spokenChanged = prevSpokenRef.current !== spokenLanguage;
    const captionChanged = prevCaptionRef.current !== captionLanguage;
    prevSpokenRef.current = spokenLanguage;
    prevCaptionRef.current = captionLanguage;

    const meetingId = mMeeting?.meetingId;
    if (!(spokenChanged || captionChanged) || !meetingId) return;

    // [NEW] Guard: if the user just picked a spoken language that Agora STT
    // cannot transcribe, auto-revert to English and narrow the spoken-language
    // picker to the Agora-supported subset. The state change re-fires this
    // effect with spokenLanguage='en', so we just bail out here.
    if (spokenChanged && !isAgoraSttTranscribable(spokenLanguage)) {
      const badLabel = getLanguageLabel(spokenLanguage);
      console.warn('[Captions] Spoken language not supported by Agora STT:', spokenLanguage, '— reverting to English');
      toast.warning(`${badLabel} is not supported for speech recognition. Reverted to English. Please pick a supported language for spoken audio.`);
      setRestrictSpokenToAgora(true);
      setSpokenLanguage('en');
      return;
    }

    let captionsActive = captionsEnabledRef.current || aiCaptionsEnabledRef.current;
    let wasAutoEnabled = false;

    // If captions aren't on and the user just picked a different caption language,
    // auto-enable captions so translation starts immediately without needing a CC toggle.
    if (!captionsActive && captionChanged && captionLanguage !== spokenLanguage) {
      console.log('[Captions] Caption language changed to', captionLanguage, '— auto-enabling AI captions');
      setAICaptionsEnabled(true);
      aiCaptionsEnabledRef.current = true;
      captionsActive = true;
      wasAutoEnabled = true;
    }

    // Clear old captions so stale text from previous language doesn't linger
    setLiveCaptions([]);
    setAICaptions([]);

    // Update browser fallback sessions if active (these are per-participant)
    if (captionSession) {
      captionSession.updateLanguages?.(spokenLanguage, captionLanguage);
    }
    if (aiCaptionSession) {
      aiCaptionSession.updateLanguages?.(spokenLanguage, captionLanguage);
    }

    // Determine if we need to (re)start the Agora STT bot.
    // Spoken language change always requires a restart (different transcription language).
    // Caption language change requires a restart if:
    //   - STT has no translateConfig (notetaker auto-start), OR
    //   - The new caption language is NOT in the current STT target set (Agora 10-lang limit)
    const needsTranslation = captionLanguage !== spokenLanguage;
    const captionLangCode = toAgoraSttLanguage(captionLanguage);
    const captionLangCovered = sttHasTranslateRef.current && sttHasTranslateRef.current.has(captionLangCode);
    const sttNeedsRestart = spokenChanged ||
      (captionChanged && needsTranslation && sttAgentId && !captionLangCovered);
    const sttNeedsStart = captionChanged && (wasAutoEnabled || !sttAgentId);

    if ((sttNeedsRestart || sttNeedsStart) && captionsActive) {
      const restartOrStartStt = async () => {
        try {
          const agoraSttLang = toAgoraSttLanguage(spokenLanguage);
          const sttOptions = {};
          const allTargets = buildAllTargetsTranslateConfig(spokenLanguage, captionLanguage);
          if (allTargets) {
            sttOptions.translateConfig = allTargets;
          }
          // Force restart if STT is already running (spoken change or language not in targets)
          if (sttAgentId && (sttNeedsRestart || !captionLangCovered)) {
            sttOptions.forceRestart = true;
            sttIntentionalRestartRef.current = true;
          }
          console.log('[Captions] Language changed, (re)starting STT:', spokenLanguage, '→', captionLanguage,
            sttOptions.forceRestart ? '(forceRestart)' : '(new start)');
          const result = await startStt(meetingId, [agoraSttLang], sttOptions);
          sttIntentionalRestartRef.current = false;
          if (result.success) {
            const botUid = Number(result.pubBotUid);
            setSttPubBotUid(botUid);
            sttPubBotUidRef.current = botUid;
            setSttAgentId(result.agentId);
            sttHasTranslateRef.current = allTargets ? new Set(allTargets.languages[0].target) : null;
            setUseGoogleTranslateFallback(false);
            useGoogleTranslateFallbackRef.current = false;
            // [NEW] Reset Agora-translate fallback on each restart so the
            // session re-tries Google Translate first after language changes.
            setUseAgoraTranslateFallback(false);
            useAgoraTranslateFallbackRef.current = false;
            console.log('[Captions] STT (re)started after language change, pubBotUid:', botUid);
          }
        } catch (err) {
          sttIntentionalRestartRef.current = false;
          console.error('[Captions] Failed to (re)start STT after language change:', err);
          setUseGoogleTranslateFallback(true);
          useGoogleTranslateFallbackRef.current = true;
        }
      };
      restartOrStartStt();
    }
    // If only captionChanged, captions already active with translateConfig — no restart needed.
    // The stream-message handler reads captionLanguageRef.current to filter translations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spokenLanguage, captionLanguage]);

  // Handler for toggling AI Captions (Agora STT powered)
  const handleToggleAICaptions = async () => {
    if (!aiCaptionsEnabled) {
      const meetingId = mMeeting?.meetingId;
      if (!meetingId) {
        toast.error('Cannot start captions: not connected to a meeting');
        return;
      }
      // [NEW] Guard against starting AI captions with an unsupported spoken language.
      if (!isAgoraSttTranscribable(spokenLanguage)) {
        const badLabel = getLanguageLabel(spokenLanguage);
        toast.warning(`${badLabel} is not supported for speech recognition. Reverted to English. Please pick a supported language for spoken audio.`);
        setRestrictSpokenToAgora(true);
        setSpokenLanguage('en');
        return;
      }

      // If the other caption system is active, stop it first to avoid conflicts
      if (captionsEnabled) {
        captionSession?.stop();
        setCaptionSession(null);
        setCaptionsEnabled(false);
        captionsEnabledRef.current = false;
        setLiveCaptions([]);
        console.log('[AI Captions] Stopped regular captions before starting AI captions');
      }

      console.log('[AI Captions] Starting Agora STT for channel:', meetingId, 'language:', spokenLanguage, 'caption:', captionLanguage);
      setAICaptionsEnabled(true);

      try {
        const agoraSttLang = toAgoraSttLanguage(spokenLanguage);
        const sttOptions = {};
        // Include target languages with user's captionLanguage prioritized.
        // Agora limits to 10 targets — priority ensures selected language always works.
        const allTargets = buildAllTargetsTranslateConfig(spokenLanguage, captionLanguage);
        if (allTargets) {
          sttOptions.translateConfig = allTargets;
        }
        // If STT is already running without translateConfig (e.g. notetaker auto-start),
        // force restart so translation messages are sent for caption language changes.
        if (sttAgentId && !sttHasTranslateRef.current) {
          sttOptions.forceRestart = true;
          sttIntentionalRestartRef.current = true;
          console.log('[AI Captions] Forcing STT restart to enable translateConfig');
        }
        const result = await startStt(meetingId, [agoraSttLang], sttOptions);
        sttIntentionalRestartRef.current = false;

        if (result.success) {
          const botUid = Number(result.pubBotUid);
          setSttPubBotUid(botUid);
          sttPubBotUidRef.current = botUid;
          setSttAgentId(result.agentId);
          sttHasTranslateRef.current = allTargets ? new Set(allTargets.languages[0].target) : null;
          // Agora STT is working — disable Google Translate fallback
          setUseGoogleTranslateFallback(false);
          useGoogleTranslateFallbackRef.current = false;
          // [NEW] Reset Agora-translate fallback so Google Translate is the
          // primary translator for this fresh AI Captions session.
          setUseAgoraTranslateFallback(false);
          useAgoraTranslateFallbackRef.current = false;
          console.log('[AI Captions] Agora STT started, pubBotUid:', botUid, 'agentId:', result.agentId);
          if (result.alreadyRunning) {
            toast.success('AI Captions enabled (session already active)');
          } else {
            toast.success('AI Captions started');
          }
        } else {
          throw new Error('STT start returned unsuccessful');
        }
      } catch (error) {
        console.error('[AI Captions] Failed to start Agora STT:', error);
        setAICaptionsEnabled(false);
        // Fallback to Web Speech API for transcription
        console.log('[AI Captions] Falling back to Web Speech API...');
        const session = createAICaptionSession({
          spokenLanguage,
          captionLanguage: spokenLanguage, // transcribe in spoken language; we translate below
          onCaption: (caption) => {
            if (!caption.isFinal) return; // skip interim when translating
            // Use refs to always get the latest language values (avoids stale closure)
            const currentSpoken = spokenLanguageRef.current;
            const currentCaption = captionLanguageRef.current;
            if (currentCaption !== currentSpoken) {
              // Agora unavailable — use Google Translate as fallback
              translateTextLocally(caption.text, currentSpoken, currentCaption).then(translated => {
                setAICaptions(prev => [...prev.slice(-10), {
                  ...caption,
                  text: translated,
                  originalText: caption.text,
                  wasTranslated: translated !== caption.text,
                  translatedBy: 'google',
                }]);
              });
            } else {
              setAICaptions(prev => [...prev.slice(-10), caption]);
            }
          },
          onError: (err) => {
            console.error('[AI Captions] Fallback error:', err);
            toast.error(err);
          }
        });

        if (session.isSupported) {
          session.start();
          setAICaptionSession(session);
          setAICaptionsEnabled(true);
          // Mark that we are using Google Translate fallback for translation
          setUseGoogleTranslateFallback(true);
          useGoogleTranslateFallbackRef.current = true;
          toast.warning('Using browser speech recognition (Agora STT unavailable)');
        } else {
          toast.error('AI Captions are not available. Please try again later.');
        }
      }
    } else {
      // Stop AI captions
      const meetingId = mMeeting?.meetingId;
      if (meetingId && sttAgentId) {
        sttIntentionalRestartRef.current = true;
        try {
          await stopStt(meetingId);
          console.log('[AI Captions] Agora STT stopped');
        } catch (error) {
          console.error('[AI Captions] Error stopping Agora STT:', error);
        }
        sttIntentionalRestartRef.current = false;
      }

      // Also stop fallback session if running
      aiCaptionSession?.stop();
      setAICaptionSession(null);
      setAICaptionsEnabled(false);
      setAICaptions([]);
      setSttPubBotUid(null);
      sttPubBotUidRef.current = null;
      setSttAgentId(null);
      sttHasTranslateRef.current = null;
      toast.info('AI Captions stopped');
    }
  };

  // Listen for Agora STT stream-message events (protobuf data from the STT bot)
  // Kept registered whenever client exists — uses refs internally to filter.
  // This prevents losing messages during STT restart (when botUid goes null momentarily).
  useEffect(() => {
    const client = mMeeting?.client;
    if (!client) return;

    const handleStreamMessage = (uid, data) => {
      const botUid = sttPubBotUidRef.current;
      // Only process messages from the STT bot (if we know its UID)
      // If botUid is null (during restart), accept messages from STT UID range (999000-999999)
      if (botUid && uid !== botUid) return;
      if (!botUid && (uid < 999000 || uid > 999999)) return;

      const parsed = parseSttMessage(data);
      if (!parsed) return;

      const currentSpoken = spokenLanguageRef.current;
      const currentCaption = captionLanguageRef.current;
      const needsTranslation = currentSpoken !== currentCaption;

      // Debug log for all final messages
      if (parsed.isFinal) {
        console.log('[STT] Message:', parsed.dataType, '| text:', parsed.text,
          '| translations:', parsed.translations?.length || 0,
          '| culture:', parsed.culture,
          '| spoken:', currentSpoken, '| caption:', currentCaption);
      }

      // Find speaker name from participants (use ref to avoid re-render dependency)
      // Remote users: keyed by numeric UID (direct lookup works)
      // Local user: keyed by participantId string (direct lookup fails, need fallback)
      let speakerName = '';
      const currentMeeting = mMeetingRef.current;
      if (parsed.speakerUid && currentMeeting?.participants) {
        const speaker = currentMeeting.participants.get(parsed.speakerUid) ||
                        currentMeeting.participants.get(String(parsed.speakerUid));
        if (speaker) {
          speakerName = speaker.displayName || '';
        } else if (currentMeeting.localParticipant?.displayName) {
          // Local user's Map key is participantId (string), not their Agora numeric UID
          // If direct lookup failed, it's likely the local user speaking
          speakerName = currentMeeting.localParticipant.displayName;
        }
        // Send speaker map to backend so stored segments get proper names
        if (speakerName && meetingState?.emitSpeakerMap) {
          meetingState.emitSpeakerMap(parsed.speakerUid, speakerName);
        }
      }

      // Relay final transcript segments to the backend for AI notetaker
      if (parsed.isFinal && parsed.text.trim() && parsed.dataType === 'transcribe') {
        meetingState.emitTranscriptSegment({
          sessionId: meetingSettings?.roomId || '',
          speaker: speakerName || `Speaker ${parsed.speakerUid}`,
          speakerUid: parsed.speakerUid,
          text: parsed.text.trim(),
          timestamp: parsed.timestamp || Date.now(),
          isFinal: true,
          language: parsed.culture || 'en-US',
        });

        // Also feed into transcriptions state so ToolsPanel transcript history updates
        setTranscriptions({
          participantId: String(parsed.speakerUid),
          participantName: speakerName || `Speaker ${parsed.speakerUid}`,
          text: parsed.text.trim(),
          timestamp: parsed.timestamp || Date.now(),
          type: 'text',
        });
      }

      // Feed ALL transcribe text (interim + final) to teleprompter for real-time tracking
      // This MUST be before the captions gate — teleprompter needs STT even without captions enabled
      if (parsed.dataType === 'transcribe' && parsed.text.trim()) {
        console.log('[STT→Prompter]', parsed.isFinal ? 'FINAL' : 'interim', ':', parsed.text.trim().slice(0, 60));
        setSttLiveText({ text: parsed.text.trim(), isFinal: parsed.isFinal, ts: Date.now() });
      }

      // Route caption to the correct state based on which system is active
      // Use refs to always get the latest enabled state (avoids stale closure)
      if (!captionsEnabledRef.current && !aiCaptionsEnabledRef.current) return;
      const setCaptions = captionsEnabledRef.current ? setLiveCaptions : setAICaptions;

      // ─────────────────────────────────────────────────────────────────
      // OLD AGORA-PRIMARY BEHAVIOR — KEPT FOR ROLLBACK
      // ─────────────────────────────────────────────────────────────────
      // Previously the Agora 'translate' message was always displayed as
      // the caption, with Google Translate only used as a fallback. To
      // restore that behavior: delete the new block below and uncomment
      // the block in this comment.
      //
      // // Handle translate-type messages: show the translation as the caption
      // if (parsed.dataType === 'translate') {
      //   if (parsed.translations && parsed.translations.length > 0) {
      //     const targetAgoraLang = toAgoraSttLanguage(currentCaption);
      //     const translation = parsed.translations.find(t =>
      //       t.lang === targetAgoraLang ||
      //       t.lang?.split('-')[0] === targetAgoraLang?.split('-')[0]
      //     );
      //     if (translation && translation.text && translation.text.trim()) {
      //       const translatedCaption = {
      //         text: translation.text,
      //         originalText: parsed.originalText || parsed.text || '',
      //         wasTranslated: true,
      //         translatedBy: 'agora',
      //         isFinal: translation.isFinal || parsed.isFinal,
      //         speaker: speakerName,
      //         speakerUid: parsed.speakerUid,
      //         timestamp: parsed.timestamp || Date.now(),
      //         isTranslating: false,
      //       };
      //       setCaptions(prev => {
      //         const updated = [...prev];
      //         const lastIdx = updated.length - 1;
      //         if (lastIdx >= 0 && !updated[lastIdx].isFinal &&
      //             updated[lastIdx].speakerUid === parsed.speakerUid) {
      //           updated[lastIdx] = translatedCaption;
      //           return updated.slice(-10);
      //         }
      //         return [...updated.slice(-10), translatedCaption];
      //       });
      //       return;
      //     }
      //   }
      //   return;
      // }

      // ─────────────────────────────────────────────────────────────────
      // NEW: Google Translate is the PRIMARY translator (2026-04-07).
      // Agora 'translate' messages are ONLY used when Google Translate
      // has failed earlier in this session (useAgoraTranslateFallbackRef).
      // ─────────────────────────────────────────────────────────────────
      if (parsed.dataType === 'translate') {
        // Always silently drop Agora translations unless we've fallen back
        // to Agora because Google Translate is unavailable.
        if (!useAgoraTranslateFallbackRef.current) {
          return;
        }
        if (parsed.translations && parsed.translations.length > 0) {
          const targetAgoraLang = toAgoraSttLanguage(currentCaption);
          const translation = parsed.translations.find(t =>
            t.lang === targetAgoraLang ||
            t.lang?.split('-')[0] === targetAgoraLang?.split('-')[0]
          );
          if (translation && translation.text && translation.text.trim()) {
            const translatedCaption = {
              text: translation.text,
              originalText: parsed.originalText || parsed.text || '',
              wasTranslated: true,
              translatedBy: 'agora',
              isFinal: translation.isFinal || parsed.isFinal,
              speaker: speakerName,
              speakerUid: parsed.speakerUid,
              timestamp: parsed.timestamp || Date.now(),
              isTranslating: false,
            };
            setCaptions(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && !updated[lastIdx].isFinal &&
                  updated[lastIdx].speakerUid === parsed.speakerUid) {
                updated[lastIdx] = translatedCaption;
                return updated.slice(-10);
              }
              return [...updated.slice(-10), translatedCaption];
            });
            return;
          }
        }
        return;
      }

      // Handle transcribe-type messages
      if (!parsed.text.trim()) return;

      // ─────────────────────────────────────────────────────────────────
      // OLD AGORA-PRIMARY BEHAVIOR — KEPT FOR ROLLBACK
      // ─────────────────────────────────────────────────────────────────
      // Original logic: when translation was needed, the handler first
      // tried any inline Agora translation on the transcribe message,
      // then fell back to Google Translate ONLY if Agora STT was offline
      // (useGoogleTranslateFallbackRef === true), and finally suppressed
      // the source-language transcribe entirely (because Agora's separate
      // 'translate' messages would deliver the caption).
      //
      // To restore Agora-primary behavior, delete the new block below
      // and uncomment the block in this comment.
      //
      // if (needsTranslation) {
      //   if (!parsed.isFinal) return;
      //   if (parsed.translations && parsed.translations.length > 0) {
      //     const targetAgoraLang = toAgoraSttLanguage(currentCaption);
      //     const agoraTranslation = parsed.translations.find(t =>
      //       t.lang === targetAgoraLang ||
      //       t.lang?.split('-')[0] === targetAgoraLang?.split('-')[0]
      //     );
      //     if (agoraTranslation?.text?.trim()) {
      //       setCaptions(prev => [...prev.slice(-10), {
      //         text: agoraTranslation.text,
      //         originalText: parsed.text,
      //         wasTranslated: true,
      //         translatedBy: 'agora',
      //         isFinal: true,
      //         speaker: speakerName,
      //         speakerUid: parsed.speakerUid,
      //         timestamp: parsed.timestamp || Date.now(),
      //         isTranslating: false,
      //       }]);
      //       return;
      //     }
      //   }
      //   if (useGoogleTranslateFallbackRef.current) {
      //     const textToTranslate = parsed.text;
      //     const srcLang = currentSpoken;
      //     const tgtLang = currentCaption;
      //     const captureSpeak = speakerName;
      //     const captureUid = parsed.speakerUid;
      //     const captureTs = parsed.timestamp || Date.now();
      //     translateTextLocally(textToTranslate, srcLang, tgtLang).then(translatedText => {
      //       setCaptions(prev => [...prev.slice(-10), {
      //         text: translatedText,
      //         originalText: textToTranslate,
      //         wasTranslated: translatedText !== textToTranslate,
      //         translatedBy: 'google',
      //         isFinal: true,
      //         speaker: captureSpeak,
      //         speakerUid: captureUid,
      //         timestamp: captureTs,
      //         isTranslating: false,
      //       }]);
      //     });
      //     return;
      //   }
      //   return;
      // }

      // ─────────────────────────────────────────────────────────────────
      // NEW: Google Translate is the PRIMARY translator (2026-04-07).
      // For each final transcribe message we always call Google first.
      // If Google succeeds we display its result. If Google throws (network
      // error, blocked region, bad payload), we flip the session to use
      // Agora 'translate' messages from that point forward.
      // ─────────────────────────────────────────────────────────────────
      if (needsTranslation) {
        if (!parsed.isFinal) return; // skip interims — they'd display in the wrong language

        // If we've already fallen back to Agora translations earlier in
        // this session, don't double-translate via Google. The matching
        // Agora 'translate' message (handled above) will deliver the caption.
        if (useAgoraTranslateFallbackRef.current) {
          return;
        }

        const textToTranslate = parsed.text;
        const srcLang = currentSpoken;
        const tgtLang = currentCaption;
        const captureSpeak = speakerName;
        const captureUid = parsed.speakerUid;
        const captureTs = parsed.timestamp || Date.now();

        // IMPORTANT: do NOT show the source-language text as a placeholder.
        // We previously rendered the English transcript while Google was in
        // flight, then swapped it out — that produced a visible English →
        // translation flicker. Instead we keep the previous caption visible
        // until Google returns the translated text, then append it as final.
        translateTextLocally(textToTranslate, srcLang, tgtLang)
          .then(translatedText => {
            setCaptions(prev => [...prev.slice(-10), {
              text: translatedText,
              originalText: textToTranslate,
              wasTranslated: translatedText !== textToTranslate,
              translatedBy: 'google',
              isFinal: true,
              speaker: captureSpeak,
              speakerUid: captureUid,
              timestamp: captureTs,
              isTranslating: false,
            }]);
          })
          .catch(err => {
            console.warn('[Captions] Google Translate failed, falling back to Agora translation:', err);
            // Flip the session to Agora-translate fallback. The matching
            // Agora 'translate' message for this same utterance will arrive
            // shortly and be appended by the translate-message handler.
            setUseAgoraTranslateFallback(true);
            useAgoraTranslateFallbackRef.current = true;
          });
        return;
      }

      // No translation needed — show transcription directly (both interim and final)
      const caption = {
        text: parsed.text,
        originalText: null,
        wasTranslated: false,
        isFinal: parsed.isFinal,
        speaker: speakerName,
        speakerUid: parsed.speakerUid,
        timestamp: parsed.timestamp || Date.now(),
        isTranslating: false,
      };

      if (parsed.isFinal) {
        setCaptions(prev => [...prev.slice(-10), caption]);
      } else {
        setCaptions(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && !updated[lastIdx].isFinal &&
              updated[lastIdx].speakerUid === parsed.speakerUid) {
            updated[lastIdx] = caption;
          } else {
            updated.push(caption);
          }
          return updated.slice(-10);
        });
      }
    };

    client.on('stream-message', handleStreamMessage);
    console.log('[STT] Registered stream-message listener for STT data');

    return () => {
      client.off('stream-message', handleStreamMessage);
      console.log('[STT] Removed stream-message listener');
    };
  // All dynamic state (captionsEnabled, aiCaptionsEnabled, sttPubBotUid, languages)
  // is read via refs inside the handler, so only client needs to be a dep.
  // This prevents the listener from being torn down during STT restarts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mMeeting?.client]);

  // NOTE: Language changes no longer restart the shared Agora STT bot.
  // The STT bot is started once with all supported target languages, and each
  // participant filters translations client-side by their captionLanguage.
  // This prevents one participant's language change from disrupting others.

  // Auto-restart STT bot if it leaves the channel unexpectedly (e.g. Agora idle timeout)
  useEffect(() => {
    const client = mMeeting?.client;
    if (!client) return;

    let restartTimer = null;

    const handleUserLeft = (user) => {
      const botUid = sttPubBotUidRef.current;
      if (!botUid || user.uid !== botUid) return;

      // If we intentionally stopped the bot (language change forceRestart), skip auto-restart
      if (sttIntentionalRestartRef.current) {
        console.log('[STT] Bot left during intentional restart (language change) — skipping auto-restart');
        return;
      }

      // STT bot left the channel — check if captions/notetaker are still supposed to be active
      const shouldBeActive = captionsEnabledRef.current || aiCaptionsEnabledRef.current || sttBotActive;
      if (!shouldBeActive) return;

      console.warn('[STT] Bot left the channel unexpectedly (uid:', user.uid, '). Auto-restarting in 1.5s...');

      // Clear stale state
      setSttPubBotUid(null);
      sttPubBotUidRef.current = null;
      setSttAgentId(null);

      // Restart after a short delay
      restartTimer = setTimeout(async () => {
        const meetingId = mMeetingRef.current?.meetingId;
        if (!meetingId) return;

        try {
          const currentSpoken = spokenLanguageRef.current || 'en';
          const currentCaption = captionLanguageRef.current || 'en';
          const agoraSttLang = toAgoraSttLanguage(currentSpoken);
          const sttOptions = {};
          const allTargets = buildAllTargetsTranslateConfig(currentSpoken, currentCaption);
          if (allTargets) {
            sttOptions.translateConfig = allTargets;
          }
          console.log('[STT] Auto-restarting for channel:', meetingId);
          const result = await startStt(meetingId, [agoraSttLang], sttOptions);

          if (result.success) {
            const newBotUid = Number(result.pubBotUid);
            setSttPubBotUid(newBotUid);
            sttPubBotUidRef.current = newBotUid;
            setSttAgentId(result.agentId);
            setSttBotActive(true);
            console.log('[STT] Auto-restarted successfully, new pubBotUid:', newBotUid);
          } else {
            console.error('[STT] Auto-restart returned unsuccessful');
          }
        } catch (error) {
          console.error('[STT] Auto-restart failed:', error);
        }
      }, 1500);
    };

    client.on('user-left', handleUserLeft);

    return () => {
      client.off('user-left', handleUserLeft);
      clearTimeout(restartTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mMeeting?.client, sttBotActive]);

  // Clean up STT on unmount or meeting leave
  useEffect(() => {
    return () => {
      const meetingId = mMeeting?.meetingId;
      if (meetingId && sttPubBotUidRef.current) {
        stopStt(meetingId).catch(() => {});
      }
    };
  }, [mMeeting?.meetingId]);

  const { publish: publishShowElement } = usePubSub("SHOW_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      console.log("SHOW_ELEMENT_SECTION", data);
      const elementData = JSON.parse(data.message);
      // If this is a "clear" message, deactivate the element
      if (elementData.clear) {
        setActiveElement(null);
        return;
      }
      // Ignore stale persisted messages replayed on join — only accept live broadcasts.
      // Exception: contracts must always come through since they're sent once and need to persist.
      if (data.isPersisted && elementData.type !== 'contract') {
        return;
      }
      // Pre-warm the proxy cache for website/weboverlay elements so loading is fast
      try {
        let url = null;
        if (elementData.type === "weboverlay" || elementData.type === "web_overlay") {
          url = elementData.url;
        } else if (elementData.type === "website" && elementData.settings) {
          const s = typeof elementData.settings === "string" ? JSON.parse(elementData.settings) : elementData.settings;
          url = s.link;
        }
        if (url) {
          if (!url.startsWith("http")) url = "https://" + url;
          const apiBase = process.env.REACT_APP_BASE_URL || "";
          fetch(`${apiBase}/proxy/embed?url=${encodeURIComponent(url)}`).catch(() => {});
        }
      } catch (e) { /* ignore warm-up errors */ }
      // Add the sender's participant ID so we know whose tile to show the video on.
      // Use functional form to avoid re-setting the same element (prevents double-mount/restart).
      setActiveElement(prev => {
        const incomingId = elementData._id || elementData.id;
        const currentId = prev?._id || prev?.id;
        if (incomingId && currentId && incomingId === currentId && prev?.type === elementData.type) {
          // Same element already active — skip update to avoid remounting VideoSection
          return prev;
        }
        return {
          ...elementData,
          senderParticipantId: data.senderId,
          senderName: data.senderName,
        };
      });
    },
  });
  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );

  usePubSub("CLOSE_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      setActiveElement(null);
    },
  });

  // Webinar promote/demote: handle mode change requests from host
  usePubSub("WEBINAR_PARTICIPANT_MODE_CHANGE_LISTENER", {
    onMessageReceived: (data) => {
      if (meetingSettings?.roomType !== "Webinar") return;
      const msg = typeof data.message === "string" ? JSON.parse(data.message) : data.message;
      const { participantId: targetId, mode: newMode, action } = msg;
      const localId = mMeeting.localParticipant?.id;
      if (!localId) return;
      const isTargetMe = String(targetId) === String(localId) ||
        String(stringToNumericUid(targetId)) === String(stringToNumericUid(localId));
      if (!isTargetMe) return;

      if (action === "approve" && newMode === Constants.modes.SEND_AND_RECV) {
        mMeeting.changeMode(Constants.modes.SEND_AND_RECV).then(() => {
          setMeetingRole('presenter');
          toast.success("You've been promoted to presenter!");
        });
      } else if (action === "deny") {
        mMeeting.changeMode(Constants.modes.RECV_ONLY).then(() => {
          setMeetingRole('participant');
          toast.info("You've been moved back to the audience.");
        });
      }
    },
  });

  // Whiteboard sharing PubSub - host broadcasts room UUID, participants join
  // Also destructure `messages` so late joiners can replay persisted open/close events
  const { publish: publishWhiteboard, messages: persistedWhiteboardMessages } = usePubSub("WHITEBOARD", {
    onMessageReceived: (data) => {
      console.log("[Whiteboard PubSub] Received message:", data);
      const messageData = typeof data.message === "string"
        ? JSON.parse(data.message)
        : data.message;

      const { action, roomUUID } = messageData;
      const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';
      // Did *I* publish this UUID? If so, I already have it open locally — skip.
      const isOwnPublish = roomUUID && lastPublishedWhiteboardUUID.current === roomUUID;

      if (action === 'open' && roomUUID && !isOwnPublish && !isWhiteboardOpen) {
        // Any participant (including co-hosts) joins the whiteboard the host opened
        console.log("[Whiteboard PubSub] Joining whiteboard room:", roomUUID);
        const lid = mMeetingRef.current?.localParticipant?.id;
        const lnum = lid ? stringToNumericUid(lid) : null;
        const isLocalPresenter = lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum)));
        joinWhiteboard(roomUUID, {
          meetingId: mMeetingRef.current?.meetingId,
          userId: lid || `user-${Date.now()}`,
          userName: mMeetingRef.current?.localParticipant?.displayName || 'Anonymous',
          // Hosts, co-hosts, and presenters get write access
          isHost: isHostOrCoHost || !!isLocalPresenter,
        });
        // Track this UUID locally so our publish effect doesn't re-publish it
        lastPublishedWhiteboardUUID.current = roomUUID;
      } else if (action === 'close' && !isOwnPublish) {
        // Anyone (except the closer) should close their whiteboard when host closes it
        console.log("[Whiteboard PubSub] Host closed whiteboard");
        setGuestHiddenWhiteboard(false);
        stopWhiteboardBase();
        lastPublishedWhiteboardUUID.current = null;
      }
    },
  });

  // Late-joiner sync from RTM persisted messages. Re-runs whenever the
  // persisted message list grows (not just the first time) so messages that
  // arrive late — common when the joiner's RTM handshake lags — still trigger
  // a join. The Socket.IO redundant path below is the primary source; this
  // RTM path is a secondary fallback.
  useEffect(() => {
    if (!persistedWhiteboardMessages || persistedWhiteboardMessages.length === 0) return;
    if (!meetingRole) return; // wait until role is known

    // Walk persisted messages in order; final state is the last open/close action
    let activeRoomUUID = null;
    persistedWhiteboardMessages.forEach((msg) => {
      try {
        const m = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
        if (m?.action === 'open' && m?.roomUUID) {
          activeRoomUUID = m.roomUUID;
        } else if (m?.action === 'close') {
          activeRoomUUID = null;
        }
      } catch (e) {
        console.warn('[Whiteboard] Failed to parse persisted message:', e);
      }
    });

    // Skip if this room is already mine (I opened it) or already open locally
    const isOwnRoom = activeRoomUUID && lastPublishedWhiteboardUUID.current === activeRoomUUID;
    if (activeRoomUUID && !isWhiteboardOpen && !isOwnRoom) {
      const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';
      console.log('[Whiteboard] RTM sync: joining active whiteboard room:', activeRoomUUID);
      const lid = mMeetingRef.current?.localParticipant?.id;
      const lnum = lid ? stringToNumericUid(lid) : null;
      const isLocalPresenter = lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum)));
      joinWhiteboard(activeRoomUUID, {
        meetingId: mMeetingRef.current?.meetingId,
        userId: lid || `user-${Date.now()}`,
        userName: mMeetingRef.current?.localParticipant?.displayName || 'Anonymous',
        isHost: isHostOrCoHost || !!isLocalPresenter,
      });
      lastPublishedWhiteboardUUID.current = activeRoomUUID;
    }
  }, [persistedWhiteboardMessages, meetingRole, isWhiteboardOpen, joinWhiteboard, presenters]);

  // Socket.IO redundant whiteboard-state sync. Runs alongside the RTM
  // PubSub path above — whichever source reports the live state first wins.
  // This is the primary path for:
  //   • late joiners (socket snapshot is authoritative, RTM persist drifts)
  //   • participants returning from breakout rooms (RTM channel was different)
  //   • long-running sessions where the RTM close message was dropped after
  //     token expiry / network blip
  //
  // IMPORTANT: the close branch only fires when we have a previous
  // server-confirmed OPEN to compare against. Without this guard, the initial
  // {isOpen:false, roomUUID:null} socket state (which is the default before
  // the server has said anything) would be mistaken for a close signal the
  // moment the local user opens the board — leading to the board "disappearing"
  // on its own right after Open Whiteboard is clicked.
  const socketWhiteboardState = meetingState?.whiteboardState;
  const socketWhiteboardSeenOpenRef = useRef(false);
  useEffect(() => {
    if (!socketWhiteboardState) return;
    if (!meetingRole) return;
    const { isOpen, roomUUID } = socketWhiteboardState;

    // Skip if this room is already mine (I just published it)
    const isOwnRoom = roomUUID && lastPublishedWhiteboardUUID.current === roomUUID;

    if (isOpen && roomUUID && !isWhiteboardOpen && !isOwnRoom) {
      const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';
      console.log('[Whiteboard] Socket sync: joining active room', roomUUID);
      const lid = mMeetingRef.current?.localParticipant?.id;
      const lnum = lid ? stringToNumericUid(lid) : null;
      const isLocalPresenter = lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum)));
      joinWhiteboard(roomUUID, {
        meetingId: mMeetingRef.current?.meetingId,
        userId: lid || `user-${Date.now()}`,
        userName: mMeetingRef.current?.localParticipant?.displayName || 'Anonymous',
        isHost: isHostOrCoHost || !!isLocalPresenter,
      });
      lastPublishedWhiteboardUUID.current = roomUUID;
      socketWhiteboardSeenOpenRef.current = true;
    } else if (isOpen && roomUUID) {
      // Record the acknowledgement so the close branch below can trust a
      // subsequent !isOpen as a genuine server-driven close.
      socketWhiteboardSeenOpenRef.current = true;
    } else if (!isOpen && isWhiteboardOpen && socketWhiteboardSeenOpenRef.current) {
      console.log('[Whiteboard] Socket sync: host closed whiteboard');
      setGuestHiddenWhiteboard(false);
      stopWhiteboardBase();
      lastPublishedWhiteboardUUID.current = null;
      socketWhiteboardSeenOpenRef.current = false;
    }
  }, [socketWhiteboardState, meetingRole, isWhiteboardOpen, joinWhiteboard, stopWhiteboardBase, presenters]);

  // Store publishWhiteboard in ref so stopWhiteboard (defined earlier) can use it
  useEffect(() => {
    publishWhiteboardRef.current = publishWhiteboard;
  }, [publishWhiteboard]);

  // Effect to publish whiteboard room UUID when host opens whiteboard
  useEffect(() => {
    const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    const roomUUID = whiteboardRoomInfo?.uuid;

    // Only publish if:
    // 1. We're the host/co-host
    // 2. Whiteboard is open
    // 3. We have a room UUID
    // 4. We haven't already published this UUID
    if (isHostOrCoHost && isWhiteboardOpen && roomUUID && lastPublishedWhiteboardUUID.current !== roomUUID) {
      console.log("[Whiteboard] Publishing whiteboard open to participants:", roomUUID);
      publishWhiteboard(
        JSON.stringify({ action: 'open', roomUUID }),
        { persist: true }
      );
      lastPublishedWhiteboardUUID.current = roomUUID;
    }

    // NOTE: Close message is now published directly in stopWhiteboard() to ensure
    // it happens BEFORE the whiteboard disconnects. This effect is kept for backwards
    // compatibility but the close branch below should rarely trigger since stopWhiteboard
    // already handles it.
    if (isHostOrCoHost && !isWhiteboardOpen && lastPublishedWhiteboardUUID.current) {
      console.log("[Whiteboard] Publishing whiteboard close to participants (effect fallback)");
      publishWhiteboard(
        JSON.stringify({ action: 'close', roomUUID: lastPublishedWhiteboardUUID.current }),
        { persist: true }
      );
      lastPublishedWhiteboardUUID.current = null;
    }
  }, [isWhiteboardOpen, whiteboardRoomInfo, meetingRole, publishWhiteboard]);

  const { publish: publishEmoji } = usePubSub("EMOJI_REACTION", {
    onMessageReceived: (data) => {
      console.log("EMOJI_REACTION", data);
      const localParticipantId = mMeeting?.localParticipant?.id;

      const messageData =
        typeof data.message === "string"
          ? JSON.parse(data.message)
          : data.message;
      const { char, alt, boosted, isEffect } = messageData;

      // Handle full-screen effects (balloon, confetti, hearts, applause, mind_blown)
      // These should trigger the ReactionsOverlay animation for ALL participants
      if (isEffect) {
        console.log("Full-screen effect received:", alt);
        // Skip local emit since sender already triggered it locally
        if (!data.isLocal) {
          setActiveReaction(null);
          setTimeout(() => setActiveReaction(alt), 10);
        }
        return; // Effects don't show tile badges, just full-screen animation
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const reaction = {
        id,
        from: data.senderName,
        emoji: char,
        emojiType: alt,
        boosted: boosted || false,
        ts: Date.now(),
      };

      // Add to recentReactions for the bottom-left floating display
      setRecentReactions((prev) => [...prev.slice(-5), reaction]);
      setTimeout(() => {
        setRecentReactions((prev) => prev.filter((r) => r.id !== id));
      }, 2500);

      // Also track per-participant for tile badges
      // This shows the reaction badge on the sender's video tile
      const senderId = data.senderId;
      if (senderId) {
        // IMPORTANT: Use the correct ID format based on participant type:
        // - LOCAL participant: stored in participants map with STRING participantId
        // - REMOTE participants: stored in participants map with NUMERIC uid
        const numericSenderId = stringToNumericUid(senderId);
        const isLocal = senderId === localParticipantId || numericSenderId === stringToNumericUid(localParticipantId);
        const participantIdToStore = isLocal ? localParticipantId : numericSenderId;

        setParticipantReactions((prev) => ({
          ...prev,
          [participantIdToStore]: {
            emoji: char,
            boosted: boosted || false,
            ts: Date.now(),
          },
        }));

        // Auto-clear this participant's reaction after 3 seconds
        setTimeout(() => {
          setParticipantReactions((prev) => {
            const updated = { ...prev };
            // Only clear if it's the same reaction (not a newer one)
            if (updated[participantIdToStore]?.ts === reaction.ts) {
              delete updated[participantIdToStore];
            }
            return updated;
          });
        }, 3000);
      }

      // Trigger full-screen animation for boosted reactions on remote participants
      // (local participant already triggered it when sending)
      if (boosted && !data.isLocal) {
        setActiveReaction(null);
        setTimeout(() => setActiveReaction(alt), 10);
      }
    },
  });

  const { publish: publishPoll } = usePubSub("POLL", {
    onMessageReceived: (data) => {
      // Handle both data.message and direct data structure
      const pollData = data.message || data;
      const { type, poll, pollId } = pollData;

      // Ignore stale persisted messages replayed on join — only accept live broadcasts.
      // Late joiners get element state from Socket.IO meeting state snapshot instead.
      if (data.isPersisted) return;

      if (type === "launch" && poll) {
        // Use isLocal flag to determine if current user launched this poll
        const isLauncher = data.isLocal === true;

        setIsPollLauncher(isLauncher);
        setActivePoll(poll);
        setShowPollModal(true);
      }

      // Handle poll end - close the voting modal for participants
      if (type === "end") {
        setShowPollModal(false);
        setActivePoll(null);
        setIsPollLauncher(false);
      }
    },
  });

  // POLL_VOTE PubSub for handling votes and updating results
  const { publish: publishPollVote } = usePubSub("POLL_VOTE", {
    onMessageReceived: (data) => {
      const voteData = data.message || data;
      const { type, pollId, vote, skip } = voteData;

      // Host/co-host processes votes to update results
      if ((meetingRole === "moderator" || meetingRole === "co-host") && pollResults && pollResults.id === pollId) {
        if (type === "vote_submitted" && vote) {
          // Update poll results with new vote
          const updatedResults = { ...pollResults };

          // Add vote to votes array
          updatedResults.votes = [...(updatedResults.votes || []), vote];
          updatedResults.totalVotes = (updatedResults.totalVotes || 0) + 1;

          // Update option vote counts
          if (vote.optionIndices && updatedResults.options) {
            updatedResults.options = updatedResults.options.map((opt, idx) => {
              if (vote.optionIndices.includes(idx) || vote.optionIndices.includes(opt.index)) {
                return { ...opt, votes: (opt.votes || 0) + 1 };
              }
              return opt;
            });
          }

          setPollResults(updatedResults);

          // Broadcast updated results to all participants
          publishPollVote({
            type: "results_update",
            pollId: pollId,
            poll: updatedResults,
          });
        }

        if (type === "vote_skipped" && skip) {
          const updatedResults = { ...pollResults };
          updatedResults.skipped = [...(updatedResults.skipped || []), skip];
          updatedResults.skippedCount = (updatedResults.skippedCount || 0) + 1;

          setPollResults(updatedResults);

          // Broadcast updated results
          publishPollVote({
            type: "results_update",
            pollId: pollId,
            poll: updatedResults,
          });
        }
      }
    },
  });

  // Timer PubSub for syncing timer state across participants
  const { publish: publishTimer } = usePubSub("TIMER", {
    onMessageReceived: (data) => {
      if (data.isPersisted) return;
      const timerData = data.message || data;
      const { type, timer, state, timerId } = timerData;

      // Handle timer launch
      if (type === "launch" && timer) {
        // Use isLocal flag to determine if current user launched this timer
        setIsTimerLauncher(data.isLocal === true);
        setActiveTimer(timer);
        setTimerState(state || { status: 'idle' });
      }

      // Handle timer state updates (start, pause, reset)
      if (type === "state_update" && state) {
        setTimerState(state);
        // For late joiners: if no active timer but state_update includes timer data, set it
        if (timer) {
          setActiveTimer(prev => prev || timer);
        }
      }

      // Handle timer end/close
      if (type === "end") {
        setActiveTimer(null);
        setIsTimerLauncher(false);
        setTimerState({ status: 'idle' });
      }
    },
  });

  // Prompter PubSub for syncing teleprompter across participants
  usePubSub("PROMPTER", {
    onMessageReceived: (data) => {
      if (data.isPersisted) return;
      const prompterData = data.message || data;
      const { type, prompter, prompterId } = prompterData;

      // Handle prompter launch - only for non-sender (host already has it set)
      if (type === "launch" && prompter && data.senderId !== mMeeting?.localParticipant?.id) {
        setPrompterElement(prompter);
      }

      // Handle prompter end/close
      if (type === "end") {
        setPrompterElement(null);
      }
    },
  });

  // Presentation PubSub for syncing presentations across participants
  const { publish: publishPresentation } = usePubSub("PRESENTATION", {
    onMessageReceived: (data) => {
      if (data.isPersisted) return;
      const presentationData = data.message || data;
      const { type, presentation, state, presentationId } = presentationData;

      if (isRecorderMode) {
        console.log('[PresentationPubSub] recorder cell=' + (recorderLayoutOverride || 'single') + ' received type=' + type + ' presId=' + (presentation?._id || presentationId || 'none'));
      }
      // Handle presentation launch
      if (type === "launch" && presentation) {
        // Use isLocal flag to determine if current user launched this presentation
        setIsPresentationLauncher(data.isLocal === true);
        setActivePresentation(presentation);
        setPresentationState(state || { currentSlide: 1, status: 'active' });
      }

      // Handle presentation state updates (page changes)
      if (type === "state_update" && state) {
        setPresentationState(state);
        // For late joiners: if no active presentation but state_update includes presentation data, set it
        if (presentation) {
          setActivePresentation(prev => prev || presentation);
        }
      }

      // Handle presentation end/close
      if (type === "end") {
        if (isRecorderMode) {
          console.log('[PresentationPubSub] recorder cell=' + (recorderLayoutOverride || 'single') + ' CLEARING activePresentation');
        }
        setActivePresentation(null);
        setIsPresentationLauncher(false);
        setPresentationState({ currentSlide: 1, status: 'idle' });
      }
    },
  });

  // Lead Capture PubSub for showing lead capture forms to both moderator and participants
  const { publish: publishLeadCapture } = usePubSub("LEAD_CAPTURE", {
    onMessageReceived: (data) => {
      if (data.isPersisted) return;
      // Parse the message - it's JSON-stringified for persist compatibility
      const raw = data.message || data;
      const leadCaptureData = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const { type, leadCapture, leadCaptureId } = leadCaptureData;

      // Skip clear messages (already handled by persist system deleting the attribute)
      if (leadCaptureData.clear) {
        setShowLeadCaptureModal(false);
        setActiveLeadCapture(null);
        setIsLeadCaptureLauncher(false);
        return;
      }

      if (type === "launch" && leadCapture) {
        // Use isLocal flag to determine if current user launched this lead capture
        const isLauncher = data.isLocal === true;
        setIsLeadCaptureLauncher(isLauncher);
        setActiveLeadCapture(leadCapture);
        setShowLeadCaptureModal(true);
      }

      // Handle lead capture end - close the form for everyone
      if (type === "end") {
        setShowLeadCaptureModal(false);
        setActiveLeadCapture(null);
        setIsLeadCaptureLauncher(false);
      }
    },
  });

  // Late joiner: replay active lead capture from Socket.IO meeting state snapshot.
  // Only runs once — skips if form is already showing (e.g. host who just launched it).
  // Waits until user has fully entered the room (showJoinScreen === false).
  // Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  const hasReplayedLeadCaptureRef = useRef(false);
  useEffect(() => {
    if (hasReplayedLeadCaptureRef.current) return;
    if (showLeadCaptureModal) {
      hasReplayedLeadCaptureRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedLeadCaptureRef.current = true;
      return;
    }
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    const leadCaptureEl = meetingState.activeElements.find(
      el => el.type === 'leadcapture' || el.type === 'lead_capture'
    );
    if (leadCaptureEl && leadCaptureEl.config?.leadCapture) {
      hasReplayedLeadCaptureRef.current = true;
      setActiveLeadCapture(leadCaptureEl.config.leadCapture);
      setShowLeadCaptureModal(true);
      setIsLeadCaptureLauncher(false);
    }
  }, [meetingState.activeElements, showLeadCaptureModal, meetingRole, showJoinScreen]);

  // Late joiner: replay active poll from Socket.IO meeting state snapshot.
  // Waits until user has fully entered the room (showJoinScreen === false).
  // Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  const hasReplayedPollRef = useRef(false);
  useEffect(() => {
    if (hasReplayedPollRef.current) return;
    if (showPollModal) {
      hasReplayedPollRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedPollRef.current = true;
      return;
    }
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    const pollEl = meetingState.activeElements.find(el => el.type === 'poll');
    if (pollEl && pollEl.config?.poll) {
      hasReplayedPollRef.current = true;
      setActivePoll(pollEl.config.poll);
      setShowPollModal(true);
      setIsPollLauncher(false);
    }
  }, [meetingState.activeElements, showPollModal, showJoinScreen, meetingRole]);

  // Late joiner: replay active timer from Socket.IO meeting state snapshot.
  // Waits until user has fully entered the room (showJoinScreen === false).
  // Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  const hasReplayedTimerRef = useRef(false);
  useEffect(() => {
    if (hasReplayedTimerRef.current) return;
    if (activeTimer) {
      hasReplayedTimerRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedTimerRef.current = true;
      return;
    }
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    const timerEl = meetingState.activeElements.find(el => el.type === 'timer');
    if (timerEl && timerEl.config?.timer) {
      hasReplayedTimerRef.current = true;
      setActiveTimer(timerEl.config.timer);
      setTimerState(timerEl.config.state || { status: 'idle' });
      setIsTimerLauncher(false);
    }
  }, [meetingState.activeElements, activeTimer, showJoinScreen, meetingRole]);

  // Late joiner: replay active presentation from Socket.IO meeting state snapshot.
  // Waits until user has fully entered the room (showJoinScreen === false).
  // Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  const hasReplayedPresentationRef = useRef(false);
  useEffect(() => {
    if (hasReplayedPresentationRef.current) return;
    if (activePresentation) {
      hasReplayedPresentationRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedPresentationRef.current = true;
      return;
    }
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    const presentationEl = meetingState.activeElements.find(el => el.type === 'presentation');
    if (presentationEl && presentationEl.config?.presentation) {
      hasReplayedPresentationRef.current = true;
      setActivePresentation(presentationEl.config.presentation);
      setPresentationState(presentationEl.config.state || { currentSlide: 1, status: 'active' });
      setIsPresentationLauncher(false);
    }
  }, [meetingState.activeElements, activePresentation, showJoinScreen, meetingRole]);

  // Sync presentation page changes from Socket.IO meeting state (fallback for RTM misses)
  useEffect(() => {
    if (!activePresentation || !meetingState.activeElements) return;
    // Only non-controllers should sync from socket (controllers are the source of truth)
    const canControl = meetingRole === "moderator" || meetingRole === "co-host" || isPresentationLauncher;
    if (canControl) return;

    const presentationEl = meetingState.activeElements.find(el => el.type === 'presentation');
    if (presentationEl?.config?.state?.currentSlide && presentationEl.config.state.currentSlide !== presentationState?.currentSlide) {
      setPresentationState(presentationEl.config.state);
    }
  }, [meetingState.activeElements, activePresentation, meetingRole, isPresentationLauncher]);

  // Late joiner: replay active prompter from Socket.IO meeting state snapshot.
  // Waits until user has fully entered the room (showJoinScreen === false).
  // Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  const hasReplayedPrompterRef = useRef(false);
  useEffect(() => {
    if (hasReplayedPrompterRef.current) return;
    if (prompterElement) {
      hasReplayedPrompterRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedPrompterRef.current = true;
      return;
    }
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    const prompterEl = meetingState.activeElements.find(el => el.type === 'prompter');
    if (prompterEl && prompterEl.config?.prompter) {
      hasReplayedPrompterRef.current = true;
      setPrompterElement(prompterEl.config.prompter);
    }
  }, [meetingState.activeElements, prompterElement, showJoinScreen, meetingRole]);

  // Late joiner: replay active SHOW_ELEMENT_SECTION element (video, cta, website, weboverlay, document, talking_point)
  // from Socket.IO meeting state snapshot. RTM channel attribute persist is the primary mechanism,
  // but this provides a reliable fallback via server-side snapshot.
  // NOTE: Waits until user has fully entered the room (showJoinScreen === false).
  // NOTE: Hosts/co-hosts skip replay — they control elements and should start clean on refresh.
  // NOTE: We delay replay by 3s to allow host cleanup events to arrive first and remove stale elements.
  const hasReplayedActiveElementRef = useRef(false);
  const elementReplayTimerRef = useRef(null);
  useEffect(() => {
    if (hasReplayedActiveElementRef.current) return;
    if (activeElement) {
      hasReplayedActiveElementRef.current = true;
      return;
    }
    if (showJoinScreen !== false) return;
    if (!meetingState.activeElements || meetingState.activeElements.length === 0) return;

    // Hosts should not have old overlays/elements auto-restored on refresh
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) {
      hasReplayedActiveElementRef.current = true;
      return;
    }

    // Delay replay to allow the host's cleanup Socket.IO events to arrive and remove
    // stale elements from meetingState.activeElements before we replay them.
    if (elementReplayTimerRef.current) clearTimeout(elementReplayTimerRef.current);
    elementReplayTimerRef.current = setTimeout(() => {
      if (hasReplayedActiveElementRef.current) return;
      if (activeElement) {
        hasReplayedActiveElementRef.current = true;
        return;
      }
      // NOTE: 'contract' is excluded — contracts must be explicitly sent by host via "Send to Participants"
      const showElementTypes = ['video', 'cta', 'website', 'weboverlay', 'web_overlay', 'document', 'talking_point', 'talkingpoint'];
      const el = meetingState.activeElements.find(e => showElementTypes.includes(e.type));
      if (el && el.config?.elementData) {
        hasReplayedActiveElementRef.current = true;
        setActiveElement({
          ...el.config.elementData,
          senderParticipantId: el.config.elementData?.senderParticipantId || 'unknown',
          senderName: el.config.elementData?.senderName || 'Unknown',
        });
      } else {
        hasReplayedActiveElementRef.current = true;
      }
    }, 3000);
    return () => clearTimeout(elementReplayTimerRef.current);
  }, [meetingState.activeElements, activeElement, meetingRole, showJoinScreen]);

  // Real-time contract delivery via Socket.IO: when a contract element is added to
  // meetingState.activeElements (by host clicking "Send to Participants"), participants
  // should immediately show it. This serves as a reliable backup to PubSub/RTM.
  const lastContractElementIdRef = useRef(null);
  useEffect(() => {
    const isHost = meetingRole === 'moderator' || meetingRole === 'co-host';
    if (isHost) return; // Hosts use activeContractView, not activeElement
    if (showJoinScreen !== false) return;

    const contractEl = meetingState.activeElements.find(e => e.type === 'contract');
    if (contractEl && contractEl.config?.elementData) {
      const contractId = contractEl.id || contractEl.config?.elementData?._id;
      // Only trigger if this is a NEW contract (not already shown)
      if (contractId && contractId !== lastContractElementIdRef.current) {
        lastContractElementIdRef.current = contractId;
        setActiveElement(prev => {
          // Don't overwrite if already showing this contract
          const currentId = prev?._id || prev?.id;
          if (currentId === contractId && prev?.type === 'contract') return prev;
          return {
            ...contractEl.config.elementData,
            type: 'contract',
          };
        });
      }
    } else {
      // Contract removed from activeElements — also clear the local
      // activeElement so the contract renderer unmounts on the participant
      // side when the host stops presenting.
      if (lastContractElementIdRef.current) {
        lastContractElementIdRef.current = null;
        setActiveElement(prev => (prev?.type === 'contract' ? null : prev));
      }
    }
  }, [meetingState.activeElements, meetingRole, showJoinScreen]);

  // Recorder bot: continuously sync activeElement from Socket.IO meeting state.
  // PubSub (RTM) is the primary delivery for live participants, but the recorder
  // bot may miss PubSub messages due to RTM timing. Socket.IO is a reliable
  // fallback that ensures elements always render in recordings.
  const lastRecorderElementIdRef = useRef(null);
  useEffect(() => {
    if (!isRecorderMode) return;
    if (showJoinScreen !== false) return;

    const showElementTypes = ['video', 'cta', 'website', 'weboverlay', 'web_overlay', 'document', 'talking_point', 'talkingpoint'];
    const el = meetingState.activeElements.find(e => showElementTypes.includes(e.type));

    if (el && el.config?.elementData) {
      const elId = el.id || el.config.elementData._id || el.config.elementData.id;
      if (elId && elId !== lastRecorderElementIdRef.current) {
        lastRecorderElementIdRef.current = elId;
        console.log('[Recorder] Syncing activeElement from Socket.IO:', el.type, elId);
        setActiveElement(prev => {
          const currentId = prev?._id || prev?.id;
          if (currentId === elId && prev?.type === el.type) return prev;
          return {
            ...el.config.elementData,
            senderParticipantId: el.config.elementData?.senderParticipantId || 'unknown',
            senderName: el.config.elementData?.senderName || 'Unknown',
          };
        });
      }
    } else if (!el && lastRecorderElementIdRef.current) {
      // All showable elements removed — clear activeElement
      lastRecorderElementIdRef.current = null;
      console.log('[Recorder] Clearing activeElement — no active elements in Socket.IO state');
      setActiveElement(null);
    }
  }, [isRecorderMode, meetingState.activeElements, showJoinScreen]);

  // Recorder bot: sync the modal-style elements (lead capture, poll, timer,
  // presentation, prompter) from Socket.IO meeting state so that when the host
  // closes one, the recording reflects the close. PubSub `type: "end"` closes
  // these for live participants but isn't reliably delivered to the headless
  // recorder browser, so without this sync the modal stays "stuck" on the
  // recording from when it was opened until the meeting ends.
  const lastRecorderLeadCaptureIdRef = useRef(null);
  const lastRecorderPollIdRef = useRef(null);
  const lastRecorderTimerIdRef = useRef(null);
  const lastRecorderPresentationIdRef = useRef(null);
  const lastRecorderPrompterIdRef = useRef(null);
  useEffect(() => {
    if (!isRecorderMode) return;
    if (showJoinScreen !== false) return;
    const elements = meetingState.activeElements || [];

    // Lead capture
    const lcEl = elements.find(e => e.type === 'leadcapture' || e.type === 'lead_capture');
    if (lcEl && lcEl.config?.leadCapture) {
      const id = lcEl.id || lcEl.config.leadCapture?._id || lcEl.config.leadCapture?.id || 'lc';
      if (id !== lastRecorderLeadCaptureIdRef.current) {
        lastRecorderLeadCaptureIdRef.current = id;
        console.log('[Recorder] Syncing lead capture from Socket.IO:', id);
        setActiveLeadCapture(lcEl.config.leadCapture);
        setShowLeadCaptureModal(true);
        setIsLeadCaptureLauncher(false);
      }
    } else if (lastRecorderLeadCaptureIdRef.current) {
      lastRecorderLeadCaptureIdRef.current = null;
      console.log('[Recorder] Clearing lead capture — removed from Socket.IO state');
      setShowLeadCaptureModal(false);
      setActiveLeadCapture(null);
      setIsLeadCaptureLauncher(false);
    }

    // Poll
    const pollEl = elements.find(e => e.type === 'poll');
    if (pollEl && pollEl.config?.poll) {
      const id = pollEl.id || pollEl.config.poll?._id || pollEl.config.poll?.id || 'poll';
      if (id !== lastRecorderPollIdRef.current) {
        lastRecorderPollIdRef.current = id;
        console.log('[Recorder] Syncing poll from Socket.IO:', id);
        setActivePoll(pollEl.config.poll);
        setShowPollModal(true);
        setIsPollLauncher(false);
      }
    } else if (lastRecorderPollIdRef.current) {
      lastRecorderPollIdRef.current = null;
      console.log('[Recorder] Clearing poll — removed from Socket.IO state');
      setShowPollModal(false);
      setActivePoll(null);
      setIsPollLauncher(false);
    }

    // Timer
    const timerEl = elements.find(e => e.type === 'timer');
    if (timerEl && timerEl.config?.timer) {
      const id = timerEl.id || timerEl.config.timer?._id || timerEl.config.timer?.id || 'timer';
      if (id !== lastRecorderTimerIdRef.current) {
        lastRecorderTimerIdRef.current = id;
        console.log('[Recorder] Syncing timer from Socket.IO:', id);
        setActiveTimer(timerEl.config.timer);
        setTimerState(timerEl.config.state || { status: 'idle' });
        setIsTimerLauncher(false);
      }
    } else if (lastRecorderTimerIdRef.current) {
      lastRecorderTimerIdRef.current = null;
      console.log('[Recorder] Clearing timer — removed from Socket.IO state');
      setActiveTimer(null);
      setIsTimerLauncher(false);
    }

    // Presentation
    const presEl = elements.find(e => e.type === 'presentation');
    if (presEl && presEl.config?.presentation) {
      const id = presEl.id || presEl.config.presentation?._id || presEl.config.presentation?.id || 'pres';
      if (id !== lastRecorderPresentationIdRef.current) {
        lastRecorderPresentationIdRef.current = id;
        console.log('[Recorder] Syncing presentation from Socket.IO:', id);
        setActivePresentation(presEl.config.presentation);
        setPresentationState(presEl.config.state || { currentSlide: 1, status: 'active' });
        setIsPresentationLauncher(false);
      }
    } else if (lastRecorderPresentationIdRef.current) {
      lastRecorderPresentationIdRef.current = null;
      console.log('[Recorder] Clearing presentation — removed from Socket.IO state');
      setActivePresentation(null);
      setIsPresentationLauncher(false);
    }

    // Prompter
    const prompterEl = elements.find(e => e.type === 'prompter');
    if (prompterEl && prompterEl.config?.prompter) {
      const id = prompterEl.id || prompterEl.config.prompter?._id || prompterEl.config.prompter?.id || 'prompter';
      if (id !== lastRecorderPrompterIdRef.current) {
        lastRecorderPrompterIdRef.current = id;
        console.log('[Recorder] Syncing prompter from Socket.IO:', id);
        setPrompterElement(prompterEl.config.prompter);
      }
    } else if (lastRecorderPrompterIdRef.current) {
      lastRecorderPrompterIdRef.current = null;
      console.log('[Recorder] Clearing prompter — removed from Socket.IO state');
      setPrompterElement(null);
    }
  }, [isRecorderMode, meetingState.activeElements, showJoinScreen]);

  function onTranslationStateChanged(data) {
    const { status, id } = data;
    console.log("onTranslationStateChanged", data);
    if (status === Constants.translationEvents.TRANSLATION_STARTING) {
      setTranslationStatus("starting");
    } else if (status === Constants.translationEvents.TRANSLATION_STARTED) {
      setTranslationStatus("started");
    } else if (status === Constants.translationEvents.TRANSLATION_STOPPING) {
      setTranslationStatus("stopping");
    } else if (status === Constants.translationEvents.TRANSLATION_STOPPED) {
      setTranslationStatus("stopped");
    }
  }

  function onTranslationText(data) {
    console.log("onTranslationText", data);
    let transenabled = translationEnabled;
    setTranslationEnabled((prev) => {
      console.log("prev", prev);
      transenabled = prev;
      return prev;
    });
    if (!transenabled) return;

    let { participantId, participantName, text, timestamp, type } = data;

    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    const profileImage =
      participant?.metaData?.profileImage ||
      participant?.value?.metaData?.profileImage ||
      null;
    setTranscriptions({
      participantId,
      participantName,
      text: text.trim(),
      timestamp,
      type,
      profileImage,
    });
  }

  function onTranslationTextOld(data) {
    console.log("onTranslationText", data);
    let { participantId, participantName, text, timestamp, type } = data;

    if (!text) return;
    const cleanedText = text.trim();

    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    const profileImage =
      participant?.metaData?.profileImage ||
      participant?.value?.metaData?.profileImage ||
      null;
    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      // Handle partial and full types
      if (type === "partial") {
        // For partial: if last entry from same participant exists, update it (always same index)
        if (lastTranscription) {
          // Replace existing entry with new partial (progressively updating same line)
          return prev.map((t, index) =>
            index === lastIndex
              ? {
                ...t,
                text: cleanedText,
                timestamp,
                type: "partial",
                profileImage: profileImage || t.profileImage,
              }
              : t
          );
        }

        // No existing entry from this participant, create new partial entry
        const transcriptionEntry = {
          id: `${participantId}-${timestamp}`,
          participantId,
          participantName,
          text: cleanedText,
          timestamp,
          type,
          profileImage,
        };
        return [...prev, transcriptionEntry];
      }

      if (type === "full") {
        // For full: replace partial at same index if exists
        if (lastTranscription && lastTranscription.type === "partial") {
          // Replace partial with full at same index
          return prev.map((t, index) =>
            index === lastIndex
              ? {
                ...t,
                text: cleanedText,
                timestamp,
                type: "full",
                profileImage: profileImage || t.profileImage,
              }
              : t
          );
        }

        // Previous full or no previous entry: create new entry (new line)
        const transcriptionEntry = {
          id: `${participantId}-${timestamp}`,
          participantId,
          participantName,
          text: cleanedText,
          timestamp,
          type,
          profileImage,
        };
        return [...prev, transcriptionEntry];
      }

      // No type specified - just add as new entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };
      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  }
  function onTranslationLanguageChanged(data) {
    const { participantId, language } = data;
    console.log("onTranslationLanguageChanged", data);
    console.log(
      `Participant ${participantId} changed translation language to ${language}`
    );
  }

  const { startTranslation, stopTranslation, changeTranslationLanguage } =
    useTranslation({
      onTranslationStateChanged,
      onTranslationText,
      onTranslationLanguageChanged,
    });

  // transcription

  // const config = {
  //   webhookUrl: "https://dev2.r-link.com/api/transcription-webhook",
  //   enabled: true,
  //   summary: {
  //     enabled: true,
  //     prompt:
  //       "Write summary in sections like Title, Agenda, Speakers, Action Items, Outlines, Notes and Summary",
  //   },
  // };

  const config = {
    summary: {
      enabled: true,
      prompt:
        "Write summary in sections like Title, Agenda, Speakers, Action Items, Outlines, Notes and Summary",
    },
  };

  function onTranscriptionStateChanged(data) {
    const { status, id } = data;
    console.log("Transcription state changed:", status, id);

    if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTING) {
      console.log("Realtime Transcription is starting", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTED) {
      console.log("Realtime Transcription is started", id);
    } else if (
      status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
    ) {
      console.log("Realtime Transcription is stopping", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPED) {
      console.log("Realtime Transcription is stopped", id);
    }
  }

  // Callback function for transcription text event with smart deduplication
  const onTranscriptionText = (data) => {
    // how to read hooks here?
    let transenabled = translationEnabled;
    setTranslationEnabled((prev) => {
      console.log("prev", prev);
      transenabled = prev;
      return prev;
    });
    console.log("transenabled", transenabled);
    if (transenabled) return;
    let { participantId, participantName, text, timestamp, type } = data;
    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    setTranscriptions({
      participantId,
      participantName,
      text,
      timestamp,
      type,
      profileImage:
        participant?.metaData?.profileImage ||
        participant?.value?.metaData?.profileImage ||
        null,
    });
    return;
    console.log("onTranscriptionText", data);
    // Filter out very short or empty transcriptions
    if (type == "realtime") return;
    if (!text || text.trim().length < 3) return;

    const cleanedText = text.trim();

    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      if (lastTranscription) {
        const timeDiff = timestamp - lastTranscription.timestamp;

        // Within 3 seconds from same person - likely same utterance
        if (timeDiff < 3000) {
          // Exact duplicate
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip
          }

          // New text is extension of previous (progressive transcription)
          if (cleanedText.startsWith(lastTranscription.text)) {
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }

          // Previous text is prefix of new text (refined transcription)
          if (
            lastTranscription.text.startsWith(cleanedText) &&
            lastTranscription.text.length > cleanedText.length
          ) {
            return prev; // Keep the longer one
          }

          // Check for high similarity (likely correction/refinement)
          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.7) {
            // 70% similar - aggressive deduplication
            // Always replace with the latest version within 3 seconds
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }
        }

        // Between 3-8 seconds - check for very similar content
        if (timeDiff >= 3000 && timeDiff < 8000) {
          // Only merge if very similar or identical
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip exact duplicate
          }

          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.9) {
            // Very high similarity
            // Replace with longer version
            if (cleanedText.length > lastTranscription.text.length) {
              return prev.map((t, index) =>
                index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
              );
            }
            return prev; // Keep existing
          }
        }
      }

      const participant =
        mMeeting.participants?.get?.(participantId) ||
        mMeeting.participants[participantId];
      const profileImage =
        participant?.metaData?.profileImage ||
        participant?.value?.metaData?.profileImage ||
        null;

      // Create new transcription entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };

      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  };

  // Calculate text similarity (Levenshtein-based)
  function calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  // Simple edit distance calculation
  function getEditDistance(str1, str2) {
    const costs = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  }

  // Auto-scroll to bottom when new transcriptions arrive
  useEffect(() => {
    if (transcriptionListRef.current) {
      transcriptionListRef.current.scrollTop =
        transcriptionListRef.current.scrollHeight;
    }
  }, [transcriptions]);

  // Hide transcription panel when transcription is turned off
  useEffect(() => {
    if (!translationEnabled) {
      setIsTranscriptionPanelVisible(false);
    }
  }, [translationEnabled]);

  // Adjust panel position on window resize to keep it in bounds
  useEffect(() => {
    const handleResize = () => {
      setPanelPosition((prev) => {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });

      // Update panel size if it exceeds new window width
      setPanelSize((prev) => {
        const maxWidth = window.innerWidth;
        if (prev.width > maxWidth) {
          return {
            ...prev,
            width: Math.max(PANEL_MIN_WIDTH, maxWidth),
          };
        }
        return prev;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [panelSize.width, PANEL_FIXED_HEIGHT, isMobile, PANEL_MOBILE_HEIGHT]);

  // Handle panel dragging and resizing with limits
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        setPanelPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - dragOffset.y)),
        });
      }
      if (isResizing) {
        const panel = transcriptionPanelRef.current;
        if (panel) {
          const rect = panel.getBoundingClientRect();
          // Allow resizing up to full browser width
          const maxWidth = window.innerWidth;
          const panelHeight = isMobile
            ? PANEL_MOBILE_HEIGHT
            : PANEL_FIXED_HEIGHT;
          setPanelSize({
            width: Math.max(
              PANEL_MIN_WIDTH,
              Math.min(maxWidth, e.clientX - rect.left)
            ),
            height: panelHeight, // Keep height fixed
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    dragOffset,
    panelSize.width,
    PANEL_FIXED_HEIGHT,
    isMobile,
    PANEL_MOBILE_HEIGHT,
  ]);

  useEffect(() => {
    if (!isMobile || !isDragging) return;

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!e.touches[0]) return;

      const touch = e.touches[0];
      const panelWidth = panelSize.width;
      const panelHeight = PANEL_MOBILE_HEIGHT;
      const maxX = window.innerWidth - panelWidth;
      const maxY = window.innerHeight - panelHeight;

      const newX = Math.max(
        0,
        Math.min(maxX, touch.clientX - touchDragOffset.x)
      );
      const newY = Math.max(
        0,
        Math.min(maxY, touch.clientY - touchDragOffset.y)
      );

      setMobilePanelPosition({
        x: newX,
        y: window.innerHeight - newY - panelHeight,
      });
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [
    isDragging,
    isMobile,
    touchDragOffset,
    panelSize.width,
    PANEL_MOBILE_HEIGHT,
  ]);

  const handleDragStart = (e) => {
    // Allow dragging from anywhere on the panel, but not from resize handle
    if (e.target.closest(".cursor-ew-resize")) {
      return;
    }
    const panel = transcriptionPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    if (e.target.closest(".cursor-ew-resize")) {
      return;
    }

    const transcriptionList = transcriptionListRef.current;
    if (transcriptionList && transcriptionList.contains(e.target)) {
      const touch = e.touches[0];
      const listRect = transcriptionList.getBoundingClientRect();
      const touchY = touch.clientY - listRect.top;
      const isScrollable =
        transcriptionList.scrollHeight > transcriptionList.clientHeight;

      if (isScrollable) {
        const isNearTop = touchY < 60;
        const isNearBottom = touchY > listRect.height - 60;

        if (!isNearTop && !isNearBottom) {
          return;
        }
      }
    }

    const panel = transcriptionPanelRef.current;
    if (!panel) return;

    const touch = e.touches[0];
    const rect = panel.getBoundingClientRect();
    setTouchDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  // Whiteboard drag and resize handlers
  const handleWhiteboardDragStart = (e) => {
    const whiteboard = whiteboardRef.current;
    if (whiteboard && !isWhiteboardResizing) {
      const rect = whiteboard.getBoundingClientRect();
      setWhiteboardDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsWhiteboardDragging(true);
    }
  };

  const handleWhiteboardResizeStart = (e, handle) => {
    e.stopPropagation();
    setWhiteboardResizeHandle(handle);
    setIsWhiteboardResizing(true);
  };

  // Handle whiteboard dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isWhiteboardDragging) {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        setWhiteboardPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - whiteboardDragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - whiteboardDragOffset.y)),
        });
      }

      if (isWhiteboardResizing && whiteboardResizeHandle) {
        const whiteboard = whiteboardRef.current;
        if (whiteboard) {
          const rect = whiteboard.getBoundingClientRect();
          const minWidth = 400;
          const minHeight = 300;
          const maxWidth = window.innerWidth - rect.left;
          const maxHeight = window.innerHeight - rect.top;

          let newWidth = whiteboardSize.width;
          let newHeight = whiteboardSize.height;

          switch (whiteboardResizeHandle) {
            case "se": // southeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "sw": // southwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "ne": // northeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "nw": // northwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "e": // east
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              break;
            case "w": // west
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              break;
            case "s": // south
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "n": // north
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            default:
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
          }

          setWhiteboardSize({ width: newWidth, height: newHeight });

          // Adjust position for handles that resize from left or top
          if (["sw", "nw", "w", "n"].includes(whiteboardResizeHandle)) {
            const widthDiff = newWidth - whiteboardSize.width;
            const heightDiff = newHeight - whiteboardSize.height;

            let newX = whiteboardPosition.x;
            let newY = whiteboardPosition.y;

            if (["sw", "nw", "w"].includes(whiteboardResizeHandle)) {
              newX = whiteboardPosition.x - widthDiff;
            }
            if (["nw", "n"].includes(whiteboardResizeHandle)) {
              newY = whiteboardPosition.y - heightDiff;
            }

            setWhiteboardPosition({
              x: Math.max(0, newX),
              y: Math.max(0, newY),
            });
          }
        }
      }
    };

    const handleMouseUp = () => {
      setIsWhiteboardDragging(false);
      setIsWhiteboardResizing(false);
      setWhiteboardResizeHandle(null);
    };

    if (isWhiteboardDragging || isWhiteboardResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isWhiteboardDragging,
    isWhiteboardResizing,
    whiteboardDragOffset,
    whiteboardResizeHandle,
    whiteboardSize,
    whiteboardPosition,
  ]);

  // Keep whiteboard within bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      setWhiteboardPosition((prev) => {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [whiteboardSize]);

  // Passing callback functions to useTranscription hook
  const { startTranscription, stopTranscription } = useTranscription({
    onTranscriptionStateChanged,
    onTranscriptionText,
  });

  useEffect(() => {
    if (meetingRole && ["moderator", "co-host", "host"].includes(meetingRole)) {
      getFolderElementList();
    }
  }, [meetingRole, meetingSettings]);

  const getFolderElementList = async () => {
    try {
      setIsElementRefreshing(true);
      const response = await getFolderElements(meetingSettings?.roomId);
      console.log("response", response.folders);
      if (response.folders) {
        setFolderElementList(response.folders);
      }
      setIsElementRefreshing(false);
    } catch (error) {
      setIsElementRefreshing(false);
    }
  };

  return (
    <BreakoutRoomProvider
      meetingId={meetingSettings?.roomId}
      localParticipantId={mMeeting?.localParticipant?.id}
      localParticipantName={mMeeting?.localParticipant?.displayName || user?.name || 'User'}
      meetingRole={meetingRole}
      hostUserId={user?.id}
      localMicOn={mMeeting?.localMicOn}
      localWebcamOn={mMeeting?.localWebcamOn}
    >
    <div className={recorderLayoutOverride ? "absolute inset-0" : "fixed inset-0"}>
      {/* Grid-recorder cells use `absolute inset-0` so each of the 4 sibling
          MeetingContainerNew instances fills its own 1280x720 parent cell
          (the wrapper in Conference.js forceGrid branch is position:relative).
          `fixed inset-0` would make every cell stack at viewport size, which
          is what caused the earlier "single window, cropped 4 ways" bug. */}
      {vbApplyPending && (
        <div className={(recorderLayoutOverride ? "absolute" : "fixed") + " inset-0 z-[9999] flex items-center justify-center bg-[#001233]"}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-white/20 border-t-[#6a1fbf] rounded-full animate-spin" />
            <p className="text-white text-sm">Applying your virtual background…</p>
          </div>
        </div>
      )}
      {/* iOS media consent prompt — temporarily disabled for debugging */}
      {/* <IOSMediaConsent /> */}
      <PrivacyNoticeModal
        open={privacyNoticeOpen}
        onClose={() => setPrivacyNoticeOpen(false)}
        onDisablePrivacy={() => {
          meetingState.emitPrivacyUpdate?.(false);
          setPrivacyNoticeOpen(false);
        }}
      />
      {/* Cha-ching sound for rally token reward */}
      <audio preload="auto" ref={chaChingSoundRef} src="/audio/cha-ching.mp3" />
      {/* Rally Token Confetti Celebration Overlay */}
      <RewardCelebration
        show={showRewardCelebration}
        variant={rewardCelebrationVariant}
        onComplete={() => setShowRewardCelebration(false)}
      />
      <div ref={containerRef} className="h-full flex flex-col bg-[#001233]">
        {showPollResults && (
          <PollResults onClose={() => {
            setShowPollResults(false);
            // Also close poll for all participants
            const pollId = pollResults?.id || pollResults?._id || activePoll?._id || activePoll?.id;
            if (pollId) {
              // End the meeting poll vote session in the database
              if (mMeeting?.meetingId && meetingSession) {
                pollServices.endMeetingPollVote({
                  meetingId: mMeeting.meetingId,
                  sessionId: meetingSession,
                  pollId: pollId,
                }).catch(err => console.warn("Failed to end meeting poll vote session:", err));
              }
              // Broadcast end to close poll modal for all participants
              publishPoll({
                type: "end",
                clear: true,
                pollId: pollId,
                timestamp: Date.now(),
              }, { persist: true });
              meetingState.emitElementRemove(pollId);
            }
            // Clean up local poll state
            setShowPollModal(false);
            setActivePoll(null);
            setIsPollLauncher(false);
            setPollResults(null);
          }} />
        )}

        {/* Waiting Room Panel for Hosts - Hidden: now integrated in attendees section */}
        {false && isHost && waitingRoomEnabled && waitingParticipants.length > 0 && (
          <WaitingParticipantsPanel
            waitingParticipants={waitingParticipants}
            onAdmit={admitParticipant}
            onAdmitAll={admitAllParticipants}
            onDeny={denyParticipant}
            onClose={() => setShowWaitingRoomPanel(false)}
            rightSidebarCollapsed={rightSidebarCollapsed}
          />
        )}

        {/* Old ElementSection panel removed — elements are now managed via StudioLeftSidebar */}
        {/* CTA Section moved inside meeting-stage-area for flex layout */}
        {activeElement && (activeElement.type === "talking_point" || activeElement.type === "talkingpoint") && (
          <TalkingPointSection
            sideBarContainerWidth={sideBarContainerWidth}
            leftSidebarCollapsed={leftSidebarCollapsed}
            rightSidebarCollapsed={rightSidebarCollapsed}
          />
        )}
        {/* Video element - fullscreen and background modes render here, PiP mode renders in ParticipantView */}
        {!useInlineRecorderElements && activeElement && activeElement.type === "video" && (() => {
          const videoSettings = typeof activeElement.settings === 'string'
            ? JSON.parse(activeElement.settings)
            : activeElement.settings;
          const displayMode = videoSettings?.display_mode || 'fullscreen';
          // Only render VideoSection for fullscreen and background modes
          // PiP mode renders inside the participant's tile (ParticipantView.js)
          if (displayMode === 'fullscreen' || displayMode === 'background') {
            // Check if the local user is the one who triggered the video
            const localId = mMeeting.localParticipant?.id;
            const senderId = activeElement.senderParticipantId;
            const isVideoOwner = senderId && localId && (
              senderId === localId
              || String(senderId) === String(localId)
              || String(stringToNumericUid(senderId)) === String(localId)
              || String(senderId) === String(stringToNumericUid(localId))
            );
            return (
              <VideoSection
                key={activeElement._id || activeElement.id || 'video'}
                settings={activeElement.settings}
                isOwner={!!isVideoOwner}
                forceMuted={isSecondaryRecorderCell}
                forceHide={isSecondaryRecorderCell}
                onClosePanel={() => {
                  // Close the video element - notify other participants if moderator
                  const elementId = activeElement?._id || activeElement?.id;
                  setActiveElement(null);
                  if (meetingRole === "moderator" || meetingRole === "co-host") {
                    publishCloseElementSection(
                      JSON.stringify({
                        set: true,
                      })
                    );
                    // Clear persisted element so late joiners don't see stale element
                    publishShowElement(JSON.stringify({ clear: true }), { persist: true });
                    if (elementId) meetingState.emitElementRemove(elementId);
                  }
                }}
                sideBarContainerWidth={sideBarContainerWidth}
                leftSidebarCollapsed={leftSidebarCollapsed}
                rightSidebarCollapsed={rightSidebarCollapsed}
                isRecorderMode={isRecorderMode}
              />
            );
          }
          return null;
        })()}
        {!useInlineRecorderElements && activeElement && activeElement.type === "website" && (
          <WebsiteSection
            settings={JSON.parse(activeElement.settings)}
            canClose={meetingRole === "moderator" || meetingRole === "co-host"}
            onClose={() => {
              if (meetingRole === "moderator" || meetingRole === "co-host") {
                const elementId = activeElement?._id || activeElement?.id;
                publishCloseElementSection(JSON.stringify({ set: true }));
                publishShowElement(JSON.stringify({ clear: true }), { persist: true });
                if (elementId) meetingState.emitElementRemove(elementId);
              }
              setActiveElement(null);
            }}
            sideBarContainerWidth={sideBarContainerWidth}
            leftSidebarCollapsed={isRecorderMode ? true : leftSidebarCollapsed}
            rightSidebarCollapsed={isRecorderMode ? true : rightSidebarCollapsed}
            isRecorderMode={isRecorderMode}
          />
        )}
        {!useInlineRecorderElements && activeElement && (activeElement.type === "weboverlay" || activeElement.type === "web_overlay") && (
          <WebsiteSection
            settings={{
              link: activeElement.url || activeElement.link || '',
              mode: activeElement.mode || 'interactive',
              displayMode: activeElement.position === 'fullscreen' ? 'full' : 'half',
              position: activeElement.position || 'fullscreen'
            }}
            canClose={meetingRole === "moderator" || meetingRole === "co-host"}
            onClose={() => {
              const elementId = activeElement?._id || activeElement?.id;
              if (meetingRole === "moderator" || meetingRole === "co-host") {
                publishCloseElementSection(JSON.stringify({ set: true }));
                publishShowElement(JSON.stringify({ clear: true }), { persist: true });
                if (elementId) meetingState.emitElementRemove(elementId);
              }
              setActiveElement(null);
            }}
            sideBarContainerWidth={sideBarContainerWidth}
            leftSidebarCollapsed={isRecorderMode ? true : leftSidebarCollapsed}
            rightSidebarCollapsed={isRecorderMode ? true : rightSidebarCollapsed}
            isRecorderMode={isRecorderMode}
          />
        )}
        {activeElement && activeElement.type === "audio" && (
          <AudioPlayerBar
            audioData={activeElement}
            isHost={meetingRole === "moderator" || meetingRole === "co-host"}
            onStop={() => {
              const elementId = activeElement?._id || activeElement?.id;
              setActiveElement(null);
              if (meetingRole === "moderator" || meetingRole === "co-host") {
                publishCloseElementSection(JSON.stringify({ set: true }));
                publishShowElement(JSON.stringify({ clear: true }), { persist: true });
                if (elementId) meetingState.emitElementRemove(elementId);
              }
            }}
          />
        )}
        {!useInlineRecorderElements && activeElement && activeElement.type == "document" && (
          <DocumentViewer
            fileUrl={JSON.parse(activeElement.settings).fileUrl}
            onClosePanel={() => {
              if (meetingRole === "moderator" || meetingRole === "co-host") {
                const elementId = activeElement?._id || activeElement?.id;
                publishCloseElementSection(JSON.stringify({ set: true }));
                publishShowElement(JSON.stringify({ clear: true }), { persist: true });
                if (elementId) meetingState.emitElementRemove(elementId);
              }
              setActiveElement(null);
            }}
            sideBarContainerWidth={sideBarContainerWidth}
          />
        )}
        {/* Contract Renderer is rendered inside meeting-stage-area below */}
        {/* Poll is now shown via PollVotingModal for both moderator and participants */}
        {/* Teleprompter - Host & Co-Host */}
        {prompterElement && (meetingRole === "moderator" || meetingRole === "co-host") && (
          <PrompterViewer
            prompter={prompterElement}
            isOpen={!!prompterElement}
            sttLiveText={sttLiveText}
            sttBotActive={sttBotActive}
            onEnsureStt={handleStartNotetaker}
            onClose={() => {
              const elementId = prompterElement?._id || prompterElement?.id;
              setPrompterElement(null);
              // Notify other participants
              publishCloseElementSection(JSON.stringify({ type: 'prompter', clear: true }));
              // Clear persisted element so late joiners don't see stale prompter
              publishShowElement(JSON.stringify({ clear: true }), { persist: true });
              if (elementId) meetingState.emitElementRemove(elementId);
            }}
            windowMode={prompterElement?.settings?.windowMode || "docked"}
          />
        )}
        {/* Presentation Element */}
        {!useInlineRecorderElements && activePresentation && (
          <PresentationStageRenderer
            presentation={activePresentation}
            presentationState={presentationState}
            isHost={meetingRole === "moderator" || meetingRole === "co-host" || isPresentationLauncher}
            leftSidebarCollapsed={isRecorderMode ? true : leftSidebarCollapsed}
            rightSidebarCollapsed={isRecorderMode ? true : rightSidebarCollapsed}
            isRecorderMode={isRecorderMode}
            onStateUpdate={(newState) => {
              setPresentationState(newState);
              // Broadcast state update to all participants (launcher or moderator/co-host can control)
              const canControl = meetingRole === "moderator" || meetingRole === "co-host" || isPresentationLauncher;
              if (canControl) {
                const presentationId = activePresentation._id || activePresentation.id;
                publishPresentation({
                  type: "state_update",
                  presentationId,
                  presentation: activePresentation,
                  state: newState,
                  timestamp: Date.now(),
                }, { persist: true });
                // Also sync page change to Socket.IO meeting state for late joiners and RTM fallback
                meetingState.emitElementUpdate(presentationId, {
                  presentation: activePresentation,
                  state: newState,
                });
              }
            }}
            onPresentationEnd={() => {
              const presentationId = activePresentation._id || activePresentation.id;
              setActivePresentation(null);
              setIsPresentationLauncher(false);
              setPresentationState({ currentSlide: 1, status: 'idle' });
              // Launcher or moderator/co-host can end the presentation
              const canControl = meetingRole === "moderator" || meetingRole === "co-host" || isPresentationLauncher;
              if (canControl) {
                publishPresentation({
                  type: "end",
                  clear: true,
                  presentationId,
                  timestamp: Date.now(),
                }, { persist: true });
                meetingState.emitElementRemove(presentationId);
              }
            }}
          />
        )}
        {typeof localParticipantAllowedJoin === "boolean" &&
          showJoinScreen == false ? (
          localParticipantAllowedJoin ? (
            <>
              {/* <BottomBar
                                bottomBarHeight={bottomBarHeight}
                                setIsMeetingLeft={setIsMeetingLeft}
                            /> */}
              {!isRecorderMode && (
                <CrossRoomWaitingBanner
                  queues={crossRoomQueues}
                  onDismiss={(mid) =>
                    setCrossRoomQueues((prev) => prev.filter((q) => q.meetingId !== mid))
                  }
                  onAdmitAll={(targetMeetingId) =>
                    new Promise((resolve) => {
                      const sock = waitingRoomSocketRef.current;
                      if (!sock) {
                        resolve({ success: false, message: 'not connected' });
                        return;
                      }
                      sock.emit(
                        'cross-room-admit-all',
                        {
                          targetMeetingId,
                          requesterUserId: mMeeting?.localParticipant?.id,
                        },
                        (resp) => {
                          if (resp?.success) {
                            setCrossRoomQueues((prev) =>
                              prev.filter((q) => q.meetingId !== targetMeetingId),
                            );
                          }
                          resolve(resp || { success: false });
                        },
                      );
                    })
                  }
                />
              )}
              {!isRecorderMode && <TopBar
                setIsMeetingLeft={setIsMeetingLeft}
                setCustomVideoStream={setCustomVideoStream}
                micOn={micOn}
                setMicOn={setMicOn}
                webcamOn={webcamOn}
                setWebcamOn={setWebcamOn}
                customAudioStream={customAudioStream}
                setCustomAudioStream={setCustomAudioStream}
                customVideoStream={customVideoStream}
                startTranscription={() => startTranscription(config)}
                stopTranscription={stopTranscription}
                handleToggleAICaptions={handleToggleAICaptions}
                aiCaptionsEnabled={aiCaptionsEnabled}
                startTranslation={startTranslation}
                stopTranslation={stopTranslation}
                changeTranslationLanguage={changeTranslationLanguage}
                setShowPollResults={setShowPollResults}
                showPollResults={showPollResults}
                publishMeetingSettings={publishMeetingSettings}
                admitAllWaitingParticipants={admitAllParticipants}
                showRewardsPanel={showRewardsPanel}
                setShowRewardsPanel={setShowRewardsPanel}
                onStartNotetaker={handleStartNotetaker}
                onStopNotetaker={handleStopNotetaker}
              />}
              <div
                id="meeting-stage-area"
                ref={meetingAreaRef}
                className={`relative flex flex-1 ${ctaIsHorizontal ? 'flex-col' : 'flex-row'} bg-[#001233] ${isRecorderMode ? 'p-0' : 'p-2 md:p-3'} ${isRecorderMode ? '' : isMobileLike ? (isHost ? 'ml-10' : '') : !leftSidebarCollapsed ? "ml-64 md:ml-[380px]" : "ml-14"
                  } ${isRecorderMode ? '' : isMobileLike ? 'mr-10' : !rightSidebarCollapsed ? "mr-72 md:mr-80" : "mr-14"
                  } transition-all duration-300 ${isRecorderMode ? '' : isMobileLandscape
                    ? `pr-20 pb-16`
                    : isMobileLike
                      ? "pb-16"
                      : "pb-16 md:pb-20"
                  }`}
                style={{
                  // When presenting with participant strip visible, add extra bottom padding
                  // When presenting with participant strip visible, add extra bottom padding for the bottom bar
                  ...(isPresenting && !isBottomParticipantCollapsed && {
                    paddingBottom: isMobileLandscape ? "96px" : "100px",
                  }),
                  ...(isMobileLandscape && {
                    paddingRight: `${(isMobilePanelOpen ? mobilePanelWidth : 0) + 80}px`,
                  }),
                  backgroundColor: "#001233",
                }}
              >
                {/* Contract Renderer - inside meeting-stage-area so it overlays only the center content */}
                {(() => {
                  if (useInlineRecorderElements) return null;
                  const isHostRole = meetingRole === "moderator" || meetingRole === "co-host";
                  const contractData = isHostRole ? activeContractView : (activeElement?.type === "contract" ? activeElement : null);
                  if (!contractData) return null;
                  return (
                    <ContractRenderer
                      contract={contractData}
                      onClose={() => {
                        const cid = contractData?._id || contractData?.id;
                        // Close locally
                        if (isHostRole) {
                          setActiveContractView(null);
                        }
                        setActiveElement(null);
                        lastContractElementIdRef.current = null;
                        // Also clean up on participant side — otherwise they
                        // stay stuck on the old contract and re-present won't
                        // work (same contract ID gets ignored). Use Minimize
                        // instead if you want to keep the contract open for
                        // participants while hiding it on the host.
                        if (isHostRole) {
                          publishShowElement(JSON.stringify({ clear: true }), { persist: true })
                            .catch(() => {});
                          if (cid) {
                            meetingState.emitElementRemove(cid);
                          }
                        }
                      }}
                      onUpdate={(updatedContract) => {
                        if (isHostRole) {
                          setActiveContractView(updatedContract);
                          const contractId = updatedContract._id || updatedContract.id;
                          if (contractId) {
                            // Strip ALL filled field values before persisting
                            // the Element template. onUpdate here fires on
                            // submission — it should never carry live host or
                            // participant fills back onto the reusable
                            // template. Host pre-fill defaults are managed
                            // separately in the ContractModal editor flow.
                            const sanitizedFields = (updatedContract.editable_fields || []).map((f) => {
                              const { value, ...rest } = f;
                              return rest;
                            });
                            folderService.updateElement(contractId, {
                              ...updatedContract,
                              editable_fields: sanitizedFields,
                              type: 'contract',
                            }).catch(err => console.error('Failed to update contract:', err));
                          }
                        } else {
                          setActiveElement(prev => ({ ...prev, ...updatedContract }));
                        }
                      }}
                      isHost={isHostRole}
                      currentUserName={mMeeting?.localParticipant?.displayName || (isHostRole ? 'Host' : 'Participant')}
                      currentUserId={user?.id || user?._id || mMeeting?.localParticipant?.id || ''}
                      currentUserEmail={user?.email || ''}
                      meetingId={meetingSettings?.roomId || mMeeting?.meetingId || ''}
                      onSendToParticipants={async (contractPayload) => {
                        // Persist via Socket.IO meeting state FIRST (reliable + late joiners)
                        // This runs synchronously (fire-and-forget emit) so it never blocks.
                        meetingState.emitElementAdd({
                          id: contractPayload._id || contractPayload.id,
                          type: 'contract',
                          config: { elementData: contractPayload },
                          addedAt: Date.now(),
                        });
                        // Also publish to participants via PubSub (parallel delivery).
                        // Don't await — RTM sendCommand/persist can hang if the
                        // channel isn't fully ready, which blocks the toast + audit.
                        publishShowElement(JSON.stringify(contractPayload), { persist: true })
                          .then(() => console.log('[Contract] PubSub publish succeeded'))
                          .catch((err) => console.error('[Contract] PubSub publish failed:', err));
                      }}
                      onStopPresenting={() => {
                        const contractId = contractData?._id || contractData?.id;
                        // Clear for participants via PubSub
                        publishShowElement(JSON.stringify({ clear: true }), { persist: true })
                          .catch(() => {});
                        // Remove from Socket.IO meeting state
                        if (contractId) {
                          meetingState.emitElementRemove(contractId);
                        }
                        // Also close the contract locally on the host so
                        // the host's view returns to the meeting grid.
                        if (isHostRole) {
                          setActiveContractView(null);
                        }
                        setActiveElement(null);
                        lastContractElementIdRef.current = null;
                      }}
                    />
                  );
                })()}
                {/* CTA Banner - rendered as flex sibling to push video grid */}
                {!useInlineRecorderElements && activeElement && activeElement.type === "cta" && (
                  <CtaSection
                    sideBarContainerWidth={sideBarContainerWidth}
                    leftSidebarCollapsed={leftSidebarCollapsed}
                    rightSidebarCollapsed={rightSidebarCollapsed}
                  />
                )}
                {isPresenting && !isRecorderMode && (() => {
                  // Layout-aware presenter rendering. The 4 modes match the bottom-bar
                  // layout picker so the host/co-host can change view while presenting.
                  // - speaker-top (default): screen on top, participant strip below (collapsible)
                  // - speaker-side: screen on the left, participant column on the right
                  // - focus: screen only, no participant strip
                  // - gallery: screen on top, participant strip below (same as speaker-top
                  //   for now — both show participants in a horizontal strip)
                  const presenterLayout = selectedLayout || "speaker-top";
                  const isFocusMode = presenterLayout === "focus";
                  const isSideMode = presenterLayout === "speaker-side";
                  const showStrip = !isFocusMode && !isMobile;
                  const sideStripWidth = 280;
                  const presenterHeight = isMobileLandscape
                    ? Math.max(0, containerHeight - bottomBarHeight)
                    : isMobileLike
                      ? Math.max(0, containerHeight - 96)
                      : isFocusMode || isSideMode
                        ? Math.max(0, containerHeight - bottomBarHeight)
                        : Math.max(
                          0,
                          containerHeight -
                          bottomBarHeight -
                          (isBottomParticipantCollapsed ? 0 : 270)
                        );
                  return (
                    <div
                      className={`flex flex-1 ${isSideMode ? "flex-row" : "flex-col"} ${isMobileLandscape ? "overflow-hidden" : ""}`}
                      style={{
                        ...(isMobileLandscape && {
                          maxHeight: "100%",
                          height: "100%",
                        }),
                      }}
                    >
                      {participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))}

                      <div className={isSideMode ? "flex-1 min-w-0" : ""}>
                        <PresenterView height={presenterHeight}>
                          {showStrip && !isSideMode && (
                            <button
                              onClick={() =>
                                setIsBottomParticipantCollapsed(
                                  !isBottomParticipantCollapsed
                                )
                              }
                              className={`absolute z-50 bottom-3 right-3 bg-[#001233]/80 hover:bg-[#0a1628] text-white rounded-full p-2 shadow-lg transition-all duration-200 flex items-center justify-center ${
                                isBottomParticipantCollapsed
                                  ? "expand-button-animate"
                                  : "collapse-button-animate"
                              }`}
                              title={
                                isBottomParticipantCollapsed
                                  ? "Expand participants"
                                  : "Collapse participants"
                              }
                              style={{
                                width: "36px",
                                height: "36px",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-5 h-5 arrow-pulse-animate"
                              >
                                {isBottomParticipantCollapsed ? (
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4.5 18.75l7.5-7.5 7.5 7.5m-15-6l7.5-7.5 7.5 7.5"
                                  />
                                ) : (
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5"
                                  />
                                )}
                              </svg>
                            </button>
                          )}
                        </PresenterView>
                      </div>

                      {showStrip && isSideMode && (
                        <div
                          className="flex-shrink-0 flex flex-col gap-2 overflow-y-auto p-2"
                          style={{ width: sideStripWidth }}
                        >
                          <WebinarGridComponent
                            isPresenting={isPresenting}
                            isFlexCol={true}
                            layout="speaker-side"
                          />
                        </div>
                      )}

                      {showStrip && !isSideMode && !isBottomParticipantCollapsed && (
                        <div
                          className={`relative ${isMobileLandscape ? "pb-2" : ""}`}
                          style={{
                            height: isMobileLandscape ? "160px" : "230px",
                            minHeight: isMobileLandscape ? "160px" : "210px",
                            maxHeight: isMobileLandscape ? "180px" : "250px",
                            overflow: "visible",
                          }}
                        >
                          <WebinarGridComponent
                            isPresenting={isPresenting}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {selectedLayout === "gallery" && (!isPresenting || isRecorderMode) && (
                  <div
                    className={`flex flex-1 ${isPresenting ? "flex-col" : ""}`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <WebinarGridComponent isPresenting={isPresenting} />
                    )}
                  </div>
                )}

                {selectedLayout === "focus" && (!isPresenting || isRecorderMode) && (
                  <div
                    className={`flex flex-1 ${isPresenting ? "flex-col" : ""}`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <div className="relative flex flex-1 min-h-0 h-full">
                        <FocusParticipantView isPresenting={isPresenting} />
                        {useInlineRecorderElements && (
                          <div className="recorder-inline-slot absolute inset-0 z-30 overflow-hidden" style={{ transform: 'translateZ(0)' }}>
                            {activeElement?.type === 'website' && (
                              <WebsiteSection settings={JSON.parse(activeElement.settings)} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                            )}
                            {(activeElement?.type === 'weboverlay' || activeElement?.type === 'web_overlay') && (
                              <WebsiteSection settings={{ link: activeElement.url || activeElement.link || '', mode: activeElement.mode || 'interactive', displayMode: activeElement.position === 'fullscreen' ? 'full' : 'half', position: activeElement.position || 'fullscreen' }} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                            )}
                            {activePresentation && (
                              <PresentationStageRenderer presentation={activePresentation} presentationState={presentationState} isHost={false} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} onStateUpdate={() => {}} onPresentationEnd={() => {}} />
                            )}
                            {(() => { const cd = activeContractView || (activeElement?.type === 'contract' ? activeElement : null); return cd ? <ContractRenderer contract={cd} isHost={false} currentUserName="Recorder" currentUserId="" currentUserEmail="" meetingId={meetingSettings?.roomId || mMeeting?.meetingId || ''} isRecorderMode={true} /> : null; })()}
                            {activeElement?.type === 'cta' && (
                              <CtaSection sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                            )}
                            {activeElement?.type === 'video' && (() => {
                              const vs = typeof activeElement.settings === 'string' ? JSON.parse(activeElement.settings) : activeElement.settings;
                              const dm = vs?.display_mode || 'fullscreen';
                              return (dm === 'fullscreen' || dm === 'background') ? (
                                <VideoSection key={activeElement._id || activeElement.id || 'video'} settings={activeElement.settings} isOwner={false} sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} />
                              ) : null;
                            })()}
                            {activeElement?.type === 'document' && (
                              <DocumentViewer fileUrl={JSON.parse(activeElement.settings).fileUrl} sideBarContainerWidth={0} />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectedLayout === "speaker-top" && (!isPresenting || isRecorderMode) && (
                  <div className={`flex flex-1 flex-col`}>
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <div className="flex flex-col flex-1 h-full overflow-hidden p-3 gap-3">
                        {/* Main speaker area - takes remaining space */}
                        <div className="relative flex-1 min-h-0">
                          <SpeakerParticipantView
                            isPresenting={isPresenting}
                          />
                          {useInlineRecorderElements && (
                            <div className="recorder-inline-slot absolute inset-0 z-30 overflow-hidden" style={{ transform: 'translateZ(0)' }}>
                              {activeElement?.type === 'website' && (
                                <WebsiteSection settings={JSON.parse(activeElement.settings)} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {(activeElement?.type === 'weboverlay' || activeElement?.type === 'web_overlay') && (
                                <WebsiteSection settings={{ link: activeElement.url || activeElement.link || '', mode: activeElement.mode || 'interactive', displayMode: activeElement.position === 'fullscreen' ? 'full' : 'half', position: activeElement.position || 'fullscreen' }} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {activePresentation && (
                                <PresentationStageRenderer presentation={activePresentation} presentationState={presentationState} isHost={false} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} onStateUpdate={() => {}} onPresentationEnd={() => {}} />
                              )}
                              {(() => { const cd = activeContractView || (activeElement?.type === 'contract' ? activeElement : null); return cd ? <ContractRenderer contract={cd} isHost={false} currentUserName="Recorder" currentUserId="" currentUserEmail="" meetingId={meetingSettings?.roomId || mMeeting?.meetingId || ''} isRecorderMode={true} /> : null; })()}
                              {activeElement?.type === 'cta' && (
                                <CtaSection sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {activeElement?.type === 'video' && (() => {
                                const vs = typeof activeElement.settings === 'string' ? JSON.parse(activeElement.settings) : activeElement.settings;
                                const dm = vs?.display_mode || 'fullscreen';
                                return (dm === 'fullscreen' || dm === 'background') ? (
                                  <VideoSection key={activeElement._id || activeElement.id || 'video'} settings={activeElement.settings} isOwner={false} sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} />
                                ) : null;
                              })()}
                              {activeElement?.type === 'document' && (
                                <DocumentViewer fileUrl={JSON.parse(activeElement.settings).fileUrl} sideBarContainerWidth={0} />
                              )}
                            </div>
                          )}
                        </div>
                        {/* Bottom filmstrip - for thumbnails */}
                        <div className="flex-shrink-0">
                          <div className="flex flex-row items-center justify-start gap-3 overflow-x-auto">
                            <WebinarGridComponent
                              isPresenting={true}
                              layout="speaker-top"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedLayout === "speaker-side" && (!isPresenting || isRecorderMode) && (
                  <div
                    className={`flex flex-1 ${isPresenting ? "flex-col" : ""}`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <div className="flex flex-row flex-1 h-full overflow-hidden p-3 gap-3">
                        {/* Main speaker area - takes remaining space */}
                        <div className="relative flex-1 min-w-0 h-full">
                          <SpeakerSideParticipantView
                            isPresenting={isPresenting}
                          />
                          {useInlineRecorderElements && (
                            <div className="recorder-inline-slot absolute inset-0 z-30 overflow-hidden" style={{ transform: 'translateZ(0)' }}>
                              {activeElement?.type === 'website' && (
                                <WebsiteSection settings={JSON.parse(activeElement.settings)} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {(activeElement?.type === 'weboverlay' || activeElement?.type === 'web_overlay') && (
                                <WebsiteSection settings={{ link: activeElement.url || activeElement.link || '', mode: activeElement.mode || 'interactive', displayMode: activeElement.position === 'fullscreen' ? 'full' : 'half', position: activeElement.position || 'fullscreen' }} canClose={false} isRecorderMode={true} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {activePresentation && (
                                <PresentationStageRenderer presentation={activePresentation} presentationState={presentationState} isHost={false} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} onStateUpdate={() => {}} onPresentationEnd={() => {}} />
                              )}
                              {(() => { const cd = activeContractView || (activeElement?.type === 'contract' ? activeElement : null); return cd ? <ContractRenderer contract={cd} isHost={false} currentUserName="Recorder" currentUserId="" currentUserEmail="" meetingId={meetingSettings?.roomId || mMeeting?.meetingId || ''} isRecorderMode={true} /> : null; })()}
                              {activeElement?.type === 'cta' && (
                                <CtaSection sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} />
                              )}
                              {activeElement?.type === 'video' && (() => {
                                const vs = typeof activeElement.settings === 'string' ? JSON.parse(activeElement.settings) : activeElement.settings;
                                const dm = vs?.display_mode || 'fullscreen';
                                return (dm === 'fullscreen' || dm === 'background') ? (
                                  <VideoSection key={activeElement._id || activeElement.id || 'video'} settings={activeElement.settings} isOwner={false} sideBarContainerWidth={0} leftSidebarCollapsed={true} rightSidebarCollapsed={true} isRecorderMode={true} />
                                ) : null;
                              })()}
                              {activeElement?.type === 'document' && (
                                <DocumentViewer fileUrl={JSON.parse(activeElement.settings).fileUrl} sideBarContainerWidth={0} />
                              )}
                            </div>
                          )}
                        </div>
                        {/* Right sidebar filmstrip - fixed width for thumbnails */}
                        <div className="w-[200px] md:w-[220px] flex-shrink-0 flex flex-col gap-3 overflow-y-auto">
                          <WebinarGridComponent
                            isPresenting={true}
                            isFlexCol={true}
                            layout="speaker-side"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Floating presenter camera PiP — shown to everyone whenever
                    an element is actively being presented (PDF, PPT, XLS,
                    Website, WebOverlay, Contract, Video, etc.), so the
                    audience never loses sight of the speaker even when the
                    element covers the gallery. Also fires for the
                    prompter/teleprompter and polls so hosts see themselves
                    while presenting.
                    Hidden in recorder mode entirely — the active speaker is
                    already visible in the speaker-top / speaker-side / focus
                    cells of the grid, so a floating overlay is redundant AND
                    its Document-PiP auto-popout misbehaves in headless Xvfb
                    Chrome (captures as a black broken window).
                    If the element didn't carry a senderParticipantId (the
                    replay path sets it to the string 'unknown'), fall
                    back to the local user when they're the host, else
                    the first moderator in the participant map. */}
                {!isRecorderMode && (() => {
                  const hasFullStageElement =
                    !!activeElement ||
                    !!prompterElement ||
                    !!activePoll ||
                    !!activePresentation ||
                    !!activeContractView ||
                    !!activeLeadCapture;
                  if (!hasFullStageElement) return null;
                  let presenterId = activeElement?.senderParticipantId;
                  if (!presenterId || presenterId === "unknown") {
                    if (meetingRole === "moderator" || meetingRole === "co-host") {
                      presenterId = mMeeting?.localParticipant?.id || null;
                    } else {
                      // Find the first moderator among known participants
                      const parts = mMeeting?.participants;
                      if (parts && typeof parts.forEach === "function") {
                        parts.forEach((p, id) => {
                          if (presenterId) return;
                          const role = p?.metaData?.meetingRole || p?.meetingRole;
                          if (role === "moderator" || role === "co-host") {
                            presenterId = id;
                          }
                        });
                      }
                    }
                  }
                  if (!presenterId) return null;
                  return <PresenterCameraPiP participantId={presenterId} />;
                })()}

                {/* Timer Element - inside meeting area so it stays within bounds */}
                {activeTimer && (
                  <TimerStageRenderer
                    timer={activeTimer}
                    timerState={timerState}
                    isHost={meetingRole === "moderator" || meetingRole === "co-host" || isTimerLauncher}
                    meetingAreaRef={meetingAreaRef}
                    onStateUpdate={(newState) => {
                      setTimerState(newState);
                      const canControl = meetingRole === "moderator" || meetingRole === "co-host" || isTimerLauncher;
                      if (canControl) {
                        const timerId = activeTimer._id || activeTimer.id;
                        publishTimer({
                          type: "state_update",
                          timerId,
                          timer: activeTimer,
                          state: newState,
                          timestamp: Date.now(),
                        }, { persist: true });
                        // Sync to Socket.IO meeting state so late joiners get current timer state
                        meetingState.emitElementUpdate(timerId, { state: newState });
                      }
                    }}
                    onTimerEnd={() => {
                      const timerId = activeTimer._id || activeTimer.id;
                      setActiveTimer(null);
                      setIsTimerLauncher(false);
                      setTimerState({ status: 'idle' });
                      const canControl = meetingRole === "moderator" || meetingRole === "co-host" || isTimerLauncher;
                      if (canControl) {
                        publishTimer({
                          type: "end",
                          clear: true,
                          timerId,
                          timestamp: Date.now(),
                        }, { persist: true });
                        meetingState.emitElementRemove(timerId);
                      }
                    }}
                  />
                )}

                <SidebarConatiner
                  height={containerHeight - bottomBarHeight}
                  sideBarContainerWidth={sideBarContainerWidth}
                />
              </div>
              {/* Agora Interactive Whiteboard - Uses CDN-loaded SDK */}
              {(isWhiteboardOpen || isWhiteboardLoading) && !guestHiddenWhiteboard && (
                <WhiteboardContainer
                  fastboard={fastboard}
                  isLoading={isWhiteboardLoading}
                  error={whiteboardError}
                  onClose={stopWhiteboard}
                  isHost={(() => {
                    if (meetingRole === "moderator" || meetingRole === "co-host") return true;
                    const lid = mMeeting?.localParticipant?.id;
                    const lnum = lid ? stringToNumericUid(lid) : null;
                    return !!(lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum))));
                  })()}
                  isMobile={isMobile}
                />
              )}
              {/* Show Whiteboard button — visible when a guest has hidden the active whiteboard */}
              {isWhiteboardOpen && guestHiddenWhiteboard && (
                <button
                  onClick={() => setGuestHiddenWhiteboard(false)}
                  className="fixed bottom-24 left-4 z-[9999] flex items-center gap-2 px-4 py-2.5 bg-[#6a1fbf] hover:bg-[#5a19a0] text-white text-sm font-medium rounded-xl shadow-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></svg>
                  Show Whiteboard
                </button>
              )}

              {/* Transcription Panel - Transparent Style */}
              {(translationEnabled || transcriptionEnabled) &&
                transcriptions && (
                  <div
                    ref={transcriptionPanelRef}
                    className="fixed z-50 flex flex-col"
                    style={{
                      ...(isMobile
                        ? {
                          left: `${mobilePanelPosition.x}px`,
                          bottom: `${mobilePanelPosition.y}px`,
                          top: "auto",
                          transform: "none",
                          width: `${Math.min(
                            panelSize.width,
                            window.innerWidth - 32
                          )}px`,
                        }
                        : {
                          left: `${panelPosition.x}px`,
                          top: `${panelPosition.y}px`,
                          width: `${panelSize.width}px`,
                        }),
                      // height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                      //   }px`,
                      // cursor: isDragging ? "grabbing" : "grab",
                      // boxShadow: "0 4px 15px rgba(0, 0, 0, 0.5)",
                      // borderRadius: "8px",
                      // backgroundColor: "rgba(0, 0, 0, 0.35)",
                      // backdropFilter: "blur(10px)",
                      // border: "1px solid rgba(255, 255, 255, 0.2)",
                      // userSelect: "none",
                      // touchAction: isMobile ? "pan-x pan-y" : "none",
                    }}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleTouchStart}
                  >
                    {/* <div
                      ref={transcriptionListRef}
                      className="flex-1 overflow-y-auto"
                      style={{
                        overflowY: "auto",
                        backgroundColor: "#242424",
                        borderRadius: "8px",
                        height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                          }px`,
                        ...(isMobile && {
                          touchAction: "pan-y",
                        }),
                      }}
                    > */}
                    <div
                      ref={transcriptionListRef}
                      className="flex-1 overflow-y-auto flex items-center justify-center"
                      style={{
                        overflowY: "auto",
                        backgroundColor: "transparent",
                        borderRadius: "8px",
                        height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                          }px`,
                        ...(isMobile && {
                          touchAction: "pan-y",
                        }),
                      }}
                    >
                      {!transcriptions ? (
                        <div></div>
                      ) : (
                        <div className="w-full flex items-center justify-center px-4 py-2">
                          {(() => {
                            // Check if selected language is Arabic (RTL)
                            const isRTL = selectedLanguage?.startsWith("ar");
                            const textDirection = isRTL ? "rtl" : "ltr";
                            const textAlign = isRTL ? "right" : "left";

                            return (
                              <div
                                style={{
                                  backgroundColor: "rgba(0, 0, 0, 0.75)",
                                  padding: "12px 20px",
                                  borderRadius: "8px",
                                  maxWidth: "90%",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  direction: textDirection,
                                }}
                              >
                                {/* Profile Image or Initial */}
                                {(() => {
                                  const participant =
                                    mMeeting?.participants?.get?.(
                                      transcriptions.participantId
                                    ) ||
                                    mMeeting?.participants?.[
                                    transcriptions.participantId
                                    ];
                                  const profileImage =
                                    transcriptions.profileImage ||
                                    participant?.metaData?.profileImage ||
                                    participant?.value?.metaData
                                      ?.profileImage ||
                                    null;
                                  const participantName =
                                    transcriptions.participantName || "User";
                                  const firstChar =
                                    participantName?.charAt(0)?.toUpperCase() ||
                                    "?";

                                  // Generate a color based on participant ID for consistency
                                  const colors = [
                                    "rgba(45, 140, 255, 0.8)",
                                    "rgba(255, 140, 45, 0.8)",
                                    "rgba(140, 45, 255, 0.8)",
                                    "rgba(45, 255, 140, 0.8)",
                                    "rgba(255, 45, 140, 0.8)",
                                  ];
                                  const colorIndex =
                                    transcriptions.participantId?.charCodeAt(
                                      0
                                    ) % colors.length || 0;
                                  const bgColor = colors[colorIndex];

                                  return (
                                    <div
                                      style={{
                                        flexShrink: 0,
                                        width: isMobile ? "36px" : "40px",
                                        height: isMobile ? "36px" : "40px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: profileImage
                                          ? "transparent"
                                          : bgColor,
                                      }}
                                    >
                                      {profileImage ? (
                                        <img
                                          src={profileImage}
                                          alt={participantName}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <span
                                          style={{
                                            color: "#FFFFFF",
                                            fontSize: isMobile
                                              ? "16px"
                                              : "18px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {firstChar}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                <p
                                  className="text-white break-words"
                                  style={{
                                    fontSize: isMobile ? "18px" : "20px",
                                    lineHeight: "1.6",
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "500",
                                    textAlign: textAlign,
                                    flex: 1,
                                    minWidth: 0, // Ensures text doesn't overflow
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {transcriptions.text}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Right Edge Resize Handle - Visual Indicator */}
                    <div
                      className="absolute right-0 w-3 cursor-ew-resize group"
                      onMouseDown={handleResizeStart}
                      title="Drag to resize width"
                      style={{
                        top: "0px",
                        bottom: "0px",
                        borderBottomRightRadius: "8px",
                        borderTopRightRadius: "8px",
                      }}
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-white/20" />
                    </div>
                  </div>
                )}
            </>
          ) : (
            <></>
          )
        ) : (
          <>
            {showJoinScreen ? (
              <WaitingToJoinScreenNew visible={showJoinScreen} overlayType="video" />
            ) : (
              /* Meeting is connecting or retrying — show a loading indicator
                 instead of a blank blue screen */
              <div className="absolute inset-0 flex items-center justify-center z-30">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-10 h-10 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
                  <p className="text-white/60 text-sm">
                    {mMeeting.meetingState === 'FAILED' ? 'Reconnecting...' : 'Connecting to meeting...'}
                  </p>
                  {mMeeting.meetingState === 'FAILED' && uiRetryCountRef.current >= 2 && (
                    <button
                      onClick={() => {
                        uiRetryCountRef.current = 0;
                        mMeeting.join();
                      }}
                      className="mt-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
                    >
                      Retry Connection
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Studio Bottom Controls - Fixed at bottom */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin &&
          !isRecorderMode && (
            <div className="fixed bottom-0 left-0 right-0 z-40">
              <StudioBottomControls
                isWebinarViewer={meetingSettings?.viewOnly || (meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole))}
                isMuted={!mMeeting?.localMicOn}
                isCameraOn={mMeeting?.localWebcamOn}
                isScreenSharing={mMeeting?.localScreenShareOn}
                currentLayout={selectedLayout}
                canScreenShare={(() => {
                  const hasRole = ["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
                  const localId = mMeeting?.localParticipant?.id;
                  const numericLocalId = localId ? (typeof localId === 'number' ? localId : stringToNumericUid(localId)) : null;
                  const isPresenterFlag = localId && (
                    presenters?.has(localId) ||
                    presenters?.has(String(localId)) ||
                    presenters?.has(numericLocalId)
                  );
                  const hasPermission = hasRole || isPresenterFlag;
                  // Someone else is presenting — disable for everyone except the active sharer (so they can stop)
                  const isDisabledByPresenter = mMeeting?.presenterId && !mMeeting?.localScreenShareOn;
                  return hasPermission && !isDisabledByPresenter;
                })()}
                onToggleMic={async () => {
                  if (mMeeting?.localMicOn) {
                    await mMeeting?.muteMic();
                    if (rtm?.setPresenceState) {
                      try { await rtm.setPresenceState({ micMuted: "true" }); } catch (e) {
                        console.warn('[MeetingContainer] Failed to update RTM presence (mute):', e);
                      }
                    }
                  } else {
                    // Hosts, co-hosts, and presenters can always unmute themselves
                    const isHostOrCoHostRole = ["moderator", "co-host", "host"].includes(meetingRole);
                    const localId = mMeeting?.localParticipant?.id;
                    const numericLocalId = localId ? (typeof localId === 'number' ? localId : stringToNumericUid(localId)) : null;
                    const isPresenter = localId && (
                      presenters?.has(localId) ||
                      presenters?.has(String(localId)) ||
                      (numericLocalId != null && presenters?.has(numericLocalId))
                    );

                    if (mutedByHost && !isHostOrCoHostRole && !isPresenter) {
                      toast.warning("You cannot unmute yourself. The host has muted your microphone. Please wait for the host to allow you to unmute.", {
                        toastId: 'muted-by-host',
                        position: "bottom-left",
                        autoClose: 4000,
                        theme: "dark",
                      });
                      return;
                    }
                    await mMeeting?.unmuteMic();
                    if (rtm?.setPresenceState) {
                      try { await rtm.setPresenceState({ micMuted: "false" }); } catch (e) {
                        console.warn('[MeetingContainer] Failed to update RTM presence (unmute):', e);
                      }
                    }
                  }
                }}
                onToggleCamera={async () => {
                  if (mMeeting?.localWebcamOn) {
                    mMeeting?.disableWebcam();
                    if (rtm?.setPresenceState) {
                      rtm.setPresenceState({ camOff: "true" });
                    }
                  } else {
                    // Hosts, co-hosts, and presenters can always toggle their camera
                    const isHostOrCoHostRole = ["moderator", "co-host", "host"].includes(meetingRole);
                    const localId = mMeeting?.localParticipant?.id;
                    const numericLocalId = localId ? (typeof localId === 'number' ? localId : stringToNumericUid(localId)) : null;
                    const isPresenter = localId && (
                      presenters?.has(localId) ||
                      presenters?.has(String(localId)) ||
                      (numericLocalId != null && presenters?.has(numericLocalId))
                    );

                    if (cameraOffByHost && !isHostOrCoHostRole && !isPresenter) {
                      toast.warning("You cannot enable your camera. The host has disabled your camera. Please wait for the host to allow you to enable it.", {
                        position: "bottom-left",
                        autoClose: 4000,
                        theme: "dark",
                      });
                      return;
                    }
                    mMeeting?.enableWebcam();
                    if (rtm?.setPresenceState) {
                      rtm.setPresenceState({ camOff: "false" });
                    }
                  }
                }}
                onToggleScreenShare={async () => {
                  // Check if user is allowed to screen share:
                  // 1. Host/co-host/moderator are always allowed
                  // 2. Attendees who have been made presenter are allowed
                  const isHostOrCoHost = ["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
                  const localParticipantId = mMeeting?.localParticipant?.id;
                  // Check all ID formats for presenter status
                  const numericLocalId = localParticipantId ? (typeof localParticipantId === 'number' ? localParticipantId : stringToNumericUid(localParticipantId)) : null;
                  const isPresenter = localParticipantId && (
                    presenters?.has(localParticipantId) ||
                    presenters?.has(String(localParticipantId)) ||
                    presenters?.has(numericLocalId)
                  );
                  const canScreenShare = isHostOrCoHost || isPresenter;

                  if (!canScreenShare) {
                    toast.warning("Only hosts or presenters can share screen. Ask the host to make you a presenter.", {
                      position: "bottom-left",
                      autoClose: 4000,
                      theme: "dark",
                    });
                    return;
                  }

                  // Use localScreenShareOn (the local-participant flag the
                  // button visual already uses) instead of isPresenting
                  // (which depends on presenterId + hasActiveScreenShare and
                  // can lag behind localScreenShareOn by a few hundred ms
                  // after a share starts). Without this, a quick second
                  // click during the startup window opens the OS picker
                  // again instead of stopping the existing share.
                  if (mMeeting?.localScreenShareOn) {
                    mMeeting?.disableScreenShare();
                  } else {
                    // Enable screen share with audio track included
                    // This helps prevent audio dropout issues
                    try {
                      await mMeeting?.enableScreenShare();
                      // Re-enable mic after screen share to prevent audio dropout
                      // Some browsers pause mic track when screen share starts
                      // (Primary recovery is in AgoraContext; this is a fallback)
                      if (mMeeting?.localParticipant?.micOn) {
                        setTimeout(async () => {
                          const micTrack = mMeeting?.localParticipant?.micStream?.track;
                          if (micTrack && micTrack.enabled === false) {
                            try {
                              await micTrack.setEnabled(true);
                            } catch (e) {
                              micTrack.enabled = true;
                            }
                          }
                        }, 800); // Longer delay than AgoraContext (500ms) as secondary fallback
                      }
                    } catch (err) {
                      console.error("Screen share error:", err);
                    }
                  }
                }}
                onLayoutChange={(layoutId) => {
                  if (isWebinarParticipant) return; // Webinar attendees receive layout from host
                  setSelectedLayout(layoutId);
                  meetingState.emitSceneChange(layoutId, layoutId);
                }}
                onInvite={() => {
                  const meetingLink = window.location.href;
                  navigator.clipboard.writeText(meetingLink);
                  toast.success("Meeting link copied to clipboard!");
                }}
                onLeave={() => {
                  // If in breakout room, redirect to main room immediately
                  const bkOrigin = localStorage.getItem('rlink_breakout_origin');
                  if (bkOrigin) {
                    const bkMeetingId = localStorage.getItem('rlink_breakout_meetingId');
                    const bkOdId = localStorage.getItem('rlink_breakout_odId');
                    const bkName = localStorage.getItem('rlink_breakout_name') || user?.name || '';
                    // Save flag for unassign on main room connect
                    if (bkMeetingId && bkOdId) {
                      localStorage.setItem('rlink_returned_from_breakout', JSON.stringify({ parentMeetingId: bkMeetingId, odId: bkOdId }));
                    }
                    localStorage.removeItem('rlink_breakout_origin');
                    localStorage.removeItem('rlink_breakout_meetingId');
                    localStorage.removeItem('rlink_breakout_odId');
                    localStorage.removeItem('rlink_breakout_roomId');
                    localStorage.removeItem('rlink_breakout_roomName');
                    localStorage.removeItem('rlink_breakout_moderatorId');
                    localStorage.removeItem('rlink_breakout_moderatorName');
                    localStorage.removeItem('rlink_breakout_isModerator');
                    localStorage.setItem('rlink_breakout_name', bkName);
                    window.location.href = `/${bkOrigin}?fromBreakout=1`;
                    return;
                  }
                  mMeeting?.leave();
                  leaveOrRedirect();
                }}
                isHost={["moderator", "co-host", "host"].includes(meetingRole)}
                coHosts={coHosts}
                presenters={presenters}
                onEndForAll={async () => {
                  const meetingId = mMeeting?.meetingId;
                  if (meetingId) {
                    try {
                      await requests.post(`/agora/force-end-meeting/${meetingId}`);
                    } catch (err) {
                      console.error("Failed to force-end meeting:", err);
                    }
                  }
                  mMeeting?.end();
                  leaveOrRedirect();
                }}
                onEndForAllExceptHost={async () => {
                  const meetingId = mMeeting?.meetingId;
                  if (meetingId) {
                    isEndExceptHostRef.current = true;
                    try {
                      // Collect UIDs to exclude (host + co-hosts + presenters)
                      const excludeUids = [];
                      // Add local participant (host)
                      const localId = mMeeting?.localParticipant?.id;
                      if (localId) excludeUids.push(String(localId));
                      // Add co-hosts
                      if (coHosts && coHosts.size > 0) {
                        coHosts.forEach(uid => excludeUids.push(String(uid)));
                      }
                      // Add presenters
                      if (presenters && presenters.size > 0) {
                        presenters.forEach(uid => excludeUids.push(String(uid)));
                      }

                      // Always use selective kick to keep the host (and co-hosts/presenters) in the meeting
                      await requests.post(`/agora/force-end-meeting/${meetingId}`, { excludeUids });
                    } catch (err) {
                      console.error("Failed to end meeting for others:", err);
                      isEndExceptHostRef.current = false;
                    }
                  }
                }}
                onLeaveAndAssignHost={(participantId) => {
                  if (participantId) {
                    try {
                      publishRoleChange(JSON.stringify({
                        targetParticipantId: participantId,
                        action: 'promote',
                        newRole: 'co-host',
                      }), { persist: true });
                    } catch (err) {
                      console.error("Failed to assign host:", err);
                    }
                  }
                  isLeaveAndAssignRef.current = true;
                  mMeeting?.leave();
                  leaveOrRedirect();
                }}
                participants={mMeeting?.participants}
                onOpenSettings={() => { }}
                onSendReaction={(reactionId, emoji, isBoosted, points) => {
                  // Publish reaction via PubSub so it appears on participant tiles
                  // Include boosted flag so tile reactions can show different styles
                  publishEmoji(JSON.stringify({ char: emoji, alt: reactionId, boosted: isBoosted || false }));

                  // Only trigger full-screen floating animation for BOOSTED reactions (rocket, boom, fire)
                  // Standard reactions only show badge on tile, no full-screen animation
                  if (isBoosted) {
                    setActiveReaction(null);
                    setTimeout(() => setActiveReaction(reactionId), 10);
                  }
                  console.log('Reaction sent:', { reactionId, emoji, isBoosted, points });
                }}
                onTriggerEffect={(effectId) => {
                  // Broadcast effect via RTM so all participants see it
                  // Get emoji for the effect from EFFECT_REACTIONS
                  const effectEmojis = {
                    balloon: '🎈', confetti: '🎉', hearts: '❤️',
                    applause: '👏', mind_blown: '🤯'
                  };
                  publishEmoji(JSON.stringify({
                    char: effectEmojis[effectId] || '✨',
                    alt: effectId,
                    isEffect: true
                  }));
                  // Trigger local full-screen effect animation immediately
                  setActiveReaction(null);
                  setTimeout(() => setActiveReaction(effectId), 10);
                  console.log('Effect triggered and broadcasted:', effectId);
                }}
                onRaiseHand={() => publishRaiseHand({ action: isHandRaised ? "lower" : "raise" })}
                onToggleBRB={() => publishBRB({ action: isBRB ? "back" : "brb" })}
                isHandRaised={isHandRaised}
                isBRB={isBRB}
                reactionsEnabled={meetingSettings?.allowReactions !== false}
                boostedReactionsEnabled={meetingSettings?.allowReactions !== false}
                allowRaiseHand={meetingSettings?.allowRaiseHand !== false}
                audioInputs={audioInputs}
                audioOutputs={audioOutputs}
                videoInputs={videoInputs}
                selectedAudioInput={selectedAudioInput}
                selectedAudioOutput={selectedAudioOutput}
                selectedVideoInput={selectedVideoInput}
                onAudioInputChange={(deviceId) => {
                  setSelectedAudioInput(deviceId);
                  mMeeting?.changeMic(deviceId);
                }}
                onAudioOutputChange={(deviceId) => {
                  setSelectedAudioOutput(deviceId);
                  // Route Agora-played remote audio (this is what actually carries
                  // other participants' voices) — DOM setSinkId() can't reach it.
                  if (mMeeting?.setPlaybackDevice) {
                    mMeeting.setPlaybackDevice(deviceId);
                  }
                  // Also apply to any DOM-rendered audio/video elements.
                  const audioElements = document.querySelectorAll('audio, video');
                  audioElements.forEach((element) => {
                    if (element.setSinkId) {
                      element.setSinkId(deviceId).catch(err => {
                        // Ignore AbortError - occurs when element is removed during setSinkId operation
                        if (err.name !== 'AbortError') {
                          console.error('Error setting audio output:', err);
                        }
                      });
                    }
                  });
                }}
                onVideoInputChange={(deviceId) => {
                  setSelectedVideoInput(deviceId);
                  mMeeting?.changeWebcam(deviceId);
                }}
                captionsEnabled={captionsEnabled}
                onToggleCaptions={() => handleToggleCaptions(!captionsEnabled)}
                spokenLanguage={spokenLanguage}
                captionLanguage={captionLanguage}
                onSpokenLanguageChange={setSpokenLanguage}
                onCaptionLanguageChange={setCaptionLanguage}
                onOpenTranslationTools={() => setShowTranslatorModal(true)}
                onStartAICaptions={handleToggleAICaptions}
                agoraFallbackActive={useAgoraTranslateFallback}
                restrictSpokenToAgora={restrictSpokenToAgora}
                isAICaptionsActive={aiCaptionsEnabled}
                // AI Notetaker / Transcription (broadcast state from MeetingStateContext)
                onToggleTranscription={() => (meetingState.notetaker?.isActive ? handleStopNotetaker() : handleStartNotetaker())}
                isTranscriptionActive={!!meetingState.notetaker?.isActive}
                canToggleTranscription={meetingRole === 'moderator' || meetingRole === 'co-host'}
                // VideoSDK Translation (AI Captions from top menu)
                translationEnabled={translationEnabled}
                setTranslationEnabled={setTranslationEnabled}
                translationStatus={translationStatus}
                startTranslation={startTranslation}
                stopTranslation={stopTranslation}
                changeTranslationLanguage={changeTranslationLanguage}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                // Virtual background props
                blurEnabled={blurEnabled}
                setBlurEnabled={setBlurEnabled}
                blurStrength={blurStrength}
                setBlurStrength={setBlurStrength}
                selectedBackground={selectedBackground}
                handleStartVirtualBackground={handleStartVirtualBackground}
                virtualBackgroundOn={virtualBackgroundOn}
                // Green screen props
                greenScreenEnabled={greenScreenEnabled}
                handleStartGreenScreen={handleStartGreenScreen}
                // Pass local webcam stream for preview (processed stream when effects are applied)
                localWebcamStream={mMeeting?.localParticipant?.webcamStream}
                localWebcamOn={mMeeting?.localWebcamOn}
                processedWebcamStream={processedWebcamStream}
                // Meeting statistics for settings (ref to avoid parent re-renders)
                localParticipantStatsRef={localParticipantStatsRef}
                // Camera resolution callback
                onCameraResolutionChange={handleCameraResolutionChange}
                // Audio settings callback
                onAudioSettingsChange={handleAudioSettingsChange}
                // AI Noise cancellation props
                noiseCancellationEnabled={mMeeting?.noiseCancellationEnabled}
                noiseCancellationMode={mMeeting?.noiseCancellationMode}
                onNoiseCancellationToggle={mMeeting?.toggleNoiseCancellation}
                onNoiseCancellationModeChange={mMeeting?.changeNoiseCancellationMode}
                meetingStartTime={meetingStartTime}
                rightSidebarCollapsed={rightSidebarCollapsed}
                showGoLiveButton={meetingSettings?.roomType === 'Live Stream' && ["moderator", "co-host", "host"].includes(meetingRole)}
                isLiveStreaming={meetingState?.isLiveStreaming}
                admitAllWaitingParticipants={admitAllParticipants}
              />
            </div>
          )}

        {/* Live Captions Bar - Fixed full-width above bottom controls */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin &&
          captionsEnabled && (
            <div
              className={`fixed bottom-20 md:bottom-16 z-[200] pointer-events-auto overflow-visible transition-all duration-300 ${
                !leftSidebarCollapsed ? 'left-64 md:left-[380px]' : 'left-10 md:left-14'
              } ${
                !rightSidebarCollapsed ? 'right-72 md:right-80' : 'right-10 md:right-14'
              }`}
            >
              <LiveCaptions
                isEnabled={captionsEnabled}
                spokenLanguage={spokenLanguage}
                captionLanguage={captionLanguage}
                onSpokenChange={setSpokenLanguage}
                onCaptionChange={setCaptionLanguage}
                captions={liveCaptions}
                onClose={() => handleToggleCaptions(false)}
                agoraFallbackActive={useAgoraTranslateFallback}
                restrictSpokenToAgora={restrictSpokenToAgora}
              />
            </div>
          )}

        {/* AI Captions Bar - Fixed full-width above bottom controls */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin &&
          aiCaptionsEnabled && (
            <div
              className={`fixed bottom-20 md:bottom-16 z-[200] pointer-events-auto overflow-visible transition-all duration-300 ${
                !leftSidebarCollapsed ? 'left-64 md:left-[380px]' : 'left-10 md:left-14'
              } ${
                !rightSidebarCollapsed ? 'right-72 md:right-80' : 'right-10 md:right-14'
              }`}
            >
              <LiveCaptions
                isEnabled={aiCaptionsEnabled}
                spokenLanguage={spokenLanguage}
                captionLanguage={captionLanguage}
                onSpokenChange={setSpokenLanguage}
                onCaptionChange={setCaptionLanguage}
                captions={aiCaptions}
                onClose={handleToggleAICaptions}
                agoraFallbackActive={useAgoraTranslateFallback}
                restrictSpokenToAgora={restrictSpokenToAgora}
              />
            </div>
          )}

        {/* Studio Left Sidebar - Fixed on left (host/co-host only) */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin &&
          isHost &&
          !isRecorderMode && (
            <div className="fixed top-14 md:top-16 left-0 bottom-14 md:bottom-16 z-30">
              <StudioLeftSidebar
                isCollapsed={leftSidebarCollapsed}
                onToggleCollapse={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
                selectedLayout={selectedLayout}
                onLayoutChange={(layoutId) => {
                  if (isWebinarParticipant) return; // Webinar attendees receive layout from host
                  setSelectedLayout(layoutId);
                  meetingState.emitSceneChange(layoutId, layoutId);
                }}
                showElementSection={showElementSection}
                setShowElementSection={setShowElementSection}
                onRefreshElements={getFolderElementList}
                activeContractView={activeContractView}
                setActiveContractView={setActiveContractView}
                activeElements={sidebarActiveElements}
                onDeactivateElement={handleSidebarDeactivateElement}
              />
            </div>
          )}

        {/* Studio Right Sidebar - Fixed on right */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin &&
          !isRecorderMode && (
            <div className="fixed top-14 md:top-16 right-0 bottom-14 md:bottom-16 z-30">
              <StudioRightSidebar
                isPresenter={(() => {
                  const lid = mMeeting?.localParticipant?.id;
                  const lnum = lid ? stringToNumericUid(lid) : null;
                  return !!(lid && (presenters?.has(lid) || presenters?.has(String(lid)) || (lnum != null && presenters?.has(lnum))));
                })()}
                isCollapsed={rightSidebarCollapsed}
                onToggleCollapse={() => setRightSidebarCollapsed(!rightSidebarCollapsed)}
                sideBarMode={sideBarMode}
                setSideBarMode={() => { }}
                participantsCount={participantsData?.length || 0}
                unreadChat={0}
                onTriggerEffect={(effectId) => {
                  // Broadcast effect via RTM so all participants see it
                  const effectEmojis = {
                    balloon: '🎈', confetti: '🎉', hearts: '❤️',
                    applause: '👏', mind_blown: '🤯'
                  };
                  publishEmoji(JSON.stringify({
                    char: effectEmojis[effectId] || '✨',
                    alt: effectId,
                    isEffect: true
                  }));
                  // Trigger local full-screen effect animation immediately
                  setActiveReaction(null);
                  setTimeout(() => setActiveReaction(effectId), 10);
                  console.log('Effect triggered from sidebar and broadcasted:', effectId);
                }}
                onSendReaction={(reactionId, emoji, isBoosted) => {
                  // Publish reaction via PubSub so it appears on participant tiles
                  publishEmoji(JSON.stringify({ char: emoji, alt: reactionId, boosted: isBoosted || false }));

                  // Only trigger full-screen floating animation for BOOSTED reactions
                  if (isBoosted) {
                    setActiveReaction(null);
                    setTimeout(() => setActiveReaction(reactionId), 10);
                  }
                  console.log('Quick reaction from sidebar:', { reactionId, emoji, isBoosted });
                }}
                onRaiseHand={() => publishRaiseHand({ action: isHandRaised ? "lower" : "raise" })}
                onToggleBRB={() => publishBRB({ action: isBRB ? "back" : "brb" })}
                isHandRaised={isHandRaised}
                isBRB={isBRB}
                branding={branding}
                onBrandingChange={setBranding}
                localParticipantName={mMeeting?.localParticipant?.displayName || 'You'}
                startWhiteboard={startWhiteboard}
                stopWhiteboard={stopWhiteboard}
                isWhiteboardOpen={isWhiteboardOpen}
                // AI Translation props (synced with bottom bar)
                translationEnabled={translationEnabled}
                setTranslationEnabled={setTranslationEnabled}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                startTranslation={startTranslation}
                stopTranslation={stopTranslation}
                // AI Captions props
                aiCaptionsEnabled={aiCaptionsEnabled}
                handleToggleAICaptions={handleToggleAICaptions}
                // Participant actions
                onMuteAll={() => {
                  console.log('[onMuteAll] Sending force mute ALL via Socket.IO');
                  // Single Socket.IO broadcast — all participants receive and mute themselves.
                  // Do NOT also publish on PubSub MUTE_MIC: receivers listen on both
                  // channels and the two handlers race-fire duplicate "muted by host" toasts.
                  meetingState.emitForceMuteAll();
                }}
                onUnmuteAll={() => {
                  console.log('[onUnmuteAll] Releasing mute-all lock via Socket.IO');
                  meetingState.emitGrantUnmuteAll();
                }}
                onLowerAllHands={() => {
                  publishLowerHand("LOWER_ALL");
                }}
                onLowerHand={(participantId) => {
                  publishLowerHand({ participantId });
                }}
                // Waiting room
                waitingParticipants={waitingParticipants}
                onAdmitParticipant={admitParticipant}
                onAdmitAllParticipants={admitAllParticipants}
                onDenyParticipant={denyParticipant}
                onSendMessageToWaiting={sendMessageToWaiting}
                waitingRoomChatMessages={waitingRoomChatMessages}
                // AI Notetaker - pass transcriptions and STT bot state
                transcriptions={transcriptions}
                sttBotActive={sttBotActive}
                onStartNotetaker={handleStartNotetaker}
                onStopNotetaker={handleStopNotetaker}
                // Participants list for breakout rooms — pass raw Map for live name resolution
                participantsMap={mMeeting?.participants}
                participantIds={participantsData}
                meetingStartTime={meetingStartTime}
              />
            </div>
          )}

        {/* Reactions Overlay - Floating emoji animations */}
        {typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin && (
            <ReactionsOverlay
              activeReaction={activeReaction}
              onReactionComplete={() => setActiveReaction(null)}
            />
          )}

        {/* Note: Branding overlays (Logo & QR) are now rendered inside ParticipantView for proper positioning */}

        {/* Raised Hands Queue - For hosts to manage raised hands (hidden) */}
        {false && typeof localParticipantAllowedJoin === "boolean" &&
          !showJoinScreen &&
          localParticipantAllowedJoin && (
            <RaisedHandsQueue
              raisedHands={raisedHandsWithNames}
              isHost={["moderator", "co-host"].includes(meetingRole)}
              onLowerHand={(participantId) => {
                publishLowerHand({ participantId });
              }}
              onLowerAll={() => {
                publishLowerHand("LOWER_ALL");
              }}
            />
          )}

        {/* Real-Time Translator Modal */}
        <RealTimeTranslatorModal
          isOpen={showTranslatorModal}
          onClose={() => setShowTranslatorModal(false)}
          sourceLanguage={spokenLanguage}
          targetLanguage={captionLanguage}
          onSourceChange={setSpokenLanguage}
          onTargetChange={setCaptionLanguage}
        />
      </div>

      {/* Poll Voting Modal - Shown to both host (preview) and participants (voting) */}
      {showPollModal && activePoll && (
        <PollVotingModal
          poll={activePoll}
          isOpen={true}
          isModeratorView={isPollLauncher}
          onClose={() => {
            setShowPollModal(false);
            setActivePoll(null);
          }}
          onEndPoll={() => {
            setShowPollModal(false);
            setActivePoll(null);
          }}
          onVoteSubmitted={() => {}}
        />
      )}

      {/* Lead Capture Viewer Modal - Shown to both moderator (preview) and participants (form) */}
      <LeadCaptureViewer
        leadCapture={activeLeadCapture}
        isOpen={showLeadCaptureModal}
        isModeratorView={meetingRole === "moderator" || meetingRole === "co-host" || isLeadCaptureLauncher}
        onClose={() => {
          // For moderator/launcher: just close the preview, don't end the form
          // For participants: close the modal
          const isController = meetingRole === "moderator" || meetingRole === "co-host" || isLeadCaptureLauncher;
          setShowLeadCaptureModal(false);
          if (!isController) {
            setActiveLeadCapture(null);
          }
        }}
        onEndForm={() => {
          // Moderator, co-host, or launcher can end the form - this ends it for everyone
          const canEnd = meetingRole === "moderator" || meetingRole === "co-host" || isLeadCaptureLauncher;
          if (canEnd && activeLeadCapture) {
            setShowLeadCaptureModal(false);
            publishLeadCapture(JSON.stringify({
              type: "end",
              clear: true,
              leadCaptureId: activeLeadCapture.id || activeLeadCapture._id,
              timestamp: Date.now(),
            }), { persist: true });
            // Remove from Socket.IO meeting state so late joiners don't see it
            meetingState.emitElementRemove(activeLeadCapture.id || activeLeadCapture._id);
            setActiveLeadCapture(null);
            setIsLeadCaptureLauncher(false);
          }
        }}
        onSubmitted={(leadCaptureId, submissionData) => {
          // Handle lead capture submission callback
          console.log("Lead capture submitted:", leadCaptureId, submissionData);
        }}
      />

      {/* Participant Permission Modals */}
      <ParticipantPermissionModal
        open={showMicPermissionModal}
        onAccept={handleAcceptMicPermission}
        onDecline={handleDeclineMicPermission}
        permissionType="mic"
      />
      <ParticipantPermissionModal
        open={showVideoPermissionModal}
        onAccept={handleAcceptVideoPermission}
        onDecline={handleDeclineVideoPermission}
        permissionType="video"
      />

      {/* Rewards Panel - now rendered inline as dropdown in TopBar */}
    </div>
    </BreakoutRoomProvider>
  );
}
